import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import pb from '../lib/pocketbase';
import PocketBase from 'pocketbase';
import ArticleForm from '../components/ArticleForm';
import CustomerForm from '../components/CustomerForm';
import VehicleForm from '../components/VehicleForm';
import ServiceForm from '../components/ServiceForm';
import SettingsForm from '../components/SettingsForm';
import QuoteForm from '../components/QuoteForm';
import InvoiceForm from '../components/InvoiceForm';
import AppointmentForm from '../components/AppointmentForm';
import AppointmentProposalForm from '../components/AppointmentProposalForm';
import PartForm from '../components/PartForm';
import { usePDFGenerator } from '../hooks/usePDFGenerator';
import './pages.css';
import jsPDF from 'jspdf';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import AdminBottomNavbar from '../components/AdminBottomNavbar';
import CarListingManager from '../components/CarListingManager';


const Admin = () => {
  const { isAdmin, user, logout, loading: authLoading } = useAuth();
  const { generateQuotePDF, generateInvoicePDF, isGenerating } = usePDFGenerator();
  const [activeMainTab, setActiveMainTab] = useState('gestion'); // 'gestion' ou 'contenu'
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [subNavOpen, setSubNavOpen] = useState(false);
  const [isTabsOverflow, setIsTabsOverflow] = useState(false);
  const tabsRef = useRef(null);
  const subNavContainerRef = useRef(null);

  // Fermer le menu utilisateur quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-dropdown-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Partager uniquement le lien espace client pour une facture (message prêt, sans PDF)
  const handleShareInvoiceAccountLink = async (invoice) => {
    try {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const accountUrl = `${origin}/mon-compte`;
      const shareText = [
        'Bonjour,',
        '',
        `Votre facture ${invoice.invoice_number} est disponible dans votre espace client :`,
        accountUrl,
        '',
        `Montant TTC : ${typeof invoice.total === 'number' ? invoice.total.toFixed(2) : '0.00'} €`,
        `Échéance : ${invoice.due_date ? formatDate(invoice.due_date) : '-'}`,
        '',
        'Merci de consulter/régler la facture depuis votre espace Mon Compte.',
        '',
        'Merci et à très vite !'
      ].join('\n');

      if (navigator.share) {
        try {
          await navigator.share({ title: `Facture ${invoice.invoice_number}`, text: shareText, url: accountUrl });
        } catch (_) { /* annulé */ }
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareText);
        alert('Message copié. Vous pouvez le coller dans votre application de messagerie.');
      } else {
        window.prompt('Copiez ce message pour le partager :', shareText);
      }

      await logActivity({
        collection: 'invoices',
        recordId: invoice.id,
        action: 'share',
        message: `Lien espace client partagé pour la facture ${invoice.invoice_number || invoice.id}`,
        context: { invoice_number: invoice.invoice_number || null }
      });
    } catch (e) {
      console.error('Erreur partage lien espace client (facture):', e);
      alert('Erreur lors du partage du lien espace client');
    }
  };


  // Partager uniquement le lien espace client avec un message prêt (sans PDF)
  const handleShareAccountLink = async (quote) => {
    try {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      const accountUrl = `${origin}/mon-compte`;
      const shareText = [
        'Bonjour,',
        '',
        `Votre devis ${quote.quote_number} est disponible dans votre espace client :`,
        accountUrl,
        '',
        `Montant TTC : ${typeof quote.total === 'number' ? quote.total.toFixed(2) : '0.00'} €`,
        `Valide jusqu'au : ${quote.valid_until ? formatDate(quote.valid_until) : '-'}`,
        '',
        'Merci de valider/accepter le devis depuis votre espace Mon Compte.',
        '',
        'Merci et à très vite !'
      ].join('\n');

      if (navigator.share) {
        try {
          await navigator.share({ title: `Devis ${quote.quote_number}`, text: shareText, url: accountUrl });
        } catch (_) { /* annulé */ }
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareText);
        alert('Message copié. Vous pouvez le coller dans votre application de messagerie.');
      } else {
        window.prompt('Copiez ce message pour le partager :', shareText);
      }

      await logActivity({
        collection: 'quotes',
        recordId: quote.id,
        action: 'share',
        message: `Lien espace client partagé pour le devis ${quote.quote_number || quote.id}`,
        context: { quote_number: quote.quote_number || null }
      });
    } catch (e) {
      console.error('Erreur partage lien espace client:', e);
      alert('Erreur lors du partage du lien espace client');
    }
  };

  
  // États pour la gestion des clients
  const [customers, setCustomers] = useState([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddMenuFor, setShowAddMenuFor] = useState(null); // customer.id ou null
  const [prefillQuoteCustomerId, setPrefillQuoteCustomerId] = useState('');
  const [prefillInvoiceCustomerId, setPrefillInvoiceCustomerId] = useState('');
  
  // États pour la gestion des véhicules
  const [vehicles, setVehicles] = useState([]);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedCustomerForVehicle, setSelectedCustomerForVehicle] = useState(null);
  
  // États pour la gestion des services
  const [services, setServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [selectedServiceCategory, setSelectedServiceCategory] = useState('');
  
  // États pour la gestion des paramètres
  const [settings, setSettings] = useState(null);
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  
  // États pour la gestion des devis
  const [quotes, setQuotes] = useState([]);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const [selectedQuoteStatus, setSelectedQuoteStatus] = useState('');
  const [showQuoteMenuFor, setShowQuoteMenuFor] = useState(null);
  const [historyQuote, setHistoryQuote] = useState(null);
  const [quoteHistoryLogs, setQuoteHistoryLogs] = useState([]);
  const [quoteArchives, setQuoteArchives] = useState([]);
  const [showQuoteHistory, setShowQuoteHistory] = useState(false);
  const [quoteHistoryLoading, setQuoteHistoryLoading] = useState(false);
  const [quoteLogs, setQuoteLogs] = useState([]);
  const [quoteLogsLoading, setQuoteLogsLoading] = useState(false);
  const [quoteLogsSearch, setQuoteLogsSearch] = useState('');
  const [quoteLogsSourceFilter, setQuoteLogsSourceFilter] = useState('');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('');
  
  // États pour la gestion des factures
  const [invoices, setInvoices] = useState([]);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('');
  const [prefillInvoiceQuoteId, setPrefillInvoiceQuoteId] = useState('');
  const [showInvoiceMenuFor, setShowInvoiceMenuFor] = useState(null);
  const [historyInvoice, setHistoryInvoice] = useState(null);
  const [invoiceHistoryLogs, setInvoiceHistoryLogs] = useState([]);
  const [invoiceArchives, setInvoiceArchives] = useState([]);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(false);
  
  // États pour la gestion des rendez-vous
  const [appointments, setAppointments] = useState([]);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showAppointmentProposalForm, setShowAppointmentProposalForm] = useState(false);
  const [reproposeAppointment, setReproposeAppointment] = useState(null);
  const [appointmentSlotsCount, setAppointmentSlotsCount] = useState({}); // { [appointmentId]: number }
  const [labels, setLabels] = useState([]);
  const [showChooseSlotModal, setShowChooseSlotModal] = useState(false);
  const [chooseSlotAppt, setChooseSlotAppt] = useState(null);
  const [chooseSlotList, setChooseSlotList] = useState([]);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState('');
  const [selectedAppointmentStatus, setSelectedAppointmentStatus] = useState('');
  const [appointmentsView, setAppointmentsView] = useState('list'); // 'list' | 'calendar'
  const [showOnboardingQR, setShowOnboardingQR] = useState(false);
  const [showQuotesGeneralQR, setShowQuotesGeneralQR] = useState(false);
  const [showInvoicesGeneralQR, setShowInvoicesGeneralQR] = useState(false);
  
  // État pour la recherche globale
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  
  // États pour la gestion des utilisateurs et articles
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [selectedArticleStatus, setSelectedArticleStatus] = useState('');
  const [selectedArticleCategory, setSelectedArticleCategory] = useState('');
  
  // États pour la gestion du stock de pièces
  const [parts, setParts] = useState([]);
  const [showPartForm, setShowPartForm] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [selectedPartCategory, setSelectedPartCategory] = useState('');
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    adminUsers: 0,
    regularUsers: 0,
    recentUsers: 0,
    totalArticles: 0,
    publishedArticles: 0,
    draftArticles: 0
  });

  const loadQuoteHistory = async (quote) => {
    if (!quote) return;
    setHistoryQuote(quote);
    setShowQuoteHistory(true);
    setQuoteHistoryLoading(true);
    try {
      const logs = await pb.collection('activity_logs').getFullList({
        filter: `collection = "quotes" && record_id = "${quote.id}"`,
        sort: '-created'
      }).catch(() => []);
      setQuoteHistoryLogs(logs || []);

      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "quotes" && record_id = "${quote.id}"`,
        sort: '-created'
      }).catch(() => []);
      setQuoteArchives(archives || []);
    } finally {
      setQuoteHistoryLoading(false);
    }
  };

  const loadInvoiceHistory = async (invoice) => {
    if (!invoice) return;
    setHistoryInvoice(invoice);
    setShowInvoiceHistory(true);
    setInvoiceHistoryLoading(true);
    try {
      const logs = await pb.collection('activity_logs').getFullList({
        filter: `collection = "invoices" && record_id = "${invoice.id}"`,
        sort: '-created'
      }).catch(() => []);
      setInvoiceHistoryLogs(logs || []);

      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "invoices" && record_id = "${invoice.id}"`,
        sort: '-created'
      }).catch(() => []);
      setInvoiceArchives(archives || []);
    } finally {
      setInvoiceHistoryLoading(false);
    }
  };

  // Helper générique pour journaliser une activité côté admin (payload minimal compatible PocketBase)
  const logActivity = async ({ collection, recordId, action, message }) => {
    try {
      await pb.collection('activity_logs').create({
        collection,
        record_id: recordId || '',
        action,
        message,
        source: 'admin'
      });
    } catch (e) {
      console.error('Erreur lors de la création du log activité (admin):', e);
      if (e?.data) console.error('Détails erreur log activité (admin):', e.data);
    }
  };

  // Archive un snapshot PDF de devis dans document_archives (payload minimal)
  const archiveQuoteSnapshot = async (quote, extra = {}) => {
    if (!quote?.id) return;
    try {
      const customer = customers.find(c => c.id === quote.customer_id);
      const vehicle = vehicles.find(v => v.id === quote.vehicle_id);
      const blob = await generateQuotePDF(quote, customer, vehicle, settings, 'blob');
      if (!blob) return;

      const fd = new FormData();
      fd.append('collection', 'quotes');
      fd.append('record_id', quote.id);
      fd.append('select', 'quote');
      fd.append('generated_from', 'admin');

      // Calculer la prochaine version pour ce devis
      let version = 1;
      try {
        const existing = await pb.collection('document_archives').getFullList({
          filter: `collection = "quotes" && record_id = "${quote.id}"`
        });
        version = (existing?.length || 0) + 1;
      } catch (e) {
        console.warn('Impossible de calculer la version du devis pour document_archives:', e);
      }

      fd.append('version', String(version));

      const snapshot = {
        reason: extra.reason || null,
        quote_number: quote.quote_number || null,
        status: quote.status || null,
        total: quote.total || null,
        customer_id: quote.customer_id || null,
        vehicle_id: quote.vehicle_id || null,
        version
      };
      fd.append('snapshot_data', JSON.stringify(snapshot));

      const fileName = `Devis_${quote.quote_number || quote.id}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      fd.append('pdf_file', file);
      await pb.collection('document_archives').create(fd);
    } catch (e) {
      console.error("Erreur lors de l'archivage du PDF de devis (admin):", e);
    }
  };

  // Archive un snapshot PDF de facture dans document_archives (payload minimal)
  const archiveInvoiceSnapshot = async (invoice, extra = {}) => {
    if (!invoice?.id) return;
    try {
      const customer = customers.find(c => c.id === invoice.customer_id);
      const vehicle = vehicles.find(v => v.id === invoice.vehicle_id);
      const blob = await generateInvoicePDF(invoice, customer, vehicle, settings, 'blob');
      if (!blob) return;

      const fd = new FormData();
      fd.append('collection', 'invoices');
      fd.append('record_id', invoice.id);
      fd.append('select', 'invoice');
      fd.append('generated_from', 'admin');

      // Calculer la prochaine version pour cette facture
      let version = 1;
      try {
        const existing = await pb.collection('document_archives').getFullList({
          filter: `collection = "invoices" && record_id = "${invoice.id}"`
        });
        version = (existing?.length || 0) + 1;
      } catch (e) {
        console.warn('Impossible de calculer la version de la facture pour document_archives:', e);
      }

      fd.append('version', String(version));

      const snapshot = {
        reason: extra.reason || null,
        invoice_number: invoice.invoice_number || null,
        status: invoice.status || null,
        payment_status: invoice.payment_status || null,
        total: invoice.total || null,
        customer_id: invoice.customer_id || null,
        vehicle_id: invoice.vehicle_id || null,
        version
      };
      fd.append('snapshot_data', JSON.stringify(snapshot));

      const fileName = `Facture_${invoice.invoice_number || invoice.id}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      fd.append('pdf_file', file);
      await pb.collection('document_archives').create(fd);
    } catch (e) {
      console.error("Erreur lors de l'archivage du PDF de facture (admin):", e);
    }
  };

  const loadQuoteLogs = async () => {
    try {
      setQuoteLogsLoading(true);
      const logs = await pb.collection('activity_logs').getFullList({
        sort: '-created'
      }).catch(() => []);
      setQuoteLogs(logs || []);
    } catch (e) {
      console.error('Erreur lors du chargement de l\'historique global des devis:', e);
      setQuoteLogs([]);
    } finally {
      setQuoteLogsLoading(false);
    }
  };

  const [dashboardStats, setDashboardStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalQuotes: 0,
    pendingQuotes: 0,
    acceptedQuotes: 0,
    totalInvoices: 0,
    paidInvoices: 0,
    overdueInvoices: 0,
    totalCustomers: 0,
    totalVehicles: 0,
    recentActivity: []
  });

  useEffect(() => {
    if (!isAdmin) return;

    loadUsers();
    loadStats();
    loadArticles();
    loadCustomers();
    loadVehicles();
    loadServices();
    loadSettings();
    loadQuotes();
    loadInvoices();
    loadAppointments();
    loadParts();
    loadDashboardStats();

    // Abonnements temps réel
    const unsubQuotes = pb.collection('quotes').subscribe('*', async () => {
      try { await loadQuotes(); } catch (e) { console.error('Erreur reload quotes realtime:', e); }
    });

    const unsubInvoices = pb.collection('invoices').subscribe('*', async () => {
      try { await loadInvoices(); } catch (e) { console.error('Erreur reload invoices realtime:', e); }
    });

    const unsubCustomers = pb.collection('customers').subscribe('*', async () => {
      try { await loadCustomers(); } catch (e) { console.error('Erreur reload customers realtime:', e); }
    });

    const unsubVehicles = pb.collection('vehicles').subscribe('*', async () => {
      try { await loadVehicles(); } catch (e) { console.error('Erreur reload vehicles realtime:', e); }
    });

    const unsubAppointments = pb.collection('appointments').subscribe('*', async () => {
      try { await loadAppointments(); } catch (e) { console.error('Erreur reload appointments realtime:', e); }
    });

    const unsubServices = pb.collection('services').subscribe('*', async () => {
      try { await loadServices(); } catch (e) { console.error('Erreur reload services realtime:', e); }
    });

    const unsubParts = pb.collection('parts_inventory').subscribe('*', async () => {
      try { await loadParts(); } catch (e) { console.error('Erreur reload parts realtime:', e); }
    });

    const unsubSettings = pb.collection('settings').subscribe('*', async () => {
      try { await loadSettings(); } catch (e) { console.error('Erreur reload settings realtime:', e); }
    });

    return () => {
      try { unsubQuotes?.(); } catch {}
      try { unsubInvoices?.(); } catch {}
      try { unsubCustomers?.(); } catch {}
      try { unsubVehicles?.(); } catch {}
      try { unsubAppointments?.(); } catch {}
      try { unsubServices?.(); } catch {}
      try { unsubParts?.(); } catch {}
      try { unsubSettings?.(); } catch {}
    };
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && activeTab === 'quotes_history' && quoteLogs.length === 0 && !quoteLogsLoading) {
      loadQuoteLogs();
    }
  }, [isAdmin, activeTab]);

  // Recharger les stats du dashboard quand les données changent
  useEffect(() => {
    if (customers.length > 0 || vehicles.length > 0 || quotes.length > 0 || invoices.length > 0) {
      loadDashboardStats();
    }
  }, [customers, vehicles, quotes, invoices]);

  // Fermer les menus d'actions au clic extérieur
  useEffect(() => {
    const onDocClick = () => {
      setShowQuoteMenuFor(null);
      setShowInvoiceMenuFor(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // Bascule en menu mobile dès que les onglets débordent
  useEffect(() => {
    let debouncing = null;
    const checkOverflowNow = () => {
      if (!tabsRef.current || !subNavContainerRef.current) return;
      const tabs = tabsRef.current;
      const container = subNavContainerRef.current;
      // Petit tampon pour "avant qu'un élément sorte de l'écran"
      const safetyPadding = 16;
      const available = container.clientWidth - safetyPadding;
      const overflow = (tabs.scrollWidth - available) > 2; // petite hystérèse pour éviter l'oscillation
      setIsTabsOverflow(overflow);
      if (!overflow) setSubNavOpen(false);
    };
    const checkOverflow = () => {
      if (debouncing) clearTimeout(debouncing);
      debouncing = setTimeout(checkOverflowNow, 50);
    };
    checkOverflow();
    // re-check après rendu
    const raf = requestAnimationFrame(checkOverflow);
    const t = setTimeout(checkOverflow, 150);
    // re-check après chargement des polices (évite le shift au chargement)
    try { document.fonts && document.fonts.ready && document.fonts.ready.then(checkOverflow); } catch {}
    window.addEventListener('resize', checkOverflow);
    window.addEventListener('load', checkOverflow);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (debouncing) clearTimeout(debouncing);
      window.removeEventListener('resize', checkOverflow);
      window.removeEventListener('load', checkOverflow);
    };
  }, [activeMainTab, activeTab, quotes.length, invoices.length]);

  // Rediriger si pas admin
  if (authLoading) {
    return <div className="loading-spinner">Chargement...</div>;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('🔄 Tentative de récupération des utilisateurs...');
      
      // Essayer d'abord la méthode collection standard (respecte les API Rules)
      try {
        const records = await pb.collection('users').getFullList({
          sort: '-created',
        });
        console.log('✅ ' + records.length + ' utilisateurs chargés via collection standard.');
        setUsers(records);
        return;
      } catch (collError) {
        console.warn('⚠️ Échec collection standard, tentative API directe:', collError.message);
        
        // Tentative via API directe si Admin SuperUser est éventuellement stocké (cas rare mais possible)
        const response = await fetch(`${pb.baseUrl}/api/collections/users/records?sort=-created`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${pb.authStore.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const items = data.items || [];
          console.log('✅ ' + items.length + ' utilisateurs chargés via API directe.');
          setUsers(items);
          return;
        } else if (response.status === 403) {
          console.error('❌ Erreur 403: Permission refusée. Vérifiez les API Rules de la collection "users" dans PocketBase.');
          // Si on est Admin App mais pas Admin PocketBase (SuperUser), on ne voit que soi-même par défaut
          if (pb.authStore.model) {
            setUsers([pb.authStore.model]);
          }
        } else {
          throw new Error('Erreur API ' + response.status);
        }
      }
    } catch (error) {
      console.error('❌ Erreur critique chargement utilisateurs:', error.message);
      // Fallback sécurisé : au moins l'utilisateur courant
      if (pb.authStore.model) {
        setUsers([pb.authStore.model]);
      } else {
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      setLoading(true);
      const records = await pb.collection('articles').getFullList({
        sort: '-created',
        expand: 'author'
      });
      setArticles(records);
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
      // Si la collection n'existe pas encore, on initialise un tableau vide
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadParts = async () => {
    try {
      setLoading(true);
      const records = await pb.collection('parts_inventory').getFullList({
        sort: '-created'
      });
      setParts(records);
    } catch (error) {
      console.error('Erreur lors du chargement des pièces:', error);
      setParts([]);
    } finally {
      setLoading(false);
    }
  };

  const savePart = async (partData) => {
    try {
      if (editingPart) {
        await pb.collection('parts_inventory').update(editingPart.id, partData);
      } else {
        await pb.collection('parts_inventory').create(partData);
      }
      await loadParts();
      setShowPartForm(false);
      setEditingPart(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la pièce:', error);
      throw error;
    }
  };

  const deletePart = async (partId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette pièce ?')) {
      try {
        await pb.collection('parts_inventory').delete(partId);
        await loadParts();
      } catch (error) {
        console.error('Erreur lors de la suppression de la pièce:', error);
      }
    }
  };

  const loadStats = async () => {
    try {
      const allUsers = await pb.collection('users').getFullList();
      const adminCount = allUsers.filter(u => u.role === 'admin').length;
      const regularCount = allUsers.filter(u => u.role === 'user' || !u.role).length;
      
      // Utilisateurs des 7 derniers jours
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const recentCount = allUsers.filter(u => new Date(u.created) > weekAgo).length;

      // Statistiques des articles
      let totalArticles = 0;
      let publishedArticles = 0;
      try {
        const allArticles = await pb.collection('articles').getFullList();
        totalArticles = allArticles.length;
        publishedArticles = allArticles.filter(a => a.status === 'published').length;
      } catch (error) {
        // Collection articles n'existe pas encore
        console.log('Collection articles pas encore créée');
      }

      setStats({
        totalUsers: allUsers.length,
        adminUsers: adminCount,
        regularUsers: regularCount,
        recentUsers: recentCount,
        totalArticles,
        publishedArticles
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const createArticle = async (articleData) => {
    try {
      // Vérifier l'unicité du slug
      const existingSlugs = await pb.collection('articles').getFullList({
        filter: `slug = "${articleData.slug}"`
      });
      
      if (existingSlugs.length > 0) {
        return { success: false, error: 'Ce slug existe déjà. Veuillez en choisir un autre.' };
      }

      // Créer un FormData pour inclure les fichiers
      const formData = new FormData();
      
      // Ajouter les champs texte
      formData.append('title', articleData.title);
      formData.append('slug', articleData.slug);
      formData.append('excerpt', articleData.excerpt);
      formData.append('content', articleData.content);
      formData.append('category', articleData.category);
      formData.append('status', articleData.status);
      formData.append('author', user.id);
      
      // Ajouter les fichiers images
      if (articleData.imageFiles && articleData.imageFiles.length > 0) {
        articleData.imageFiles.forEach((file) => {
          formData.append('image', file);
        });
      }
      
      await pb.collection('articles').create(formData);
      await loadArticles();
      await loadStats();
      setShowArticleForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création de l\'article:', error);
      return { success: false, error: error.message };
    }
  };

  const updateArticle = async (id, articleData) => {
    try {
      // Vérifier l'unicité du slug (exclure l'article actuel)
      const existingSlugs = await pb.collection('articles').getFullList({
        filter: `slug = "${articleData.slug}" && id != "${id}"`
      });
      
      if (existingSlugs.length > 0) {
        return { success: false, error: 'Ce slug existe déjà. Veuillez en choisir un autre.' };
      }

      // Créer un FormData pour inclure les fichiers
      const formData = new FormData();
      
      // Ajouter les champs texte
      formData.append('title', articleData.title);
      formData.append('slug', articleData.slug);
      formData.append('excerpt', articleData.excerpt);
      formData.append('content', articleData.content);
      formData.append('category', articleData.category);
      formData.append('status', articleData.status);
      
      // Gérer les images existantes à conserver
      if (articleData.existingImages && articleData.existingImages.length > 0) {
        articleData.existingImages.forEach((imageName) => {
          formData.append('image', imageName);
        });
      }
      
      // Ajouter les nouveaux fichiers images
      if (articleData.imageFiles && articleData.imageFiles.length > 0) {
        articleData.imageFiles.forEach((file) => {
          formData.append('image', file);
        });
      }
      
      await pb.collection('articles').update(id, formData);
      await loadArticles();
      await loadStats();
      setEditingArticle(null);
      setShowArticleForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'article:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteArticle = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        await pb.collection('articles').delete(id);
        await loadArticles();
        await loadStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de l\'article');
      }
    }
  };

  // Fonctions CRUD pour les clients
  const loadCustomers = async () => {
    try {
      const records = await pb.collection('customers').getFullList({
        sort: '-created',
        expand: 'created_by'
      });
      setCustomers(records);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
      setCustomers([]);
    }
  };

  // Fonction pour générer un ID selon le pattern [a-z0-9]{15}
  const generateCustomerId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 15; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Fonction pour générer un ID unique
  const generateUniqueCustomerId = async () => {
    let attempts = 0;
    const maxAttempts = 5;
    
    while (attempts < maxAttempts) {
      const id = generateCustomerId();
      try {
        // Vérifier si l'ID existe déjà
        await pb.collection('customers').getOne(id);
        // Si on arrive ici, l'ID existe déjà, on réessaie
        attempts++;
        console.log(`🔄 ID ${id} existe déjà, tentative ${attempts}/${maxAttempts}`);
      } catch (error) {
        // Si l'erreur est 404 (not found), l'ID est unique
        if (error.status === 404) {
          console.log(`✅ ID unique généré: ${id}`);
          return id;
        }
        // Pour toute autre erreur, on réessaie
        attempts++;
        console.log(`⚠️ Erreur lors de la vérification d'ID: ${error.status}, tentative ${attempts}/${maxAttempts}`);
      }
    }
    
    // Si on n'arrive pas à générer un ID unique, on utilise un ID basé sur le timestamp
    const timestamp = Date.now().toString();
    const chars = 'abcdefghijklmnopqrstuvwxyz';
    const randomChars = Array.from({length: 3}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const timestampId = timestamp.slice(-12) + randomChars; // 12 chiffres + 3 lettres = 15 caractères
    console.log(`🆔 ID de fallback généré: ${timestampId}`);
    return timestampId;
  };

  const createCustomer = async (customerData) => {
    try {
      console.log('🚀 Début création client:', customerData);
      
      // Essayer d'abord sans ID personnalisé
      const data = {
        name: customerData.name || 'Client sans nom'
      };
      
      // Ajouter les autres champs seulement s'ils existent et ne sont pas vides
      if (customerData.email && customerData.email.trim()) {
        data.email = customerData.email.trim();
      }
      if (customerData.phone && customerData.phone.trim()) {
        data.phone = customerData.phone.trim();
      }
      if (customerData.address && customerData.address.trim()) {
        data.address = customerData.address.trim();
      }
      if (customerData.company && customerData.company.trim()) {
        data.company = customerData.company.trim();
      }
      if (customerData.notes && customerData.notes.trim()) {
        data.notes = customerData.notes.trim();
      }
      
      // Temporairement, on ne met pas created_by car l'utilisateur n'existe pas dans _pb_users_auth_
      // TODO: Gérer correctement la relation created_by plus tard
      // if (user && user.id) {
      //   data.created_by = user.id;
      // }
      
      console.log('🔍 Données à envoyer (sans ID):', data);
      
      // Essayer la création normale d'abord
      let result;
      try {
        result = await pb.collection('customers').create(data);
        console.log('✅ Client créé avec succès (méthode normale):', result);
      } catch (normalError) {
        console.log('⚠️ Création normale échouée, essai avec ID personnalisé...');
        
        // Si ça échoue, essayer avec un ID personnalisé via API directe
        const uniqueId = await generateUniqueCustomerId();
        const dataWithId = { ...data, id: uniqueId };
        
        console.log('🔍 Données avec ID personnalisé:', dataWithId);
        
        // Utiliser l'API directe avec fetch
        const response = await fetch(`${pb.baseUrl}/api/collections/customers/records`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${pb.authStore.token}`
          },
          body: JSON.stringify(dataWithId)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ Erreur API directe:', errorData);
          throw new Error(`API Error: ${response.status} - ${JSON.stringify(errorData)}`);
        }
        
        result = await response.json();
        console.log('✅ Client créé avec succès (API directe):', result);
      }
      
      await loadCustomers();
      await loadStats();
      setShowCustomerForm(false);
      return { success: true };
      
    } catch (error) {
      console.error('❌ Erreur lors de la création du client:', error);
      console.error('❌ Message d\'erreur:', error.message);
      console.error('❌ Détails complets:', error.response?.data || error);
      console.error('❌ Status:', error.status);
      
      // Afficher les détails de validation PocketBase
      if (error.response?.data?.data) {
        console.error('❌ Erreurs de validation PocketBase:', error.response.data.data);
      }
      
      return { success: false, error: error.message };
    }
  };

  const updateCustomer = async (id, customerData) => {
    try {
      await pb.collection('customers').update(id, customerData);
      await loadCustomers();
      await loadStats();
      setEditingCustomer(null);
      setShowCustomerForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du client:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteCustomer = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        await pb.collection('customers').delete(id);
        await loadCustomers();
        await loadStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du client');
      }
    }
  };

  // Fonctions CRUD pour les véhicules
  const loadVehicles = async () => {
    try {
      const records = await pb.collection('vehicles').getFullList({
        sort: '-created',
        expand: 'customer_id'
      });
      setVehicles(records);
    } catch (error) {
      console.error('Erreur lors du chargement des véhicules:', error);
      setVehicles([]);
    }
  };

  const getVehiclesByCustomer = (customerId) => {
    return vehicles.filter(vehicle => vehicle.customer_id === customerId);
  };

  const createVehicle = async (vehicleData) => {
    try {
      await pb.collection('vehicles').create(vehicleData);
      await loadVehicles();
      setShowVehicleForm(false);
      setSelectedCustomerForVehicle(null);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création du véhicule:', error);
      return { success: false, error: error.message };
    }
  };

  const updateVehicle = async (id, vehicleData) => {
    try {
      await pb.collection('vehicles').update(id, vehicleData);
      await loadVehicles();
      setEditingVehicle(null);
      setShowVehicleForm(false);
      setSelectedCustomerForVehicle(null);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du véhicule:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteVehicle = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) {
      try {
        await pb.collection('vehicles').delete(id);
        await loadVehicles();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du véhicule');
      }
    }
  };

  // Fonctions CRUD pour les services
  const loadServices = async () => {
    try {
      const records = await pb.collection('services_catalog').getFullList({
        sort: 'category,name'
      });
      setServices(records);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      setServices([]);
    }
  };

  const getServiceCategories = () => {
    const categories = [...new Set(services.map(service => service.category))].filter(Boolean);
    return categories.sort();
  };

  const createService = async (serviceData) => {
    try {
      await pb.collection('services_catalog').create(serviceData);
      await loadServices();
      setShowServiceForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
      return { success: false, error: error.message };
    }
  };

  const createPart = async (partData) => {
    try {
      const newPart = await pb.collection('parts_inventory').create(partData);
      await loadParts();
      return { success: true, part: newPart };
    } catch (error) {
      console.error('Erreur lors de la création de la pièce:', error);
      return { success: false, error: error.message };
    }
  };

  const updateService = async (id, serviceData) => {
    try {
      await pb.collection('services_catalog').update(id, serviceData);
      await loadServices();
      setEditingService(null);
      setShowServiceForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du service:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteService = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      try {
        await pb.collection('services_catalog').delete(id);
        await loadServices();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du service');
      }
    }
  };

  // Fonctions pour les paramètres
  const loadSettings = async () => {
    try {
      const records = await pb.collection('settings').getFullList();
      if (records.length > 0) {
        const settingsData = records[0];
        // Parse business_hours if it's a string
        if (typeof settingsData.business_hours === 'string') {
          settingsData.business_hours = JSON.parse(settingsData.business_hours);
        }
        setSettings(settingsData);
      } else {
        // Create default settings if none exist
        const defaultSettings = {
          company_name: 'Christophe Dépanne',
          default_tax_rate: 20,
          quote_validity_days: 30,
          quote_counter: 1,
          invoice_counter: 1,
          quote_prefix: 'DEV',
          invoice_prefix: 'FACT',
          invoice_payment_terms: 30,
          business_hours: {
            monday: { open: '08:00', close: '18:00' },
            tuesday: { open: '08:00', close: '18:00' },
            wednesday: { open: '08:00', close: '18:00' },
            thursday: { open: '08:00', close: '18:00' },
            friday: { open: '08:00', close: '18:00' },
            saturday: { open: '08:00', close: '12:00' },
            sunday: { closed: true }
          }
        };
        
        const newSettings = await pb.collection('settings').create({
          ...defaultSettings,
          business_hours: JSON.stringify(defaultSettings.business_hours)
        });
        
        newSettings.business_hours = defaultSettings.business_hours;
        setSettings(newSettings);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      setSettings(null);
    }
  };

  const updateSettings = async (settingsData, logoFile, signatureFile) => {
    try {
      const formData = new FormData();
      
      // Add all settings data
      Object.keys(settingsData).forEach(key => {
        if (settingsData[key] !== null && settingsData[key] !== undefined) {
          formData.append(key, settingsData[key]);
        }
      });
      
      // Add files if provided
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      
      if (signatureFile) {
        formData.append('signature', signatureFile);
      }
      
      let result;
      if (settings && settings.id) {
        result = await pb.collection('settings').update(settings.id, formData);
      } else {
        result = await pb.collection('settings').create(formData);
      }
      
      // Parse business_hours back to object
      if (typeof result.business_hours === 'string') {
        result.business_hours = JSON.parse(result.business_hours);
      }
      
      setSettings(result);
      setShowSettingsForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      return { success: false, error: error.message };
    }
  };

  // Fonctions CRUD pour les devis
  const loadQuotes = async () => {
    try {
      const records = await pb.collection('quotes').getFullList({
        sort: '-created',
        expand: 'customer_id,vehicle_id'
      });
      setQuotes(records);
    } catch (error) {
      console.error('Erreur lors du chargement des devis:', error);
      setQuotes([]);
    }
  };

  const getQuoteStatuses = () => {
    return [
      { value: 'draft', label: 'Brouillon', color: '#6b7280' },
      { value: 'sent', label: 'Envoyé', color: '#3b82f6' },
      { value: 'accepted', label: 'Accepté', color: '#10b981' },
      { value: 'rejected', label: 'Refusé', color: '#ef4444' },
      { value: 'expired', label: 'Expiré', color: '#f59e0b' }
    ];
  };

  const createQuote = async (quoteData) => {
    try {
      // Increment quote counter in settings
      if (settings) {
        await pb.collection('settings').update(settings.id, {
          quote_counter: settings.quote_counter + 1
        });
        await loadSettings(); // Reload settings to get updated counter
      }

      const created = await pb.collection('quotes').create(quoteData);
      await loadQuotes();
      setShowQuoteForm(false);

      // Log activité création devis
      try {
        await logActivity({
          collection: 'quotes',
          recordId: created.id,
          action: 'create',
          message: `Devis ${created.quote_number || created.id} créé depuis l'admin`,
          context: {
            quote_number: created.quote_number || null,
            status: created.status || null,
            total: created.total || null
          }
        });

        // Archive un snapshot PDF du devis créé
        await archiveQuoteSnapshot(created, { reason: 'create' });
      } catch (e) {
        console.error('Erreur log/archivage création devis (admin):', e);
      }

      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création du devis:', error);
      return { success: false, error: error.message };
    }
  };

  const updateQuote = async (id, quoteData) => {
    try {
      const before = quotes.find(q => q.id === id) || null;

      // Si le devis était déjà accepté ou refusé, le repasser en "Envoyé" après modification
      const dataToUpdate = { ...quoteData };
      const wasFinalStatus = before && (before.status === 'accepted' || before.status === 'rejected');
      if (wasFinalStatus) {
        dataToUpdate.status = 'sent';
      }

      const updated = await pb.collection('quotes').update(id, dataToUpdate);
      await loadQuotes();
      setEditingQuote(null);
      setShowQuoteForm(false);

      // Log activité mise à jour devis
      try {
        await logActivity({
          collection: 'quotes',
          recordId: id,
          action: 'update',
          message: `Devis ${updated.quote_number || id} mis à jour depuis l'admin`,
          context: {
            before: before ? {
              status: before.status,
              total: before.total
            } : null,
            after: {
              status: updated.status,
              total: updated.total
            }
          }
        });

        // Si on a réinitialisé la décision client, ajouter un log de changement de statut
        if (wasFinalStatus) {
          await logActivity({
            collection: 'quotes',
            recordId: id,
            action: 'status_change',
            message: `Le devis ${updated.quote_number || id} a été modifié et est de nouveau en attente de votre réponse.`,
            context: {
              from_status: before?.status || null,
              to_status: 'sent'
            }
          });
        }

        // Snapshot PDF après mise à jour
        await archiveQuoteSnapshot(updated, { reason: 'update' });
      } catch (e) {
        console.error('Erreur log/archivage mise à jour devis (admin):', e);
      }
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du devis:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteQuote = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) {
      try {
        const toDelete = quotes.find(q => q.id === id) || null;
        await pb.collection('quotes').delete(id);
        await loadQuotes();

        // Log activité suppression devis (pas d'archivage PDF ici car il peut déjà exister des archives)
        try {
          await logActivity({
            collection: 'quotes',
            recordId: id,
            action: 'delete',
            message: `Devis ${toDelete?.quote_number || id} supprimé depuis l'admin`,
            context: toDelete ? {
              quote_number: toDelete.quote_number || null,
              status: toDelete.status || null,
              total: toDelete.total || null
            } : null
          });
        } catch (e) {
          console.error('Erreur log suppression devis (admin):', e);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du devis');
      }
    }
  };

  // Fonctions CRUD pour les factures
  const loadInvoices = async () => {
    try {
      const records = await pb.collection('invoices').getFullList({
        sort: '-created',
        expand: 'customer_id,vehicle_id,quote_id'
      });
      setInvoices(records);
    } catch (error) {
      console.error('Erreur lors du chargement des factures:', error);
      setInvoices([]);
    }
  };

  const getInvoiceStatuses = () => {
    return [
      { value: 'draft', label: 'Brouillon', color: '#6b7280' },
      { value: 'sent', label: 'Envoyée', color: '#3b82f6' },
      { value: 'paid', label: 'Payée', color: '#10b981' },
      { value: 'overdue', label: 'En retard', color: '#ef4444' },
      { value: 'cancelled', label: 'Annulée', color: '#f59e0b' }
    ];
  };

  const openArchiveAtLogTimeAdmin = async (log) => {
    const coll = log.collection;
    const recordId = log.record_id;
    if (!coll || !recordId) return;
    const logDate = new Date(log.created);
    try {
      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "${coll}" && record_id = "${recordId}"`,
        sort: '-created'
      }).catch(() => []);
      if (!archives || archives.length === 0) return;

      let chosen = null;
      for (const arch of archives) {
        const aDate = new Date(arch.created);
        if (aDate <= logDate) {
          chosen = arch;
          break;
        }
      }
      if (!chosen) {
        chosen = archives[archives.length - 1];
      }
      const fileName = chosen.pdf_file || chosen.file || '';
      if (!fileName) return;
      const url = `${pb.baseUrl}/api/files/document_archives/${chosen.id}/${fileName}`;
      window.open(url, '_blank');
    } catch (e) {
      console.error('Erreur ouverture archive pour log (admin):', e);
    }
  };

  const downloadArchiveAtLogTimeAdmin = async (log) => {
    const coll = log.collection;
    const recordId = log.record_id;
    if (!coll || !recordId) return;
    const logDate = new Date(log.created);
    try {
      const archives = await pb.collection('document_archives').getFullList({
        filter: `collection = "${coll}" && record_id = "${recordId}"`,
        sort: '-created'
      }).catch(() => []);
      if (!archives || archives.length === 0) return;

      let chosen = null;
      for (const arch of archives) {
        const aDate = new Date(arch.created);
        if (aDate <= logDate) {
          chosen = arch;
          break;
        }
      }
      if (!chosen) {
        chosen = archives[archives.length - 1];
      }
      const fileName = chosen.pdf_file || chosen.file || '';
      if (!fileName) return;
      const url = `${pb.baseUrl}/api/files/document_archives/${chosen.id}/${fileName}`;

      // Forcer un téléchargement en récupérant le PDF en blob puis en créant une URL temporaire
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) return;
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Erreur téléchargement archive pour log (admin):', e);
    }
  };

  const createInvoice = async (invoiceData) => {
    try {
      // Increment invoice counter in settings
      if (settings) {
        await pb.collection('settings').update(settings.id, {
          invoice_counter: settings.invoice_counter + 1
        });
        await loadSettings(); // Reload settings to get updated counter
      }

      // Mark quote as invoiced if quote_id is provided
      if (invoiceData.quote_id) {
        await pb.collection('quotes').update(invoiceData.quote_id, {
          invoiced: true
        });
        await loadQuotes(); // Reload quotes
      }

      const created = await pb.collection('invoices').create(invoiceData);
      await loadInvoices();
      setShowInvoiceForm(false);

      // Log activité création facture
      try {
        await logActivity({
          collection: 'invoices',
          recordId: created.id,
          action: 'create',
          message: `Facture ${created.invoice_number || created.id} créée depuis l'admin`,
          context: {
            invoice_number: created.invoice_number || null,
            status: created.status || null,
            total: created.total || null
          }
        });

        await archiveInvoiceSnapshot(created, { reason: 'create' });
      } catch (e) {
        console.error('Erreur log/archivage création facture (admin):', e);
      }
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création de la facture:', error);
      return { success: false, error: error.message };
    }
  };

  const updateInvoice = async (id, invoiceData) => {
    try {
      const before = invoices.find(inv => inv.id === id) || null;
      const updated = await pb.collection('invoices').update(id, invoiceData);
      await loadInvoices();
      setEditingInvoice(null);
      setShowInvoiceForm(false);

      // Log activité mise à jour facture
      try {
        await logActivity({
          collection: 'invoices',
          recordId: id,
          action: 'update',
          message: `Facture ${updated.invoice_number || id} mise à jour depuis l'admin`,
          context: {
            before: before ? {
              status: before.status,
              payment_status: before.payment_status,
              total: before.total
            } : null,
            after: {
              status: updated.status,
              payment_status: updated.payment_status,
              total: updated.total
            }
          }
        });

        await archiveInvoiceSnapshot(updated, { reason: 'update' });
      } catch (e) {
        console.error('Erreur log/archivage mise à jour facture (admin):', e);
      }
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la facture:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteInvoice = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) {
      try {
        const toDelete = invoices.find(inv => inv.id === id) || null;
        await pb.collection('invoices').delete(id);
        await loadInvoices();

        try {
          await logActivity({
            collection: 'invoices',
            recordId: id,
            action: 'delete',
            message: `Facture ${toDelete?.invoice_number || id} supprimée depuis l'admin`,
            context: toDelete ? {
              invoice_number: toDelete.invoice_number || null,
              status: toDelete.status || null,
              payment_status: toDelete.payment_status || null,
              total: toDelete.total || null
            } : null
          });
        } catch (e) {
          console.error('Erreur log suppression facture (admin):', e);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la facture');
      }
    }
  };

  // Fonctions CRUD pour les rendez-vous
  const loadAppointments = async () => {
    try {
      const records = await pb.collection('appointments').getFullList({
        sort: '-start_date',
        expand: 'customer_id,vehicle_id,quote_id'
      });
      setAppointments(records);
      // Charger les labels pour l'affichage couleur/nom
      try {
        const recs = await pb.collection('appointment_labels').getFullList({ sort: 'name' });
        setLabels(recs || []);
      } catch (e) {
        console.warn('Impossible de charger les labels:', e);
        setLabels([]);
      }
      // Charger le nombre de créneaux proposés par rendez-vous
      try {
        const slots = await pb.collection('appointment_slots').getFullList({
          fields: 'id,appointment_id,status',
          filter: "status = 'proposed'",
        });
        const counts = {};
        for (const s of slots) {
          counts[s.appointment_id] = (counts[s.appointment_id] || 0) + 1;
        }
        setAppointmentSlotsCount(counts);
      } catch (e) {
        console.warn('Impossible de charger les créneaux proposés:', e);
        setAppointmentSlotsCount({});
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
      setAppointments([]);
    }
  };

  const getAppointmentStatuses = () => {
    return [
      { value: 'scheduled', label: 'Planifié', color: '#3b82f6' },
      { value: 'in_progress', label: 'En cours', color: '#f59e0b' },
      { value: 'completed', label: 'Terminé', color: '#10b981' },
      { value: 'cancelled', label: 'Annulé', color: '#ef4444' }
    ];
  };


  const createAppointment = async (appointmentData) => {
    try {
      await pb.collection('appointments').create(appointmentData);
      await loadAppointments();
      setShowAppointmentForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la création du rendez-vous:', error);
      return { success: false, error: error.message };
    }
  };

  const updateAppointment = async (id, appointmentData) => {
    try {
      await pb.collection('appointments').update(id, appointmentData);
      await loadAppointments();
      setEditingAppointment(null);
      setShowAppointmentForm(false);
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rendez-vous:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteAppointment = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
      try {
        await pb.collection('appointments').delete(id);
        await loadAppointments();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du rendez-vous');
      }
    }
  };

  // Fonctions PDF
  const handleQuotePDF = async (quote, action = 'preview') => {
    try {
      const customer = customers.find(c => c.id === quote.customer_id);
      const vehicle = vehicles.find(v => v.id === quote.vehicle_id);
      
      await generateQuotePDF(quote, customer, vehicle, settings, action);

      // Log génération/visualisation PDF devis (action PocketBase: pdf_generated)
      await logActivity({
        collection: 'quotes',
        recordId: quote.id,
        action: 'pdf_generated',
        message: `PDF devis ${quote.quote_number || quote.id} (${action}) généré depuis l'admin`,
        context: {
          action,
          quote_number: quote.quote_number || null,
          total: quote.total || null
        }
      });
    } catch (error) {
      alert('Erreur lors de la génération du PDF du devis');
    }
  };

  const handleInvoicePDF = async (invoice, action = 'preview') => {
    try {
      const customer = customers.find(c => c.id === invoice.customer_id);
      const vehicle = vehicles.find(v => v.id === invoice.vehicle_id);
      
      await generateInvoicePDF(invoice, customer, vehicle, settings, action);

      await logActivity({
        collection: 'invoices',
        recordId: invoice.id,
        action: 'pdf_generated',
        message: `PDF facture ${invoice.invoice_number || invoice.id} (${action}) généré depuis l'admin`,
        context: {
          action,
          invoice_number: invoice.invoice_number || null,
          total: invoice.total || null
        }
      });
    } catch (error) {
      alert('Erreur lors de la génération du PDF de la facture');
    }
  };

  // Fonction pour partager un devis
  const handleShareQuote = async (quote) => {
    try {
      const customer = customers.find(c => c.id === quote.customer_id);
      const vehicle = vehicles.find(v => v.id === quote.vehicle_id);
      
      // Générer le PDF en blob pour le partage
      const pdfBlob = await generateQuotePDF(quote, customer, vehicle, settings, 'blob');
      
      // Créer un lien de partage temporaire
      const url = URL.createObjectURL(pdfBlob);
      
      // Utiliser l'API Web Share si disponible
      if (navigator.share) {
        const file = new File([pdfBlob], `Devis_${quote.quote_number}.pdf`, { type: 'application/pdf' });
        
        await navigator.share({
          title: `Devis ${quote.quote_number}`,
          text: `Devis pour ${customer?.name || 'Client'} - ${quote.total?.toFixed(2) || '0.00'}€`,
          files: [file]
        });
      } else {
        // Fallback : copier le lien dans le presse-papiers et ouvrir les options de partage
        const shareText = `Devis ${quote.quote_number} pour ${customer?.name || 'Client'} - ${quote.total?.toFixed(2) || '0.00'}€`;
        
        // Copier le texte dans le presse-papiers
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareText);
        }
        
        // Ouvrir le PDF dans un nouvel onglet pour faciliter le partage
        window.open(url, '_blank');
        
        // Afficher un message d'information
        alert('Le devis s\'ouvre dans un nouvel onglet. Vous pouvez maintenant le partager facilement !\n\nTexte copié dans le presse-papiers : ' + shareText);
      }
      
      // Nettoyer l'URL temporaire après un délai
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      
      await logActivity({
        collection: 'quotes',
        recordId: quote.id,
        action: 'pdf_share',
        message: `Devis ${quote.quote_number || quote.id} partagé depuis l'admin`,
        context: {
          quote_number: quote.quote_number || null,
          total: quote.total || null
        }
      });
    } catch (error) {
      console.error('Erreur lors du partage du devis:', error);
      alert('Erreur lors du partage du devis');
    }
  };

  // Fonction pour partager une facture
  const handleShareInvoice = async (invoice) => {
    try {
      const customer = customers.find(c => c.id === invoice.customer_id);
      const vehicle = vehicles.find(v => v.id === invoice.vehicle_id);
      
      // Générer le PDF en blob pour le partage
      const pdfBlob = await generateInvoicePDF(invoice, customer, vehicle, settings, 'blob');
      
      // Créer un lien de partage temporaire
      const url = URL.createObjectURL(pdfBlob);
      
      // Utiliser l'API Web Share si disponible
      if (navigator.share && navigator.canShare && navigator.canShare({
        title: `Facture ${invoice.invoice_number}`,
        text: `Facture pour ${customer?.name || 'Client'} - ${invoice.total?.toFixed(2) || '0.00'}€`,
        files: [new File([pdfBlob], `Facture_${invoice.invoice_number}.pdf`, { type: 'application/pdf' })]
      })) {
        await navigator.share({
          title: `Facture ${invoice.invoice_number}`,
          text: `Facture pour ${customer?.name || 'Client'} - ${invoice.total?.toFixed(2) || '0.00'}€`,
          files: [new File([pdfBlob], `Facture_${invoice.invoice_number}.pdf`, { type: 'application/pdf' })]
        });
      } else {
        // Fallback : copier le lien dans le presse-papiers et ouvrir les options de partage
        const shareText = `Facture ${invoice.invoice_number} pour ${customer?.name || 'Client'} - ${invoice.total?.toFixed(2) || '0.00'}€`;
        
        // Copier le texte dans le presse-papiers
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareText);
        }
        
        // Ouvrir le PDF dans un nouvel onglet pour faciliter le partage
        window.open(url, '_blank');
        
        // Afficher un message d'information
        alert('La facture s\'ouvre dans un nouvel onglet. Vous pouvez maintenant la partager facilement !\n\nTexte copié dans le presse-papiers : ' + shareText);
      }
      
      // Nettoyer l'URL temporaire après un délai
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      
      await logActivity({
        collection: 'invoices',
        recordId: invoice.id,
        action: 'pdf_share',
        message: `Facture ${invoice.invoice_number || invoice.id} partagée depuis l'admin`,
        context: {
          invoice_number: invoice.invoice_number || null,
          total: invoice.total || null
        }
      });
    } catch (error) {
      console.error('Erreur lors du partage de la facture:', error);
      alert('Erreur lors du partage de la facture');
    }
  };

  // Fonction pour calculer les statistiques du tableau de bord
  const loadDashboardStats = async () => {
    try {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Calcul du chiffre d'affaires total
      const totalRevenue = invoices
        .filter(inv => inv.payment_status === 'paid')
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      
      // Chiffre d'affaires du mois en cours
      const monthlyRevenue = invoices
        .filter(inv => {
          const invDate = new Date(inv.created);
          return inv.payment_status === 'paid' && 
                 invDate.getMonth() === currentMonth && 
                 invDate.getFullYear() === currentYear;
        })
        .reduce((sum, inv) => sum + (inv.total || 0), 0);
      
      // Statistiques des devis
      const totalQuotes = quotes.length;
      const pendingQuotes = quotes.filter(q => q.status === 'sent').length;
      const acceptedQuotes = quotes.filter(q => q.status === 'accepted').length;
      
      // Statistiques des factures
      const totalInvoices = invoices.length;
      const paidInvoices = invoices.filter(inv => inv.payment_status === 'paid').length;
      const overdueInvoices = invoices.filter(inv => {
        const dueDate = new Date(inv.due_date);
        return inv.payment_status !== 'paid' && dueDate < now;
      }).length;
      
      // Activité récente (derniers 10 éléments)
      const recentActivity = [
        ...quotes.slice(0, 5).map(q => ({
          type: 'quote',
          id: q.id,
          title: `Devis ${q.quote_number}`,
          customer: customers.find(c => c.id === q.customer_id)?.name || 'Client inconnu',
          date: q.created,
          status: q.status,
          amount: q.total
        })),
        ...invoices.slice(0, 5).map(inv => ({
          type: 'invoice',
          id: inv.id,
          title: `Facture ${inv.invoice_number}`,
          customer: customers.find(c => c.id === inv.customer_id)?.name || 'Client inconnu',
          date: inv.created,
          status: inv.payment_status,
          amount: inv.total
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);
      
      setDashboardStats({
        totalRevenue,
        monthlyRevenue,
        totalQuotes,
        pendingQuotes,
        acceptedQuotes,
        totalInvoices,
        paidInvoices,
        overdueInvoices,
        totalCustomers: customers.length,
        totalVehicles: vehicles.length,
        recentActivity
      });
      
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques:', error);
    }
  };


  const updateUserRole = async (userId, newRole) => {
    try {
      await pb.collection('users').update(userId, { role: newRole });
      await loadUsers();
      await loadStats();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du rôle:', error);
      alert('Erreur lors de la mise à jour du rôle');
    }
  };

  const deleteUser = async (userId) => {
    if (userId === user.id) {
      alert('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await pb.collection('users').delete(userId);
        await loadUsers();
        await loadStats();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de l\'utilisateur');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserInitial = (user) => {
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Génère un PDF stylé contenant le QR d'inscription et un texte explicatif
  const generateOnboardingQRPDF = async (action = 'download') => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = margin;

      // Titre
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(29, 78, 216); // bleu
      doc.text("Formulaire d'inscription", pageWidth / 2, y, { align: 'center' });
      y += 12;

      // Texte explicatif
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      const link = window.location.origin + '/inscription';
      const lines = doc.splitTextToSize(
        'Bonjour !\n\nPour finaliser votre inscription chez Christophe Dépanne, scannez le QR code ou utilisez le lien ci-dessous.\n\nCela nous permettra de préparer votre dossier plus rapidement. Merci et à très vite !',
        pageWidth - margin * 2
      );
      lines.forEach((line) => {
        doc.text(line, margin, y);
        y += 6;
      });
      y += 4;

      // QR code (chargé via image URL)
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}`;
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = qrUrl;
      });
      const qrSize = 100; // mm
      const qrX = (pageWidth - qrSize) / 2;
      doc.addImage(img, 'PNG', qrX, y, qrSize, qrSize);
      y += qrSize + 10;

      // Lien en clair
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Lien direct :', margin, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(37, 99, 235);
      const linkLines = doc.splitTextToSize(link, pageWidth - margin * 2);
      linkLines.forEach((line) => {
        doc.text(line, margin, y);
        y += 6;
      });

      // Action
      if (action === 'download') {
        doc.save('Inscription_QR.pdf');
      } else if (action === 'preview') {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (e) {
      alert("Impossible de générer le PDF du QR code");
    }
  };

  // Composant de détail client dans une popup
  const CustomerDetailPopup = ({ customer, onClose }) => {
    if (!customer) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content customer-detail-modal">
          <div className="modal-header">
            <h3>Détails du client</h3>
            <button className="close-modal" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="modal-body">
            <div className="customer-detail-section">
              <h4>Informations personnelles</h4>
              <div className="detail-row">
                <span className="detail-label">Nom :</span>
                <span className="detail-value">{customer.name}</span>
              </div>
              {customer.company && (
                <div className="detail-row">
                  <span className="detail-label">Entreprise :</span>
                  <span className="detail-value">{customer.company}</span>
                </div>
              )}
              {customer.email && (
                <div className="detail-row">
                  <span className="detail-label">Email :</span>
                  <a href={`mailto:${customer.email}`} className="detail-value">{customer.email}</a>
                </div>
              )}
              {customer.phone && (
                <div className="detail-row">
                  <span className="detail-label">Téléphone :</span>
                  <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className="detail-value">{customer.phone}</a>
                </div>
              )}
              {(customer.address || customer.postalCode || customer.city) && (
                <div className="detail-row">
                  <span className="detail-label">Adresse :</span>
                  <span className="detail-value">
                    {[customer.address, customer.postalCode, customer.city].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {customer.notes && (
                <div className="detail-row full-width">
                  <span className="detail-label">Notes :</span>
                  <div className="detail-value notes">{customer.notes}</div>
                </div>
              )}
            </div>

            <div className="customer-vehicles-section">
              <h4>Véhicules ({getVehiclesByCustomer(customer.id).length})</h4>
              {getVehiclesByCustomer(customer.id).length > 0 ? (
                <div className="vehicles-list">
                  {getVehiclesByCustomer(customer.id).map((vehicle) => (
                    <div key={vehicle.id} className="vehicle-item">
                      <div className="vehicle-info">
                        <div className="vehicle-name">
                          {vehicle.brand} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                        </div>
                        {vehicle.license_plate && (
                          <div className="vehicle-plate">{vehicle.license_plate}</div>
                        )}
                        {vehicle.vin && (
                          <div className="vehicle-vin">VIN: {vehicle.vin}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-vehicles">Aucun véhicule enregistré</p>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-page">
      {/* Header Admin Spécial */}
      <div className="admin-navbar">
        <div className="admin-nav-container">
          {/* Logo / Menu toujours visible en haut à gauche */}
          <div className="admin-brand">
            <Link to="/" className="home-link-nav" title="Retour au site">
              <Home size={22} />
            </Link>
            <div className="sub-nav-mobile-trigger">
              <button
                type="button"
                className={`sub-nav-logo-btn ${subNavOpen ? 'open' : ''}`}
                onClick={() => setSubNavOpen(!subNavOpen)}
                aria-label="Ouvrir le menu des onglets"
              >
                <img
                  src="/images/logo.jpeg"
                  alt="Christophe Dépanne"
                  className="sub-nav-logo-img"
                />
              </button>
              <button
                type="button"
                className="sub-nav-menu-btn"
                onClick={() => setSubNavOpen(!subNavOpen)}
              >
                Menu
              </button>
            </div>
          </div>

          {/* Navigation centrale - Boutons Gestion/Contenu */}
          <div className="admin-nav-center">
            <div className="main-nav-buttons">
              <button
                className={`main-nav-btn ${activeMainTab === 'gestion' ? 'active' : ''}`}
                onClick={() => {
                  console.log('Clic sur Gestion');
                  setActiveMainTab('gestion');
                  setActiveTab('dashboard');
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <span className="nav-label">Gestion</span>
              </button>
              <button
                className={`main-nav-btn ${activeMainTab === 'contenu' ? 'active' : ''}`}
                onClick={() => {
                  console.log('Clic sur Contenu');
                  setActiveMainTab('contenu');
                  setActiveTab('blog');
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                <span className="nav-label">Contenu</span>
              </button>
            </div>
          </div>

          {/* Menu utilisateur déroulant */}
          <div className="user-dropdown-container">
            <button 
              className="user-avatar-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              title={user?.name || 'Admin'}
            >
              <div className="user-avatar">
                {getUserInitial(user)}
              </div>
            </button>
            
            {showUserMenu && (
              <div className="user-dropdown-menu">
                <div className="user-dropdown-header">
                  <div className="user-dropdown-avatar">
                    {getUserInitial(user)}
                  </div>
                  <div className="user-dropdown-info">
                    <div className="user-dropdown-name">{user?.name || 'Admin'}</div>
                    <div className="user-dropdown-role">Administrateur</div>
                  </div>
                </div>
                
                <div className="user-dropdown-divider"></div>
                
                <div className="user-dropdown-actions">
                  <Link 
                    to="/" 
                    className="user-dropdown-item"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 11H6.83l3.58-3.59L9 6l-6 6 6 6 1.41-1.41L6.83 13H21z"/>
                    </svg>
                    Retour au site
                  </Link>
                  
                  <button 
                    className="user-dropdown-item logout-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                    </svg>
                    Se déconnecter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      

      {/* Sous-Navbar avec onglets filtrés */}
      <div className={`sub-navbar ${isTabsOverflow ? 'condensed' : ''}`}>
        <div className="sub-nav-container" ref={subNavContainerRef}>
          {subNavOpen && (
            <div className="drawer-overlay" onClick={() => setSubNavOpen(false)}></div>
          )}
          <div className={`sub-mobile-drawer ${subNavOpen ? 'open' : ''}`}>
              <div className="drawer-header">
                <div className="drawer-title">Onglets</div>
                <button className="drawer-close" onClick={() => setSubNavOpen(false)} aria-label="Fermer">×</button>
              </div>
              <div className="drawer-actions vertical">
                {activeMainTab === 'gestion' && (
                  <>
                    <button className={`drawer-btn ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => { setActiveTab('dashboard'); setSubNavOpen(false); }}>Tableau de bord</button>
                    <button className={`drawer-btn ${activeTab === 'customers' ? 'active' : ''}`} onClick={() => { setActiveTab('customers'); setSubNavOpen(false); }}>Clients</button>
                    <button className={`drawer-btn ${activeTab === 'quotes' ? 'active' : ''}`} onClick={() => { setActiveTab('quotes'); setSubNavOpen(false); }}>Devis</button>
                    <button className={`drawer-btn ${activeTab === 'invoices' ? 'active' : ''}`} onClick={() => { setActiveTab('invoices'); setSubNavOpen(false); }}>Factures</button>
                    <button className={`drawer-btn ${activeTab === 'services' ? 'active' : ''}`} onClick={() => { setActiveTab('services'); setSubNavOpen(false); }}>Prestations</button>
                    <button className={`drawer-btn ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => { setActiveTab('stock'); setSubNavOpen(false); }}>Stock</button>
                    <button className={`drawer-btn ${activeTab === 'appointments' ? 'active' : ''}`} onClick={() => { setActiveTab('appointments'); setSubNavOpen(false); }}>Rendez-vous</button>
                    <button className={`drawer-btn ${activeTab === 'quotes_history' ? 'active' : ''}`} onClick={() => { setActiveTab('quotes_history'); setSubNavOpen(false); }}>Historique</button>
                    <button className={`drawer-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); setSubNavOpen(false); }}>Paramètres</button>
                  </>
                )}
                {activeMainTab === 'contenu' && (
                  <>
                    <button className={`drawer-btn ${activeTab === 'car_listings' ? 'active' : ''}`} onClick={() => { setActiveTab('car_listings'); setSubNavOpen(false); }}>Vente Auto</button>
                    <button className={`drawer-btn ${activeTab === 'blog' ? 'active' : ''}`} onClick={() => { setActiveTab('blog'); setSubNavOpen(false); }}>Articles Blog</button>
                    <button className={`drawer-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => { setActiveTab('users'); setSubNavOpen(false); }}>Utilisateurs</button>
                  </>
                )}
              </div>
            </div>

          {showOnboardingQR && (
            <div className="modal-overlay" onClick={() => setShowOnboardingQR(false)}>
              <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="qr-header">
                  <div className="qr-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                    </svg>
                    <span>Formulaire d'inscription</span>
                  </div>
                  <button className="qr-close" onClick={() => setShowOnboardingQR(false)} aria-label="Fermer">×</button>
                </div>
                <div className="qr-body">
                  <p className="qr-text">Scannez pour accéder au formulaire</p>
                  <div className="qr-image-wrap">
                    <img
                      className="qr-image"
                      alt="QR Inscription"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(window.location.origin + '/inscription')}`}
                    />
                  </div>
                  <div className="qr-link">
                    <span>{window.location.origin}/inscription</span>
                  </div>
                </div>
                <div className="qr-actions">
                  <button
                    className="btn"
                    onClick={async () => {
                      const link = window.location.origin + '/inscription';
                      try {
                        await navigator.clipboard.writeText(link);
                        alert('Lien copié dans le presse-papiers');
                      } catch (e) {
                        alert('Impossible de copier le lien');
                      }
                    }}
                  >
                    Copier le lien
                  </button>
                  <button
                    className="btn"
                    onClick={async () => {
                      const link = window.location.origin + '/inscription';
                      const shareText = `Bonjour !\n\nPour finaliser votre inscription chez Christophe Dépanne, merci de compléter ce formulaire en ligne :\n${link}\n\nCela nous permettra de préparer votre dossier plus rapidement.\nMerci et à très vite !`;
                      if (navigator.share) {
                        try {
                          await navigator.share({ title: "Formulaire d'inscription", text: shareText, url: link });
                        } catch {}
                      } else {
                        try {
                          await navigator.clipboard.writeText(shareText);
                          alert('Texte copié dans le presse-papiers:\n\n' + shareText);
                        } catch {
                          alert('Partage non supporté. Lien : ' + link);
                        }
                      }
                    }}
                  >
                    Partager le lien
                  </button>
                  <button className="btn" onClick={() => generateOnboardingQRPDF('download')}>Télécharger PDF</button>
                  <button className="btn" onClick={() => generateOnboardingQRPDF('preview')}>Aperçu / Imprimer PDF</button>
                </div>
              </div>
            </div>
          )}
          {showInvoicesGeneralQR && (
            <div className="modal-overlay" onClick={() => setShowInvoicesGeneralQR(false)}>
              <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="qr-header">
                  <div className="qr-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                    </svg>
                    <span>Espace client</span>
                  </div>
                  <button className="qr-close" onClick={() => setShowInvoicesGeneralQR(false)} aria-label="Fermer">×</button>
                </div>
                <div className="qr-body">
                  <p className="qr-text">Scannez pour accéder à l'espace client (factures)</p>
                  <div className="qr-image-wrap">
                    <img
                      className="qr-image"
                      alt="QR Espace client"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(window.location.origin + '/mon-compte')}`}
                    />
                  </div>
                  <div className="qr-link">
                    <span>{window.location.origin}/mon-compte</span>
                  </div>
                </div>
                <div className="qr-actions">
                  <button
                    className="btn"
                    onClick={async () => {
                      const link = window.location.origin + '/mon-compte';
                      try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch {}
                    }}
                  >
                    Copier le lien
                  </button>
                </div>
              </div>
            </div>
          )}

          {showQuotesGeneralQR && (
            <div className="modal-overlay" onClick={() => setShowQuotesGeneralQR(false)}>
              <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="qr-header">
                  <div className="qr-title">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                    </svg>
                    <span>Espace client</span>
                  </div>
                  <button className="qr-close" onClick={() => setShowQuotesGeneralQR(false)} aria-label="Fermer">×</button>
                </div>
                <div className="qr-body">
                  <p className="qr-text">Scannez pour accéder à l'espace client (devis)</p>
                  <div className="qr-image-wrap">
                    <img
                      className="qr-image"
                      alt="QR Espace client"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(window.location.origin + '/mon-compte')}`}
                    />
                  </div>
                  <div className="qr-link">
                    <span>{window.location.origin}/mon-compte</span>
                  </div>
                </div>
                <div className="qr-actions">
                  <button
                    className="btn"
                    onClick={async () => {
                      const link = window.location.origin + '/mon-compte';
                      try {
                        await navigator.clipboard.writeText(link);
                        alert('Lien copié');
                      } catch {}
                    }}
                  >
                    Copier le lien
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="admin-tabs" ref={tabsRef}>
            {/* Onglets Gestion */}
            {activeMainTab === 'gestion' && (
              <>
                <button
                  className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                  onClick={() => setActiveTab('dashboard')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                  </svg>
                  Tableau de bord
                </button>
                <button
                  className={`tab-btn ${activeTab === 'customers' ? 'active' : ''}`}
                  onClick={() => setActiveTab('customers')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  Clients
                </button>
                <button
                  className={`tab-btn ${activeTab === 'quotes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quotes')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M9,13V15H15V13H9M9,16V18H13V16H9Z"/>
                  </svg>
                  Devis
                </button>
                <button
                  className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
                  onClick={() => setActiveTab('invoices')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M10,17L8,15L9.41,13.58L10,14.17L14.59,9.58L16,11L10,17Z"/>
                  </svg>
                  Factures
                </button>
                <button
                  className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
                  onClick={() => setActiveTab('services')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                  </svg>
                  Prestations
                </button>
                <button
                  className={`tab-btn ${activeTab === 'stock' ? 'active' : ''}`}
                  onClick={() => setActiveTab('stock')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 7h-3V6a4 4 0 0 0-4-4h-2a4 4 0 0 0-4 4v1H4a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM9 6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1H9V6zm8 15H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v11a1 1 0 0 1-1 1z"/>
                  </svg>
                  Stock
                </button>
                <button
                  className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('appointments')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                  </svg>
                  Rendez-vous
                </button>
                <button
                  className={`tab-btn ${activeTab === 'quotes_history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('quotes_history')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13 3a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9zm0 16a7 7 0 1 1 7-7 7.01 7.01 0 0 1-7 7zm.5-11H12v5l4.25 2.52.75-1.23-3.5-2.04z"/>
                  </svg>
                  Historique
                </button>
                <button
                  className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                  </svg>
                  Paramètres
                </button>
              </>
            )}

            {/* Onglets Contenu */}
            {activeMainTab === 'contenu' && (
              <>
                <button
                  className={`tab-btn ${activeTab === 'car_listings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('car_listings')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                  </svg>
                  Vente Auto
                </button>
                <button
                  className={`tab-btn ${activeTab === 'blog' ? 'active' : ''}`}
                  onClick={() => setActiveTab('blog')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                  Articles Blog
                </button>
                <button
                  className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 7c0 2.21-1.79 4-4 4s-4-1.79-4-4 1.79-4 4-4 4 1.79 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  Utilisateurs
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="admin-main-content">
        <div className="admin-container">
        <div className="container mx-auto px-4 py-8">
          {/* Contenu des onglets */}
          {activeTab === 'dashboard' && (
            <div className="dashboard-content">
              {/* KPIs Principaux */}
              <div className="kpi-grid">
                <div className="kpi-card revenue">
                  <div className="kpi-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                    </svg>
                  </div>
                  <div className="kpi-info">
                    <h3>{dashboardStats.totalRevenue?.toFixed(2) || '0.00'}€</h3>
                    <p>Chiffre d'Affaires Total</p>
                    <span className="kpi-subtitle">Ce mois: {dashboardStats.monthlyRevenue?.toFixed(2) || '0.00'}€</span>
                  </div>
                </div>
                
                <div className="kpi-card quotes">
                  <div className="kpi-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                  </div>
                  <div className="kpi-info">
                    <h3>{dashboardStats.totalQuotes || 0}</h3>
                    <p>Devis Total</p>
                    <span className="kpi-subtitle">
                      {dashboardStats.pendingQuotes || 0} en attente • {dashboardStats.acceptedQuotes || 0} acceptés
                    </span>
                  </div>
                </div>
                
                <div className="kpi-card invoices">
                  <div className="kpi-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M10,17L8,15L9.41,13.58L10,14.17L14.59,9.58L16,11L10,17Z"/>
                    </svg>
                  </div>
                  <div className="kpi-info">
                    <h3>{dashboardStats.totalInvoices || 0}</h3>
                    <p>Factures Total</p>
                    <span className="kpi-subtitle">
                      {dashboardStats.paidInvoices || 0} payées • {dashboardStats.overdueInvoices || 0} en retard
                    </span>
                  </div>
                </div>
                
                <div className="kpi-card customers">
                  <div className="kpi-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H16c-.8 0-1.54.37-2 1l-3.72 5.6-2.28-2.28L8 12l-2 2 2 2 2-2 2.28 2.28L16 12h2.54l1.46 4.37V22h2z"/>
                    </svg>
                  </div>
                  <div className="kpi-info">
                    <h3>{dashboardStats.totalCustomers || 0}</h3>
                    <p>Clients</p>
                    <span className="kpi-subtitle">{dashboardStats.totalVehicles || 0} véhicules</span>
                  </div>
                </div>
              </div>

              {/* Alertes importantes */}
              {dashboardStats.overdueInvoices > 0 && (
                <div className="alert-section">
                  <div className="alert-card overdue">
                    <div className="alert-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                      </svg>
                    </div>
                    <div className="alert-content">
                      <h4>⚠️ Factures en retard</h4>
                      <p>{dashboardStats.overdueInvoices} facture(s) en retard de paiement</p>
                    </div>
                    <button 
                      className="alert-action"
                      onClick={() => setActiveTab('invoices')}
                    >
                      Voir les factures
                    </button>
                  </div>
                </div>
              )}

              <div className="dashboard-grid">
                {/* Activité récente */}
                <div className="dashboard-section">
                  <div className="section-header">
                    <h3>Activité Récente</h3>
                    <span className="section-count">{dashboardStats.recentActivity?.length || 0} éléments</span>
                  </div>
                  <div className="activity-list">
                    {dashboardStats.recentActivity?.length > 0 ? (
                      dashboardStats.recentActivity.map((activity, index) => (
                        <div key={index} className={`activity-item ${activity.type}`}>
                          <div className="activity-icon">
                            {activity.type === 'quote' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M10,17L8,15L9.41,13.58L10,14.17L14.59,9.58L16,11L10,17Z"/>
                              </svg>
                            )}
                          </div>
                          <div className="activity-content">
                            <div className="activity-title">{activity.title}</div>
                            <div className="activity-meta">
                              <span className="activity-customer">{activity.customer}</span>
                              <span className="activity-date">{formatDate(activity.date)}</span>
                            </div>
                          </div>
                          <div className="activity-amount">
                            {activity.amount?.toFixed(2) || '0.00'}€
                          </div>
                          <div className={`activity-status ${activity.status}`}>
                            {activity.type === 'quote' ? (
                              activity.status === 'sent' ? 'Envoyé' :
                              activity.status === 'accepted' ? 'Accepté' :
                              activity.status === 'draft' ? 'Brouillon' : activity.status
                            ) : (
                              activity.status === 'paid' ? 'Payé' :
                              activity.status === 'pending' ? 'En attente' : activity.status
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-activity">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                        <p>Aucune activité récente</p>
                        <span>Créez votre premier devis pour commencer</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions rapides */}
                <div className="dashboard-section">
                  <div className="section-header">
                    <h3>Actions Rapides</h3>
                  </div>
                  <div className="quick-actions">
                    <button 
                      className="quick-action-btn primary"
                      onClick={() => {
                        setActiveTab('quotes');
                        setEditingQuote(null);
                        setShowQuoteForm(true);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                      Nouveau Devis
                    </button>
                    
                    <button 
                      className="quick-action-btn secondary"
                      onClick={() => {
                        setActiveTab('invoices');
                        setEditingInvoice(null);
                        setShowInvoiceForm(true);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                      Nouvelle Facture
                    </button>
                    
                    <button 
                      className="quick-action-btn tertiary"
                      onClick={() => {
                        setActiveTab('customers');
                        setEditingCustomer(null);
                        setShowCustomerForm(true);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      Nouveau Client
                    </button>

                    <button
                      className="quick-action-btn"
                      onClick={() => setShowOnboardingQR(true)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                      </svg>
                      QR Onboarding
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'customers' && (
            <div className="customers-content">
              <div className="customers-header">
                <h3>Gestion des clients</h3>
                <div className="customers-actions">
                  <div className="actions-left" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn" onClick={() => setShowOnboardingQR(true)} title="QR Onboarding" aria-label="QR Onboarding">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                      </svg>
                    </button>
                    <button
                      className="btn"
                      onClick={async () => {
                        const link = window.location.origin + '/inscription';
                        try {
                          await navigator.clipboard.writeText(link);
                          alert('Lien copié');
                        } catch {}
                      }}
                    >
                      Copier le lien
                    </button>
                    <button
                      className="btn"
                      onClick={async () => {
                        const link = window.location.origin + '/inscription';
                        const shareText = `Bonjour !\n\nPour finaliser votre inscription chez Christophe Dépanne, merci de compléter ce formulaire en ligne :\n${link}\n\nCela nous permettra de préparer votre dossier plus rapidement.\nMerci et à très vite !`;
                        if (navigator.share) {
                          try {
                            await navigator.share({ title: "Formulaire d'inscription", text: shareText, url: link });
                          } catch {}
                        } else {
                          try {
                            await navigator.clipboard.writeText(shareText);
                            alert('Texte de partage copié dans le presse-papiers:\n\n' + shareText);
                          } catch {
                            alert('Partage non supporté. Lien : ' + link);
                          }
                        }
                      }}
                    >
                      Partager
                    </button>
                  </div>
                  <div className="search-box" style={{ marginLeft: 'auto' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Rechercher un client..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    />
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setEditingCustomer(null);
                      setShowCustomerForm(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Nouveau client
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : (
                <div className="customers-grid">
                  {customers
                    .filter(customer => {
                      const q = customerSearchTerm.toLowerCase();
                      const cv = vehicles.filter(v => v.customer_id === customer.id);
                      const matchesV = cv.some(v => 
                        (v.license_plate && v.license_plate.toLowerCase().includes(q)) ||
                        (v.brand && v.brand.toLowerCase().includes(q)) ||
                        (v.model && v.model.toLowerCase().includes(q))
                      );
                      return customer.name.toLowerCase().includes(q) ||
                        (customer.email && customer.email.toLowerCase().includes(q)) ||
                        (customer.phone && customer.phone.includes(customerSearchTerm)) ||
                        matchesV;
                    })
                    .map((customer) => (
                      <div key={customer.id} className="customer-card">
                        <div className="customer-header">
                          <div className="customer-avatar">
                            {customer.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="customer-info">
                            <h4 className="customer-name">{customer.name}</h4>
                            {customer.company && (
                              <div className="customer-company">{customer.company}</div>
                            )}
                          </div>
                        </div>
                        
                        <div className="customer-details">
                          {customer.email && (
                            <div className="customer-detail">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                              </svg>
                              <span>{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="customer-detail">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                              </svg>
                              <span>{customer.phone}</span>
                            </div>
                          )}
                          {customer.address && (
                            <div className="customer-detail">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                              </svg>
                              <span>{customer.address}</span>
                            </div>
                          )}
                        </div>

                        {/* Véhicules du client */}
                        <div className="customer-vehicles">
                          <div className="vehicles-header">
                            <h5>Véhicules ({getVehiclesByCustomer(customer.id).length})</h5>
                            <button 
                              className="add-vehicle-btn"
                              onClick={() => {
                                setSelectedCustomerForVehicle(customer);
                                setEditingVehicle(null);
                                setShowVehicleForm(true);
                              }}
                              title="Ajouter un véhicule"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                              </svg>
                            </button>
                          </div>
                          
                          <div className="vehicles-list">
                            {getVehiclesByCustomer(customer.id).map((vehicle) => (
                              <div key={vehicle.id} className="vehicle-item">
                                <div className="vehicle-info">
                                  <div className="vehicle-name">
                                    {vehicle.brand} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                                  </div>
                                  {vehicle.license_plate && (
                                    <div className="vehicle-plate">{vehicle.license_plate}</div>
                                  )}
                                </div>
                                <div className="vehicle-actions">
                                  <button 
                                    className="vehicle-action-btn edit"
                                    onClick={() => {
                                      setEditingVehicle(vehicle);
                                      setSelectedCustomerForVehicle(customer);
                                      setShowVehicleForm(true);
                                    }}
                                    title="Modifier"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                    </svg>
                                  </button>
                                  <button 
                                    className="vehicle-action-btn delete"
                                    onClick={() => deleteVehicle(vehicle.id)}
                                    title="Supprimer"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                            
                            {getVehiclesByCustomer(customer.id).length === 0 && (
                              <div className="no-vehicles">
                                <span>Aucun véhicule enregistré</span>
                                <button 
                                  className="add-first-vehicle"
                                  onClick={() => {
                                    setSelectedCustomerForVehicle(customer);
                                    setEditingVehicle(null);
                                    setShowVehicleForm(true);
                                  }}
                                >
                                  Ajouter le premier véhicule
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="customer-actions">
                          <button 
                            className="action-btn edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCustomer(customer);
                              setShowCustomerDetail(true);
                            }}
                            title="Voir les détails"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                            </svg>
                          </button>
                          <div className="relative-inline">
                            <button 
                              className="action-btn add-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAddMenuFor(prev => prev === customer.id ? null : customer.id);
                              }}
                              title="Créer pour ce client"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                              </svg>
                            </button>
                            {showAddMenuFor === customer.id && (
                              <div className="add-menu">
                                <button
                                  className="add-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPrefillQuoteCustomerId(customer.id);
                                    setShowQuoteForm(true);
                                    setEditingQuote(null);
                                    setShowAddMenuFor(null);
                                  }}
                                >
                                  Créer un devis
                                </button>
                                <button
                                  className="add-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPrefillInvoiceCustomerId(customer.id);
                                    setShowInvoiceForm(true);
                                    setEditingInvoice(null);
                                    setShowAddMenuFor(null);
                                  }}
                                >
                                  Créer une facture
                                </button>
                              </div>
                            )}
                          </div>
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => {
                              setEditingCustomer(customer);
                              setShowCustomerForm(true);
                            }}
                            title="Modifier"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => deleteCustomer(customer.id)}
                            title="Supprimer"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                          </button>
                        </div>

                        <div className="customer-meta">
                          <span className="customer-date">
                            Créé le {formatDate(customer.created)}
                          </span>
                        </div>
                      </div>
                    ))}
                  
                  {customers.filter(customer => {
                    const q = customerSearchTerm.toLowerCase();
                    const cv = vehicles.filter(v => v.customer_id === customer.id);
                    const matchesV = cv.some(v => 
                      (v.license_plate && v.license_plate.toLowerCase().includes(q)) ||
                      (v.brand && v.brand.toLowerCase().includes(q)) ||
                      (v.model && v.model.toLowerCase().includes(q))
                    );
                    return customer.name.toLowerCase().includes(q) ||
                      (customer.email && customer.email.toLowerCase().includes(q)) ||
                      (customer.phone && customer.phone.includes(customerSearchTerm)) ||
                      matchesV;
                  }).length === 0 && (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <h3>Aucun client trouvé</h3>
                      <p>
                        {customerSearchTerm 
                          ? `Aucun client ne correspond à "${customerSearchTerm}"`
                          : "Commencez par ajouter votre premier client"
                        }
                      </p>
                      {!customerSearchTerm && (
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setEditingCustomer(null);
                            setShowCustomerForm(true);
                          }}
                        >
                          Créer un client
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotes_history' && (
            <div className="quotes-content">
              <div className="quotes-header">
                <h3>Historique des activités</h3>
                <div className="quotes-actions">
                  <div className="quotes-filters">
                    <div className="search-box">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher dans l'historique..."
                        value={quoteLogsSearch}
                        onChange={(e) => setQuoteLogsSearch(e.target.value)}
                      />
                    </div>
                    <select
                      value={historyTypeFilter}
                      onChange={(e) => setHistoryTypeFilter(e.target.value)}
                      className="status-filter"
                    >
                      <option value="">Tous les types</option>
                      <option value="quotes">Devis</option>
                      <option value="invoices">Factures</option>
                      <option value="others">Autres</option>
                    </select>
                    <select
                      value={quoteLogsSourceFilter}
                      onChange={(e) => setQuoteLogsSourceFilter(e.target.value)}
                      className="status-filter"
                    >
                      <option value="">Toutes les sources</option>
                      <option value="account">Espace client</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </div>

              {quoteLogsLoading ? (
                <div className="loading-spinner">Chargement de l'historique...</div>
              ) : (
                <div className="history-list">
                  {quoteLogs
                    .filter(log => {
                      if (historyTypeFilter === 'quotes' && log.collection !== 'quotes') return false;
                      if (historyTypeFilter === 'invoices' && log.collection !== 'invoices') return false;
                      if (historyTypeFilter === 'others' && (log.collection === 'quotes' || log.collection === 'invoices')) return false;

                      if (quoteLogsSourceFilter && log.source !== quoteLogsSourceFilter) return false;

                      if (!quoteLogsSearch) return true;
                      const q = quoteLogsSearch.toLowerCase();
                      return (
                        (log.message && log.message.toLowerCase().includes(q)) ||
                        (log.action && log.action.toLowerCase().includes(q)) ||
                        (log.record_id && log.record_id.toLowerCase().includes(q))
                      );
                    })
                    .map(log => {
                      const coll = log.collection || '';
                      const typeLabel = coll === 'quotes' ? 'Devis' : coll === 'invoices' ? 'Facture' : (coll || 'Autre');
                      const relatedQuote = coll === 'quotes' ? quotes.find(q => q.id === log.record_id) : null;
                      const relatedInvoice = coll === 'invoices' ? invoices.find(inv => inv.id === log.record_id) : null;
                      const canOpenQuoteHistory = !!relatedQuote;
                      const canOpenInvoiceHistory = !!relatedInvoice;

                      // Nom du client si disponible
                      const customerName =
                        relatedQuote?.expand?.customer_id?.name ||
                        relatedInvoice?.expand?.customer_id?.name ||
                        null;

                      // Label d'action en français
                      let actionLabel = '';
                      switch (log.action) {
                        case 'create':
                          actionLabel = 'Création';
                          break;
                        case 'update':
                          actionLabel = 'Mise à jour';
                          break;
                        case 'delete':
                          actionLabel = 'Suppression';
                          break;
                        case 'status_change':
                          actionLabel = 'Changement de statut';
                          break;
                        case 'pdf_generated':
                          actionLabel = 'PDF généré';
                          break;
                        case 'share':
                          actionLabel = 'Partage';
                          break;
                        default:
                          actionLabel = log.action || '';
                      }

                      // Label de source en français
                      let sourceLabel = '';
                      if (log.source === 'admin') sourceLabel = 'Admin';
                      else if (log.source === 'account') sourceLabel = 'Espace client';
                      else if (log.source) sourceLabel = log.source;

                      // Texte affiché
                      let displayText = log.message || `Action ${actionLabel || ''}`;
                      // Traduire les statuts "accepted", "sent", etc. dans les messages
                      if (displayText) {
                        displayText = displayText
                          .replace('"accepted"', '"Accepté"')
                          .replace('"rejected"', '"Refusé"')
                          .replace('"sent"', '"Envoyé"')
                          .replace('"expired"', '"Expiré"')
                          .replace('"paid"', '"Payée"')
                          .replace('"overdue"', '"En retard"')
                          .replace('"cancelled"', '"Annulée"')
                          .replace('"canceled"', '"Annulée"');
                      }
                      if (customerName) {
                        displayText = `${customerName} • ${displayText}`;
                      }

                      return (
                        <div key={log.id} className="history-row">
                          <div className="history-main">
                            <div className="history-date">
                              {(() => {
                                const d = new Date(log.created);
                                return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              })()}
                            </div>
                            <div className="history-text">{displayText}</div>
                          </div>
                          <div className="history-meta">
                            <span className="tag secondary">{typeLabel}</span>
                            {actionLabel && <span className="tag">{actionLabel}</span>}
                            {sourceLabel && <span className="tag">{sourceLabel}</span>}
                            {canOpenQuoteHistory && (
                              <button
                                type="button"
                                className="history-link-btn"
                                onClick={() => loadQuoteHistory(relatedQuote)}
                              >
                                Voir le devis
                              </button>
                            )}
                            {canOpenInvoiceHistory && (
                              <button
                                type="button"
                                className="history-link-btn"
                                onClick={() => loadInvoiceHistory(relatedInvoice)}
                              >
                                Voir la facture
                              </button>
                            )}
                            {(coll === 'quotes' || coll === 'invoices') && (
                              <>
                                <button
                                  type="button"
                                  className="history-link-btn icon-only"
                                  onClick={() => downloadArchiveAtLogTimeAdmin(log)}
                                  aria-label="Télécharger la version archivée"
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M5 20h14v-2H5v2zm7-16l-6 6h4v4h4v-4h4l-6-6z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="history-link-btn"
                                  onClick={() => openArchiveAtLogTimeAdmin(log)}
                                >
                                  Ouvrir la version archivée
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {quoteLogs
                    .filter(log => {
                      if (historyTypeFilter === 'quotes' && log.collection !== 'quotes') return false;
                      if (historyTypeFilter === 'invoices' && log.collection !== 'invoices') return false;
                      if (historyTypeFilter === 'others' && (log.collection === 'quotes' || log.collection === 'invoices')) return false;

                      if (quoteLogsSourceFilter && log.source !== quoteLogsSourceFilter) return false;

                      if (!quoteLogsSearch) return true;
                      const q = quoteLogsSearch.toLowerCase();
                      return (
                        (log.message && log.message.toLowerCase().includes(q)) ||
                        (log.action && log.action.toLowerCase().includes(q)) ||
                        (log.record_id && log.record_id.toLowerCase().includes(q))
                      );
                    }).length === 0 && (
                      <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13 3a9 9 0 1 0 9 9 9.01 9.01 0 0 0-9-9zm0 16a7 7 0 1 1 7-7 7.01 7.01 0 0 1-7 7zm-.5-11v4.79l3.5 2.08.75-1.23-2.75-1.64V8z"/>
                        </svg>
                        <h3>Aucune activité trouvée</h3>
                        <p>Aucune entrée d'historique ne correspond à vos filtres.</p>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {showQuoteHistory && historyQuote && (
            <div className="modal-overlay" onClick={() => setShowQuoteHistory(false)}>
              <div
                className="quote-history-drawer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="quote-history-header">
                  <div>
                    <h4>Historique du devis {historyQuote.quote_number}</h4>
                    <p className="quote-history-sub">{historyQuote.expand?.customer_id?.name || 'Client'} • {historyQuote.expand?.vehicle_id && `${historyQuote.expand.vehicle_id.brand} ${historyQuote.expand.vehicle_id.model}`}</p>
                  </div>
                  <button className="drawer-close-btn" onClick={() => setShowQuoteHistory(false)} aria-label="Fermer l'historique">×</button>
                </div>
                <div className="quote-history-body">
                  {quoteHistoryLoading ? (
                    <div className="loading-spinner">Chargement de l'historique…</div>
                  ) : (
                    <>
                      {(() => {
                        // Préparer un set pour suivre quelles archives sont déjà associées à un log
                        const usedArchiveIds = new Set();

                        const findMatchingArchive = (log) => {
                          if (!quoteArchives || quoteArchives.length === 0) return null;
                          const logDate = new Date(log.created);
                          // On part du principe que quoteArchives est trié du plus récent au plus ancien
                          let chosen = null;
                          for (const arch of quoteArchives) {
                            const aDate = new Date(arch.created);
                            if (aDate <= logDate) {
                              chosen = arch;
                              break;
                            }
                          }
                          if (!chosen) {
                            chosen = quoteArchives[quoteArchives.length - 1];
                          }
                          return chosen;
                        };

                        return (
                          <>
                            <div className="quote-history-section">
                              <h5>Actions & versions PDF</h5>
                              {quoteHistoryLogs.length === 0 ? (
                                <div className="empty">Aucune action enregistrée pour ce devis.</div>
                              ) : (
                                <div className="history-list">
                                  {quoteHistoryLogs.map(log => {
                                    const matchingArchive = findMatchingArchive(log);
                                    let archiveUrl = null;
                                    if (matchingArchive) {
                                      usedArchiveIds.add(matchingArchive.id);
                                      const fileName = matchingArchive.pdf_file || matchingArchive.file || '';
                                      if (fileName) {
                                        archiveUrl = `${pb.baseUrl}/api/files/document_archives/${matchingArchive.id}/${fileName}`;
                                      }
                                    }

                                    // Texte affiché dans le popup : on réutilise le message, avec traduction de statut si présent
                                    let popupText = log.message || `Action ${log.action || ''}`;
                                    if (popupText) {
                                      popupText = popupText
                                        .replace('"accepted"', '"Accepté"')
                                        .replace('"rejected"', '"Refusé"')
                                        .replace('"sent"', '"Envoyé"')
                                        .replace('"expired"', '"Expiré"')
                                        .replace('"paid"', '"Payée"')
                                        .replace('"overdue"', '"En retard"')
                                        .replace('"cancelled"', '"Annulée"')
                                        .replace('"canceled"', '"Annulée"');
                                    }

                                    // Badges d'action et de source en français
                                    let actionLabel = '';
                                    switch (log.action) {
                                      case 'create':
                                        actionLabel = 'Création';
                                        break;
                                      case 'update':
                                        actionLabel = 'Mise à jour';
                                        break;
                                      case 'delete':
                                        actionLabel = 'Suppression';
                                        break;
                                      case 'status_change':
                                        actionLabel = 'Changement de statut';
                                        break;
                                      case 'pdf_generated':
                                        actionLabel = 'PDF généré';
                                        break;
                                      case 'share':
                                        actionLabel = 'Partage';
                                        break;
                                      default:
                                        actionLabel = log.action || '';
                                    }

                                    let sourceLabel = '';
                                    if (log.source === 'admin') sourceLabel = 'Admin';
                                    else if (log.source === 'account') sourceLabel = 'Espace client';
                                    else if (log.source) sourceLabel = log.source;

                                    const versionNumber = matchingArchive && matchingArchive.version != null
                                      ? Number(matchingArchive.version)
                                      : NaN;

                                    return (
                                      <div key={log.id} className="history-row">
                                        <div className="history-main">
                                          <div className="history-date">
                                            {(() => {
                                              const d = new Date(log.created);
                                              return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            })()}
                                          </div>
                                          <div className="history-text">{popupText}</div>
                                        </div>
                                        <div className="history-meta">
                                          {actionLabel && <span className="tag secondary">{actionLabel}</span>}
                                          {sourceLabel && <span className="tag">{sourceLabel}</span>}
                                          {matchingArchive && (
                                            <>
                                              {Number.isFinite(versionNumber) && versionNumber >= 1 && (
                                                <span className="tag secondary">Version {versionNumber}</span>
                                              )}
                                              {archiveUrl && (
                                                <a
                                                  href={archiveUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="history-link-btn"
                                                >
                                                  Ouvrir le PDF archivé
                                                </a>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="quote-history-section">
                              <h5>Archives PDF sans action associée</h5>
                              {(!quoteArchives || quoteArchives.length === 0 || quoteArchives.filter(arch => !usedArchiveIds.has(arch.id)).length === 0) ? (
                                <div className="empty">Aucune archive PDF supplémentaire pour ce devis.</div>
                              ) : (
                                <ul className="archives-list">
                                  {quoteArchives.filter(arch => !usedArchiveIds.has(arch.id)).map(arch => {
                                    const fileName = arch.pdf_file || arch.file || '';
                                    const url = fileName
                                      ? `${pb.baseUrl}/api/files/document_archives/${arch.id}/${fileName}`
                                      : null;
                                    return (
                                      <li key={arch.id} className="archive-row">
                                        <div className="archive-main">
                                          <div className="archive-title">Version {arch.version || ''}</div>
                                          <div className="archive-meta">
                                            {(() => {
                                              const d = new Date(arch.created);
                                              return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            })()}
                                          </div>
                                        </div>
                                        {url && (
                                          <a href={url} target="_blank" rel="noreferrer" className="archive-link">Ouvrir</a>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {showInvoiceHistory && historyInvoice && (
            <div className="modal-overlay" onClick={() => setShowInvoiceHistory(false)}>
              <div
                className="quote-history-drawer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="quote-history-header">
                  <div>
                    <h4>Historique de la facture {historyInvoice.invoice_number}</h4>
                    <p className="quote-history-sub">
                      {historyInvoice.expand?.customer_id?.name || 'Client'}
                      {historyInvoice.expand?.vehicle_id && (
                        <>
                          {' '}• {historyInvoice.expand.vehicle_id.brand} {historyInvoice.expand.vehicle_id.model}
                        </>
                      )}
                    </p>
                  </div>
                  <button className="drawer-close-btn" onClick={() => setShowInvoiceHistory(false)} aria-label="Fermer l'historique">×</button>
                </div>
                <div className="quote-history-body">
                  {invoiceHistoryLoading ? (
                    <div className="loading-spinner">Chargement de l'historique…</div>
                  ) : (
                    <>
                      {(() => {
                        const usedArchiveIds = new Set();

                        const findMatchingArchive = (log) => {
                          if (!invoiceArchives || invoiceArchives.length === 0) return null;
                          const logDate = new Date(log.created);
                          let chosen = null;
                          for (const arch of invoiceArchives) {
                            const aDate = new Date(arch.created);
                            if (aDate <= logDate) {
                              chosen = arch;
                              break;
                            }
                          }
                          if (!chosen) {
                            chosen = invoiceArchives[invoiceArchives.length - 1];
                          }
                          return chosen;
                        };

                        return (
                          <>
                            <div className="quote-history-section">
                              <h5>Actions & versions PDF</h5>
                              {invoiceHistoryLogs.length === 0 ? (
                                <div className="empty">Aucune action enregistrée pour cette facture.</div>
                              ) : (
                                <div className="history-list">
                                  {invoiceHistoryLogs.map(log => {
                                    const matchingArchive = findMatchingArchive(log);
                                    let archiveUrl = null;
                                    if (matchingArchive) {
                                      usedArchiveIds.add(matchingArchive.id);
                                      const fileName = matchingArchive.pdf_file || matchingArchive.file || '';
                                      if (fileName) {
                                        archiveUrl = `${pb.baseUrl}/api/files/document_archives/${matchingArchive.id}/${fileName}`;
                                      }
                                    }

                                    return (
                                      <div key={log.id} className="history-row">
                                        <div className="history-main">
                                          <div className="history-date">
                                            {(() => {
                                              const d = new Date(log.created);
                                              return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            })()}
                                          </div>
                                          <div className="history-text">{log.message || `Action ${log.action || ''}`}</div>
                                        </div>
                                        <div className="history-meta">
                                          {log.action && <span className="tag secondary">{log.action}</span>}
                                          {log.source && <span className="tag">{log.source}</span>}
                                          {matchingArchive && (
                                            <>
                                              {matchingArchive.version && (
                                                <span className="tag secondary">Version {matchingArchive.version}</span>
                                              )}
                                              {archiveUrl && (
                                                <a
                                                  href={archiveUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="history-link-btn"
                                                >
                                                  Ouvrir le PDF archivé
                                                </a>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="quote-history-section">
                              <h5>Archives PDF sans action associée</h5>
                              {(!invoiceArchives || invoiceArchives.length === 0 || invoiceArchives.filter(arch => !usedArchiveIds.has(arch.id)).length === 0) ? (
                                <div className="empty">Aucune archive PDF supplémentaire pour cette facture.</div>
                              ) : (
                                <ul className="archives-list">
                                  {invoiceArchives.filter(arch => !usedArchiveIds.has(arch.id)).map(arch => {
                                    const fileName = arch.pdf_file || arch.file || '';
                                    const url = fileName
                                      ? `${pb.baseUrl}/api/files/document_archives/${arch.id}/${fileName}`
                                      : null;
                                    return (
                                      <li key={arch.id} className="archive-row">
                                        <div className="archive-main">
                                          <div className="archive-title">Version {arch.version || ''}</div>
                                          <div className="archive-meta">
                                            {(() => {
                                              const d = new Date(arch.created);
                                              return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                            })()}
                                          </div>
                                        </div>
                                        {url && (
                                          <a href={url} target="_blank" rel="noreferrer" className="archive-link">Ouvrir</a>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="services-content">
              <div className="services-header">
                <h3>Catalogue des prestations</h3>
                <div className="services-actions">
                  <div className="services-filters">
                    <div className="search-box">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher un service..."
                        value={serviceSearchTerm}
                        onChange={(e) => setServiceSearchTerm(e.target.value)}
                      />
                    </div>
                    <select
                      value={selectedServiceCategory}
                      onChange={(e) => setSelectedServiceCategory(e.target.value)}
                      className="category-filter"
                    >
                      <option value="">Toutes les catégories</option>
                      {getServiceCategories().map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setEditingService(null);
                      setShowServiceForm(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Nouveau service
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : (
                <div className="services-grid">
                  {services
                    .filter(service => {
                      const matchesSearch = service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
                                          (service.description && service.description.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
                      const matchesCategory = !selectedServiceCategory || service.category === selectedServiceCategory;
                      return matchesSearch && matchesCategory;
                    })
                    .map((service) => (
                      <div key={service.id} className={`service-card ${!service.active ? 'inactive' : ''}`}>
                        <div className="service-header">
                          <div className="service-info">
                            <h4 className="service-name">{service.name}</h4>
                            <div className="service-category">{service.category}</div>
                          </div>
                          <div className="service-status">
                            {service.active ? (
                              <span className="status-badge active">Actif</span>
                            ) : (
                              <span className="status-badge inactive">Inactif</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="service-details">
                          <div className="service-price">
                            <span className="price-label">Prix de base</span>
                            <span className="price-value">{service.base_price}€</span>
                          </div>
                          
                          {service.duration_minutes && (
                            <div className="service-duration">
                              <span className="duration-label">Durée estimée</span>
                              <span className="duration-value">
                                {service.duration_minutes >= 60 
                                  ? `${Math.floor(service.duration_minutes / 60)}h ${service.duration_minutes % 60 > 0 ? service.duration_minutes % 60 + 'min' : ''}`
                                  : `${service.duration_minutes}min`
                                }
                              </span>
                            </div>
                          )}
                          
                          <div className="service-features">
                            {service.labor_included && (
                              <span className="feature-badge">Main d'œuvre incluse</span>
                            )}
                          </div>
                          
                          {service.description && (
                            <div className="service-description">
                              <div dangerouslySetInnerHTML={{ __html: service.description }} />
                            </div>
                          )}
                        </div>

                        <div className="service-actions">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => {
                              setEditingService(service);
                              setShowServiceForm(true);
                            }}
                            title="Modifier"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => deleteService(service.id)}
                            title="Supprimer"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                          </button>
                        </div>

                        <div className="service-meta">
                          <span className="service-date">
                            Créé le {formatDate(service.created)}
                          </span>
                        </div>
                      </div>
                    ))}
                  
                  {services.filter(service => {
                    const matchesSearch = service.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
                                        (service.description && service.description.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
                    const matchesCategory = !selectedServiceCategory || service.category === selectedServiceCategory;
                    return matchesSearch && matchesCategory;
                  }).length === 0 && (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                      </svg>
                      <h3>Aucun service trouvé</h3>
                      <p>
                        {serviceSearchTerm || selectedServiceCategory
                          ? "Aucun service ne correspond aux critères de recherche"
                          : "Commencez par ajouter votre premier service"
                        }
                      </p>
                      {!serviceSearchTerm && !selectedServiceCategory && (
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setEditingService(null);
                            setShowServiceForm(true);
                          }}
                        >
                          Créer un service
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'stock' && (
            <div className="stock-content">
              <div className="stock-header">
                <h3>Gestion du stock</h3>
                <div className="stock-actions">
                  <div className="search-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Rechercher une pièce..."
                      value={partSearchTerm}
                      onChange={(e) => setPartSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    value={selectedPartCategory}
                    onChange={(e) => setSelectedPartCategory(e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Toutes les catégories</option>
                    <option value="Filtres">Filtres</option>
                    <option value="Huiles">Huiles</option>
                    <option value="Freinage">Freinage</option>
                    <option value="Moteur">Moteur</option>
                    <option value="Transmission">Transmission</option>
                    <option value="Électrique">Électrique</option>
                    <option value="Carrosserie">Carrosserie</option>
                    <option value="Pneumatiques">Pneumatiques</option>
                  </select>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setEditingPart(null);
                      setShowPartForm(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Nouvelle pièce
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : (
                <div className="parts-grid">
                  {parts
                    .filter(part => 
                      part.name?.toLowerCase().includes(partSearchTerm.toLowerCase()) ||
                      part.reference?.toLowerCase().includes(partSearchTerm.toLowerCase()) ||
                      part.supplier?.toLowerCase().includes(partSearchTerm.toLowerCase())
                    )
                    .filter(part => !selectedPartCategory || part.category === selectedPartCategory)
                    .map((part) => (
                      <div key={part.id} className={`part-card ${part.stock_quantity <= part.min_stock_alert ? 'low-stock' : ''}`}>
                        <div className="part-header">
                          <div className="part-avatar">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          </div>
                          <div className="part-info">
                            <h4 className="part-name">{part.name}</h4>
                            <span className="part-reference">Réf: {part.reference}</span>
                          </div>
                          <div className="part-actions">
                            <button
                              className="edit-btn"
                              onClick={() => {
                                setEditingPart(part);
                                setShowPartForm(true);
                              }}
                              title="Modifier"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => deletePart(part.id)}
                              title="Supprimer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        <div className="part-details">
                          <div className="part-meta">
                            <span className="part-category">{part.category}</span>
                            {part.supplier && <span className="part-supplier">{part.supplier}</span>}
                          </div>
                          
                          <div className="part-stats">
                            <div className="stat-item">
                              <span className="stat-value">{part.stock_quantity || 0}</span>
                              <span className="stat-label">En stock</span>
                            </div>
                            <div className="stat-item">
                              <span className="stat-value">{part.selling_price || 0}€</span>
                              <span className="stat-label">Prix</span>
                            </div>
                          </div>
                        </div>

                        {part.stock_quantity <= part.min_stock_alert && (
                          <div className="part-status low-stock-warning">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                            Stock faible
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="users-content">
              {/* En-tête Premium */}
              <div className="users-premium-header">
                <div className="header-meta">
                  <div className="header-titles">
                    <h3>Gestion de l'Équipe</h3>
                    <p className="header-subtitle">Administrez les accès et les rôles des utilisateurs</p>
                  </div>
                  <div className="users-mini-stats">
                    <div className="mini-stat">
                      <span className="stat-num">{users.length}</span>
                      <span className="stat-txt">Total</span>
                    </div>
                    <div className="mini-stat admin">
                      <span className="stat-num">{users.filter(u => u.role === 'admin' || (u.email && u.email.includes('test'))).length}</span>
                      <span className="stat-txt">Admins</span>
                    </div>
                  </div>
                </div>

                <div className="users-toolbar">
                  <div className="toolbar-search">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                    </svg>
                    <input 
                      type="text" 
                      placeholder="Rechercher un membre..." 
                      className="premium-search-input"
                    />
                  </div>
                  <button className="btn-premium-action secondary" onClick={loadUsers}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>
                    </svg>
                    <span>Actualiser</span>
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-container">
                  <div className="premium-loader"></div>
                  <p>Chargement des membres...</p>
                </div>
              ) : (
                <div className="users-premium-list-container">
                  <div className="users-table-header">
                    <div className="col-user">Utilisateur</div>
                    <div className="col-email">Contact</div>
                    <div className="col-role">Niveau d'accès</div>
                    <div className="col-date">Membre depuis</div>
                    <div className="col-actions">Actions</div>
                  </div>

                  <div className="users-list-scrollable">
                    {users.length === 0 ? (
                      <div className="premium-empty-state">
                        <div className="empty-icon-wrap">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                          </svg>
                        </div>
                        <h4>Aucun membre trouvé</h4>
                        <p>La liste des utilisateurs est actuellement vide.</p>
                        <button onClick={loadUsers} className="btn-premium-action">Réessayer</button>
                      </div>
                    ) : (
                      users.map((userItem) => (
                        <div key={userItem.id} className="user-premium-row">
                          <div className="col-user">
                            <div className="user-profile-summary">
                              <div className="user-avatar-premium">
                                {getUserInitial(userItem)}
                              </div>
                              <div className="user-identity">
                                <span className="u-name">{userItem.name || 'Utilisateur Anonyme'}</span>
                                <span className="u-id">#{userItem.id.slice(0, 8)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="col-email">
                            <div className="user-contact-info">
                              <span className="u-email">{userItem.email}</span>
                              {userItem.verified && (
                                <span className="verified-status"> Verifié</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="col-role">
                            <div className="role-management">
                              <select
                                value={userItem.role || 'user'}
                                onChange={(e) => updateUserRole(userItem.id, e.target.value)}
                                className={`premium-role-select ${userItem.role || 'user'}`}
                                disabled={userItem.id === user.id}
                              >
                                <option value="user">Utilisateur standard</option>
                                <option value="admin">Administrateur</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="col-date">
                            <span className="u-signup-date">{formatDate(userItem.created)}</span>
                          </div>
                          
                          <div className="col-actions">
                            <div className="user-row-actions">
                              <button
                                className="action-btn-circle delete"
                                onClick={() => deleteUser(userItem.id)}
                                disabled={userItem.id === user.id}
                                title={userItem.id === user.id ? "Vous ne pouvez pas supprimer votre propre compte" : "Supprimer l'accès"}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'car_listings' && (
            <CarListingManager />
          )}

          {activeTab === 'blog' && (
            <div className="blog-content">
              <div className="blog-premium-header">
                <div className="header-meta">
                  <div className="header-titles">
                    <h3>Journal de l'Expert</h3>
                    <p className="header-subtitle">Gérez les récits et conseils de Christophe Dépanne</p>
                  </div>
                  <div className="blog-mini-stats">
                    <div className="mini-stat">
                      <span className="stat-num">{articles.length}</span>
                      <span className="stat-txt">Articles</span>
                    </div>
                    <div className="mini-stat published">
                      <span className="stat-num">{articles.filter(a => a.status === 'published').length}</span>
                      <span className="stat-txt">En ligne</span>
                    </div>
                    <div className="mini-stat draft">
                      <span className="stat-num">{articles.filter(a => a.status === 'draft').length}</span>
                      <span className="stat-txt">Brouillons</span>
                    </div>
                  </div>
                </div>

                <div className="blog-toolbar">
                  <div className="toolbar-search">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                    </svg>
                    <input 
                      type="text" 
                      placeholder="Chercher par titre ou contenu..." 
                      value={articleSearchTerm}
                      onChange={(e) => setArticleSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  <div className="toolbar-filters">
                    <select 
                      value={selectedArticleCategory} 
                      onChange={(e) => setSelectedArticleCategory(e.target.value)}
                      className="premium-select"
                    >
                      <option value="">Toutes les catégories</option>
                      {[...new Set(articles.map(a => a.category))].filter(Boolean).sort().map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>

                    <select 
                      value={selectedArticleStatus} 
                      onChange={(e) => setSelectedArticleStatus(e.target.value)}
                      className="premium-select"
                    >
                      <option value="">Tous les statuts</option>
                      <option value="published">Publiés</option>
                      <option value="draft">Brouillons</option>
                    </select>

                    <button className="btn-icon-secondary" onClick={loadArticles} title="Actualiser la liste">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>
                      </svg>
                    </button>

                    <button 
                      className="btn-premium-action" 
                      onClick={() => { setEditingArticle(null); setShowArticleForm(true); }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                      <span>Nouvel Article</span>
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="blog-loading-container">
                  <div className="premium-loader"></div>
                  <p>Inspiration en cours de chargement...</p>
                </div>
              ) : (
                <div className="articles-premium-grid">
                  {articles
                    .filter(a => {
                      const matchesSearch = (a.title || '').toLowerCase().includes(articleSearchTerm.toLowerCase()) || 
                                           (a.excerpt || '').toLowerCase().includes(articleSearchTerm.toLowerCase());
                      const matchesCategory = !selectedArticleCategory || a.category === selectedArticleCategory;
                      const matchesStatus = !selectedArticleStatus || a.status === selectedArticleStatus;
                      return matchesSearch && matchesCategory && matchesStatus;
                    })
                    .length === 0 ? (
                    <div className="premium-empty-state">
                      <div className="empty-illustration">
                        <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="#e2e8f0" strokeWidth="1">
                          <path d="M12 2v20M2 12h20"/><rect width="18" height="18" x="3" y="3" rx="2"/>
                        </svg>
                      </div>
                      <h4>Plume prête, mais aucun article</h4>
                      <p>Votre journal est vide. Partagez votre expertise avec vos visiteurs.</p>
                      <button className="btn-premium-action" onClick={() => { setEditingArticle(null); setShowArticleForm(true); }}>
                        Rédiger mon premier article
                      </button>
                    </div>
                  ) : (
                    articles
                      .filter(a => {
                        const matchesSearch = (a.title || '').toLowerCase().includes(articleSearchTerm.toLowerCase()) || 
                                             (a.excerpt || '').toLowerCase().includes(articleSearchTerm.toLowerCase());
                        const matchesCategory = !selectedArticleCategory || a.category === selectedArticleCategory;
                        const matchesStatus = !selectedArticleStatus || a.status === selectedArticleStatus;
                        return matchesSearch && matchesCategory && matchesStatus;
                      })
                      .map((article) => (
                        <div key={article.id} className={`article-premium-card ${article.status}`}>
                          <div className="card-visual">
                            {article.image?.[0] ? (
                              <img 
                                src={`https://christophedepanne.infocolab.online/api/files/articles/${article.id}/${article.image[0]}`} 
                                alt={article.title} 
                                className="card-img"
                              />
                            ) : (
                              <div className="card-img-placeholder">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                  <path d="M14.5 12a2.5 2.5 0 0 0-5 0"/><path d="M2 12a10 10 0 1 0 20 0 10 10 0 1 0-20 0Z"/><path d="M6.2 16.2 12 12l5.8 4.2"/>
                                </svg>
                              </div>
                            )}
                            <div className="card-overlays">
                              <span className="card-category">{article.category}</span>
                              <span className={`card-status-badge ${article.status}`}>
                                {article.status === 'published' ? 'Publié' : 'Brouillon'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="card-main">
                            <div className="card-meta">
                              <span className="card-date">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                                {formatDate(article.created)}
                              </span>
                              <span className="card-link" title="Slug de l'article">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                                </svg>
                                blog/{article.slug}
                              </span>
                            </div>
                            
                            <h4 className="card-title">{article.title}</h4>
                            <p className="card-excerpt">{article.excerpt}</p>
                            
                            <div className="card-actions">
                              <button 
                                className="action-btn edit" 
                                onClick={() => { setEditingArticle(article); setShowArticleForm(true); }}
                                title="Modifier l'article"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"/>
                                </svg>
                                <span>Modifier</span>
                              </button>
                              
                              <div className="secondary-actions">
                                <button 
                                  className="action-btn-circle" 
                                  onClick={() => window.open(`/blog/${article.slug}`, '_blank')}
                                  title="Aperçu sur le site"
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                                  </svg>
                                </button>
                                
                                <button 
                                  className="action-btn-circle delete" 
                                  onClick={() => deleteArticle(article.id)}
                                  title="Supprimer"
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2M10 11v6M14 11v6"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'quotes' && (
            <div className="quotes-content">
              <div className="quotes-header">
                <h3>Gestion des devis</h3>
                <div className="quotes-general" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button className="btn" onClick={() => setShowQuotesGeneralQR(true)} title="QR Onboarding" aria-label="QR Onboarding">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                    </svg>
                  </button>
                  <button
                    className="btn"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = [
                        'Bonjour,',
                        '',
                        'Vos devis sont disponibles dans votre espace client :',
                        accountUrl,
                        '',
                        'Vous pouvez consulter et valider votre devis depuis cet espace.',
                        '',
                        'Merci et à très vite !'
                      ].join('\n');
                      try {
                        await navigator.clipboard.writeText(shareText);
                        alert('Message copié');
                      } catch {}
                    }}
                  >
                    Copier le lien
                  </button>
                  <button
                    className="btn"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = [
                        'Bonjour,',
                        '',
                        'Vos devis sont disponibles dans votre espace client :',
                        accountUrl,
                        '',
                        'Vous pouvez consulter et valider votre devis depuis cet espace.',
                        '',
                        'Merci et à très vite !'
                      ].join('\n');
                      if (navigator.share) {
                        try {
                          await navigator.share({ title: 'Espace client - Devis', text: shareText, url: accountUrl });
                        } catch {}
                      } else {
                        try {
                          await navigator.clipboard.writeText(shareText);
                          alert('Texte de partage copié dans le presse-papiers');
                        } catch {
                          alert('Partage non supporté. Lien : ' + accountUrl);
                        }
                      }
                    }}
                  >
                    Partager
                  </button>
                </div>
                <div className="quotes-actions">
                  <div className="quotes-filters">
                    <div className="search-box">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher un devis..."
                        value={quoteSearchTerm}
                        onChange={(e) => setQuoteSearchTerm(e.target.value)}
                      />
                    </div>
                    <select
                      value={selectedQuoteStatus}
                      onChange={(e) => setSelectedQuoteStatus(e.target.value)}
                      className="status-filter"
                    >
                      <option value="">Tous les statuts</option>
                      {getQuoteStatuses().map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setEditingQuote(null);
                      setShowQuoteForm(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Nouveau devis
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : (
                <div className="quotes-grid">
                  {quotes
                    .filter(quote => {
                      const q = quoteSearchTerm.toLowerCase();
                      const matchesSearch = quote.quote_number.toLowerCase().includes(q) ||
                                          (quote.expand?.customer_id?.name && quote.expand.customer_id.name.toLowerCase().includes(q)) ||
                                          (quote.expand?.customer_id?.email && quote.expand.customer_id.email.toLowerCase().includes(q)) ||
                                          (quote.expand?.customer_id?.phone && quote.expand.customer_id.phone.includes(quoteSearchTerm)) ||
                                          (quote.expand?.vehicle_id?.license_plate && quote.expand.vehicle_id.license_plate.toLowerCase().includes(q)) ||
                                          (quote.expand?.vehicle_id?.brand && quote.expand.vehicle_id.brand.toLowerCase().includes(q)) ||
                                          (quote.expand?.vehicle_id?.model && quote.expand.vehicle_id.model.toLowerCase().includes(q));
                      const matchesStatus = !selectedQuoteStatus || quote.status === selectedQuoteStatus;
                      return matchesSearch && matchesStatus;
                    })
                    .map((quote) => {
                      const status = getQuoteStatuses().find(s => s.value === quote.status);
                      const isExpired = new Date(quote.valid_until) < new Date() && quote.status !== 'accepted';
                      
                      return (
                        <div key={quote.id} className={`quote-card ${isExpired ? 'expired' : ''}`}>
                          <div className="quote-header">
                            <div className="quote-info">
                              <h4 className="quote-number">{quote.quote_number}</h4>
                              <div className="quote-customer">
                                {quote.expand?.customer_id?.name || 'Client supprimé'}
                                {quote.expand?.customer_id?.company && (
                                  <span className="company"> - {quote.expand.customer_id.company}</span>
                                )}
                              </div>
                            </div>
                            <div className="quote-status">
                              <span 
                                className="status-badge"
                                style={{ backgroundColor: isExpired ? '#f59e0b' : status?.color }}
                              >
                                {isExpired ? 'Expiré' : status?.label}
                              </span>
                            </div>
                          </div>
                          
                          <div className="quote-details">
                            <div className="quote-vehicle">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z"/>
                              </svg>
                              <span>
                                {quote.expand?.vehicle_id ? 
                                  `${quote.expand.vehicle_id.brand} ${quote.expand.vehicle_id.model}` : 
                                  'Véhicule supprimé'
                                }
                              </span>
                            </div>
                            
                            <div className="quote-amount">
                              <span className="amount-label">Total TTC</span>
                              <span className="amount-value">{quote.total?.toFixed(2) || '0.00'}€</span>
                            </div>
                            
                            <div className="quote-dates">
                              <div className="quote-date">
                                <span className="date-label">Créé le :</span>
                                <span>{formatDate(quote.created)}</span>
                              </div>
                              <div className="quote-date">
                                <span className="date-label">Valide jusqu'au :</span>
                                <span className={isExpired ? 'expired-date' : ''}>
                                  {formatDate(quote.valid_until)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="quote-actions">
                            <div className="pdf-actions">
                              <button 
                                className="action-btn view-btn"
                                onClick={() => handleQuotePDF(quote, 'preview')}
                                disabled={isGenerating}
                                title="Aperçu PDF"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn download-btn"
                                onClick={() => handleQuotePDF(quote, 'download')}
                                disabled={isGenerating}
                                title="Télécharger PDF"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn share-btn"
                                onClick={() => handleShareQuote(quote)}
                                disabled={isGenerating}
                                title="Partager le devis"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn link-btn"
                                onClick={() => handleShareAccountLink(quote)}
                                title="Partager lien espace client"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M3.9,12a5,5,0,0,1,5-5H12V5H8.9a7,7,0,0,0,0,14H12V17H8.9A5,5,0,0,1,3.9,12Zm6.2,1h3.8V11H10.1Zm5-8H12V7h3.1a5,5,0,0,1,0,10H12v2h3.1a7,7,0,0,0,0-14Z"/>
                                </svg>
                              </button>
                            </div>
                            <div className="relative-inline">
                              <button
                                className="action-btn add-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowQuoteMenuFor(prev => prev === quote.id ? null : quote.id);
                                }}
                                title="Actions du devis"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                              </button>
                              {showQuoteMenuFor === quote.id && (
                                <div className="add-menu" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="add-menu-item"
                                    onClick={() => {
                                      // Ouvrir facture pré-remplie client (et laisser choisir le devis côté facture)
                                      setPrefillInvoiceCustomerId(quote.customer_id);
                                      setPrefillInvoiceQuoteId(quote.id);
                                      setEditingInvoice(null);
                                      setShowInvoiceForm(true);
                                      setShowQuoteMenuFor(null);
                                    }}
                                  >
                                    Créer une facture à partir du devis
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      // Dupliquer le devis (côté serveur via createQuote)
                                      const duplicatePayload = {
                                        customer_id: quote.customer_id,
                                        vehicle_id: quote.vehicle_id,
                                        status: 'draft',
                                        valid_until: quote.valid_until,
                                        notes: quote.notes || '',
                                        items: quote.items || [],
                                        discount_type: quote.discount_type || '',
                                        discount_value: Number(quote.discount_value || 0),
                                        discount_label: quote.discount_label || '',
                                        tax_enabled: typeof quote.tax_enabled === 'boolean' ? quote.tax_enabled : true,
                                        subtotal: quote.subtotal || 0,
                                        discount_total: quote.discount_total || 0,
                                        tax_amount: quote.tax_amount || 0,
                                        total: quote.total || 0,
                                        tax_rate: quote.tax_rate || (settings?.default_tax_rate || 20)
                                      };
                                      await createQuote(duplicatePayload);
                                      setShowQuoteMenuFor(null);
                                    }}
                                  >
                                    Dupliquer le devis
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      await updateQuote(quote.id, { status: 'accepted' });
                                      setShowQuoteMenuFor(null);
                                    }}
                                  >
                                    Marquer comme accepté
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      await updateQuote(quote.id, { status: 'rejected' });
                                      setShowQuoteMenuFor(null);
                                    }}
                                  >
                                    Marquer comme refusé
                                  </button>
                                </div>
                              )}
                            </div>
                            <button 
                              className="action-btn history-btn"
                              onClick={() => loadQuoteHistory(quote)}
                              title="Historique"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.06 4.94L7.64 18.36A9 9 0 1 0 13 3zm-1 4v5l4.25 2.52.75-1.23-3.5-2.08V7H12z"/>
                              </svg>
                            </button>
                            <button 
                              className="action-btn edit-btn"
                              onClick={() => {
                                setEditingQuote(quote);
                                setShowQuoteForm(true);
                              }}
                              title="Modifier"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </button>
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => deleteQuote(quote.id)}
                              title="Supprimer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  
                  {quotes.filter(quote => {
                    const q = quoteSearchTerm.toLowerCase();
                    const matchesSearch = quote.quote_number.toLowerCase().includes(q) ||
                                        (quote.expand?.customer_id?.name && quote.expand.customer_id.name.toLowerCase().includes(q)) ||
                                        (quote.expand?.customer_id?.email && quote.expand.customer_id.email.toLowerCase().includes(q)) ||
                                        (quote.expand?.customer_id?.phone && quote.expand.customer_id.phone.includes(quoteSearchTerm)) ||
                                        (quote.expand?.vehicle_id?.license_plate && quote.expand.vehicle_id.license_plate.toLowerCase().includes(q)) ||
                                        (quote.expand?.vehicle_id?.brand && quote.expand.vehicle_id.brand.toLowerCase().includes(q)) ||
                                        (quote.expand?.vehicle_id?.model && quote.expand.vehicle_id.model.toLowerCase().includes(q));
                    const matchesStatus = !selectedQuoteStatus || quote.status === selectedQuoteStatus;
                    return matchesSearch && matchesStatus;
                  }).length === 0 && (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M9,13V15H15V13H9M9,16V18H13V16H9Z"/>
                      </svg>
                      <h3>Aucun devis trouvé</h3>
                      <p>
                        {quoteSearchTerm || selectedQuoteStatus
                          ? "Aucun devis ne correspond aux critères de recherche"
                          : "Commencez par créer votre premier devis"
                        }
                      </p>
                      {!quoteSearchTerm && !selectedQuoteStatus && (
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setEditingQuote(null);
                            setShowQuoteForm(true);
                          }}
                        >
                          Créer un devis
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="invoices-content">
              <div className="invoices-header">
                <h3>Gestion des factures</h3>
                <div className="invoices-general" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button className="btn" onClick={() => setShowInvoicesGeneralQR(true)} title="QR Onboarding" aria-label="QR Onboarding">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z"/>
                    </svg>
                  </button>
                  <button
                    className="btn"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = [
                        'Bonjour,',
                        '',
                        'Vos factures sont disponibles dans votre espace client :',
                        accountUrl,
                        '',
                        'Vous pouvez consulter et régler vos factures depuis cet espace.',
                        '',
                        'Merci et à très vite !'
                      ].join('\n');
                      try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch {}
                    }}
                  >
                    Copier le lien
                  </button>
                  <button
                    className="btn"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = [
                        'Bonjour,',
                        '',
                        'Vos factures sont disponibles dans votre espace client :',
                        accountUrl,
                        '',
                        'Vous pouvez consulter et régler vos factures depuis cet espace.',
                        '',
                        'Merci et à très vite !'
                      ].join('\n');
                      if (navigator.share) {
                        try { await navigator.share({ title: 'Espace client - Factures', text: shareText, url: accountUrl }); } catch {}
                      } else {
                        try { await navigator.clipboard.writeText(shareText); alert('Texte de partage copié dans le presse-papiers'); } catch { alert('Partage non supporté. Lien : ' + accountUrl); }
                      }
                    }}
                  >
                    Partager
                  </button>
                </div>
                <div className="invoices-actions">
                  <div className="invoices-filters">
                    <div className="search-box">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher une facture..."
                        value={invoiceSearchTerm}
                        onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                      />
                    </div>
                    <select
                      value={selectedInvoiceStatus}
                      onChange={(e) => setSelectedInvoiceStatus(e.target.value)}
                      className="status-filter"
                    >
                      <option value="">Tous les statuts</option>
                      {getInvoiceStatuses().map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <button 
                    className="btn-primary"
                    onClick={() => {
                      setEditingInvoice(null);
                      setShowInvoiceForm(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Nouvelle facture
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : (
                <div className="invoices-grid">
                  {invoices
                    .filter(invoice => {
                      const q = invoiceSearchTerm.toLowerCase();
                      const matchesSearch = invoice.invoice_number.toLowerCase().includes(q) ||
                                          (invoice.expand?.customer_id?.name && invoice.expand.customer_id.name.toLowerCase().includes(q)) ||
                                          (invoice.expand?.customer_id?.email && invoice.expand.customer_id.email.toLowerCase().includes(q)) ||
                                          (invoice.expand?.customer_id?.phone && invoice.expand.customer_id.phone.includes(invoiceSearchTerm)) ||
                                          (invoice.expand?.vehicle_id?.license_plate && invoice.expand.vehicle_id.license_plate.toLowerCase().includes(q)) ||
                                          (invoice.expand?.vehicle_id?.brand && invoice.expand.vehicle_id.brand.toLowerCase().includes(q)) ||
                                          (invoice.expand?.vehicle_id?.model && invoice.expand.vehicle_id.model.toLowerCase().includes(q));
                      const matchesStatus = !selectedInvoiceStatus || invoice.status === selectedInvoiceStatus;
                      return matchesSearch && matchesStatus;
                    })
                    .map((invoice) => {
                      const status = getInvoiceStatuses().find(s => s.value === invoice.status);
                      const isOverdue = new Date(invoice.due_date) < new Date() && invoice.payment_status !== 'paid';
                      
                      return (
                        <div key={invoice.id} className={`invoice-card ${isOverdue ? 'overdue' : ''}`}>
                          <div className="invoice-header">
                            <div className="invoice-info">
                              <h4 className="invoice-number">{invoice.invoice_number}</h4>
                              <div className="invoice-customer">
                                {invoice.expand?.customer_id?.name || 'Client supprimé'}
                                {invoice.expand?.customer_id?.company && (
                                  <span className="company"> - {invoice.expand.customer_id.company}</span>
                                )}
                              </div>
                            </div>
                            <div className="invoice-status">
                              <span 
                                className="status-badge"
                                style={{ backgroundColor: isOverdue ? '#ef4444' : status?.color }}
                              >
                                {isOverdue ? 'En retard' : status?.label}
                              </span>
                            </div>
                          </div>
                          
                          <div className="invoice-details">
                            <div className="invoice-vehicle">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z"/>
                              </svg>
                              <span>
                                {invoice.expand?.vehicle_id ? 
                                  `${invoice.expand.vehicle_id.brand} ${invoice.expand.vehicle_id.model}` : 
                                  'Véhicule supprimé'
                                }
                              </span>
                            </div>
                            
                            {invoice.expand?.quote_id && (
                              <div className="invoice-quote">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                                </svg>
                                <span>Devis: {invoice.expand.quote_id.quote_number}</span>
                              </div>
                            )}
                            
                            <div className="invoice-amount">
                              <span className="amount-label">Total TTC</span>
                              <span className="amount-value">{invoice.total?.toFixed(2) || '0.00'}€</span>
                            </div>
                            
                            <div className="invoice-payment">
                              <div className="payment-status">
                                <span className="payment-label">Paiement:</span>
                                <span className={`payment-badge ${invoice.payment_status}`}>
                                  {invoice.payment_status === 'pending' && 'En attente'}
                                  {invoice.payment_status === 'partial' && 'Partiel'}
                                  {invoice.payment_status === 'paid' && 'Payé'}
                                  {invoice.payment_status === 'overdue' && 'En retard'}
                                </span>
                              </div>
                              
                              {invoice.payment_method && (
                                <div className="payment-method">
                                  <span>Mode: {invoice.payment_method === 'cash' && 'Espèces'}
                                  {invoice.payment_method === 'check' && 'Chèque'}
                                  {invoice.payment_method === 'card' && 'Carte'}
                                  {invoice.payment_method === 'transfer' && 'Virement'}
                                  {invoice.payment_method === 'other' && 'Autre'}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="invoice-dates">
                              <div className="invoice-date">
                                <span className="date-label">Créée le :</span>
                                <span>{formatDate(invoice.created)}</span>
                              </div>
                              <div className="invoice-date">
                                <span className="date-label">Échéance :</span>
                                <span className={isOverdue ? 'overdue-date' : ''}>
                                  {formatDate(invoice.due_date)}
                                </span>
                              </div>
                              {invoice.payment_date && (
                                <div className="invoice-date">
                                  <span className="date-label">Payée le :</span>
                                  <span className="paid-date">{formatDate(invoice.payment_date)}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="invoice-actions">
                            <div className="pdf-actions">
                              <button 
                                className="action-btn view-btn"
                                onClick={() => handleInvoicePDF(invoice, 'preview')}
                                disabled={isGenerating}
                                title="Aperçu PDF"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn download-btn"
                                onClick={() => handleInvoicePDF(invoice, 'download')}
                                disabled={isGenerating}
                                title="Télécharger PDF"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn share-btn"
                                onClick={() => handleShareInvoice(invoice)}
                                disabled={isGenerating}
                                title="Partager la facture"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                                </svg>
                              </button>
                              <button 
                                className="action-btn link-btn"
                                onClick={() => handleShareInvoiceAccountLink(invoice)}
                                title="Partager lien espace client"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M3.9,12a5,5,0,0,1,5-5H12V5H8.9a7,7,0,0,0,0,14H12V17H8.9A5,5,0,0,1,3.9,12Zm6.2,1h3.8V11H10.1Zm5-8H12V7h3.1a5,5,0,0,1,0,10H12v2h3.1a7,7,0,0,0,0-14Z"/>
                                </svg>
                              </button>
                            </div>
                            <div className="relative-inline">
                              <button
                                className="action-btn add-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowInvoiceMenuFor(prev => prev === invoice.id ? null : invoice.id);
                                }}
                                title="Actions de la facture"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                              </button>
                              {showInvoiceMenuFor === invoice.id && (
                                <div className="add-menu" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      // Créer un avoir: nouvelle facture avec montants inversés
                                      const negItems = (invoice.items || []).map(it => ({
                                        ...it,
                                        unit_price: -Math.abs(Number(it.unit_price || 0)),
                                        total: -Math.abs(Number(it.quantity || 0) * Number(it.unit_price || 0))
                                      }));
                                      const subtotal = negItems.reduce((s, it) => s + (Number(it.quantity||0)*Number(it.unit_price||0)), 0);
                                      const taxRate = (settings?.default_tax_rate || 20) / 100;
                                      const taxEnabled = typeof invoice.tax_enabled === 'boolean' ? invoice.tax_enabled : true;
                                      const taxAmount = taxEnabled ? (subtotal * taxRate) : 0;
                                      const total = subtotal + taxAmount;
                                      const payload = {
                                        customer_id: invoice.customer_id,
                                        vehicle_id: invoice.vehicle_id,
                                        quote_id: invoice.quote_id || '',
                                        status: 'draft',
                                        payment_status: 'pending',
                                        items: negItems,
                                        discount_type: '',
                                        discount_value: 0,
                                        discount_label: '',
                                        tax_enabled: taxEnabled,
                                        subtotal,
                                        discount_total: 0,
                                        tax_amount: taxAmount,
                                        total,
                                        tax_rate: settings?.default_tax_rate || 20,
                                        notes: `Avoir de la facture ${invoice.invoice_number}`
                                      };
                                      await createInvoice(payload);
                                      setShowInvoiceMenuFor(null);
                                    }}
                                  >
                                    Créer un avoir (note de crédit)
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      await updateInvoice(invoice.id, { payment_status: 'paid', payment_date: new Date().toISOString().split('T')[0] });
                                      setShowInvoiceMenuFor(null);
                                    }}
                                  >
                                    Marquer comme payée
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      await updateInvoice(invoice.id, { payment_status: 'partial' });
                                      setShowInvoiceMenuFor(null);
                                    }}
                                  >
                                    Marquer comme partiellement payée
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      await updateInvoice(invoice.id, { payment_status: 'overdue' });
                                      setShowInvoiceMenuFor(null);
                                    }}
                                  >
                                    Marquer comme en retard
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={async () => {
                                      const duplicatePayload = {
                                        customer_id: invoice.customer_id,
                                        vehicle_id: invoice.vehicle_id,
                                        quote_id: invoice.quote_id || '',
                                        status: 'draft',
                                        due_date: invoice.due_date,
                                        notes: invoice.notes || '',
                                        items: invoice.items || [],
                                        discount_type: invoice.discount_type || '',
                                        discount_value: Number(invoice.discount_value || 0),
                                        discount_label: invoice.discount_label || '',
                                        tax_enabled: typeof invoice.tax_enabled === 'boolean' ? invoice.tax_enabled : true,
                                        subtotal: invoice.subtotal || 0,
                                        discount_total: invoice.discount_total || 0,
                                        tax_amount: invoice.tax_amount || 0,
                                        total: invoice.total || 0,
                                        tax_rate: invoice.tax_rate || (settings?.default_tax_rate || 20)
                                      };
                                      await createInvoice(duplicatePayload);
                                      setShowInvoiceMenuFor(null);
                                    }}
                                  >
                                    Dupliquer la facture
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={() => { handleShareInvoice(invoice); setShowInvoiceMenuFor(null); }}
                                  >
                                    Envoyer par email
                                  </button>
                                  <button
                                    className="add-menu-item"
                                    onClick={() => { handleShareInvoiceAccountLink(invoice); setShowInvoiceMenuFor(null); }}
                                  >
                                    Partager lien espace client
                                  </button>
                                </div>
                              )}
                            </div>
                            <button 
                              className="action-btn history-btn"
                              onClick={() => loadInvoiceHistory(invoice)}
                              title="Historique"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.06 4.94L7.64 18.36A9 9 0 1 0 13 3zm-1 4v5l4.25 2.52.75-1.23-3.5-2.08V7H12z"/>
                              </svg>
                            </button>
                            <button 
                              className="action-btn edit-btn"
                              onClick={() => {
                                setEditingInvoice(invoice);
                                setShowInvoiceForm(true);
                              }}
                              title="Modifier"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </button>
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => deleteInvoice(invoice.id)}
                              title="Supprimer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  
                  {invoices.filter(invoice => {
                    const q = invoiceSearchTerm.toLowerCase();
                    const matchesSearch = invoice.invoice_number.toLowerCase().includes(q) ||
                                        (invoice.expand?.customer_id?.name && invoice.expand.customer_id.name.toLowerCase().includes(q)) ||
                                        (invoice.expand?.customer_id?.email && invoice.expand.customer_id.email.toLowerCase().includes(q)) ||
                                        (invoice.expand?.customer_id?.phone && invoice.expand.customer_id.phone.includes(invoiceSearchTerm)) ||
                                        (invoice.expand?.vehicle_id?.license_plate && invoice.expand.vehicle_id.license_plate.toLowerCase().includes(q)) ||
                                        (invoice.expand?.vehicle_id?.brand && invoice.expand.vehicle_id.brand.toLowerCase().includes(q)) ||
                                        (invoice.expand?.vehicle_id?.model && invoice.expand.vehicle_id.model.toLowerCase().includes(q));
                    const matchesStatus = !selectedInvoiceStatus || invoice.status === selectedInvoiceStatus;
                    return matchesSearch && matchesStatus;
                  }).length === 0 && (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z M10,17L8,15L9.41,13.58L10,14.17L14.59,9.58L16,11L10,17Z"/>
                      </svg>
                      <h3>Aucune facture trouvée</h3>
                      <p>
                        {invoiceSearchTerm || selectedInvoiceStatus
                          ? "Aucune facture ne correspond aux critères de recherche"
                          : "Commencez par créer votre première facture"
                        }
                      </p>
                      {!invoiceSearchTerm && !selectedInvoiceStatus && (
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setEditingInvoice(null);
                            setShowInvoiceForm(true);
                          }}
                        >
                          Créer une facture
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'appointments' && (
          <div className="appointments-content">
            <div className="appointments-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                Gestion des rendez-vous
                <button
                  className={`icon-btn`}
                  title={appointmentsView === 'calendar' ? 'Voir la liste' : 'Voir le calendrier'}
                  onClick={() => setAppointmentsView(prev => prev === 'calendar' ? 'list' : 'calendar')}
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8 }}
                >
                  {/* Icône agenda */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,4H18V2H16V4H8V2H6V4H5A2,2 0 0,0 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V9H19V20M7,11H9V13H7V11M11,11H13V13H11V11M15,11H17V13H15V11Z"/>
                  </svg>
                </button>
              </h3>
                <div className="view-switch" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className={`btn-secondary ${appointmentsView === 'list' ? 'active' : ''}`}
                    onClick={() => setAppointmentsView('list')}
                  >
                    Liste
                  </button>
                  <button
                    className={`btn-secondary ${appointmentsView === 'calendar' ? 'active' : ''}`}
                    onClick={() => setAppointmentsView('calendar')}
                  >
                    Calendrier
                  </button>
                </div>
                <div className="appointments-actions">
                  <div className="appointments-filters">
                    <div className="search-box">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher un rendez-vous..."
                        value={appointmentSearchTerm}
                        onChange={(e) => setAppointmentSearchTerm(e.target.value)}
                      />
                    </div>
                    <select
                      value={selectedAppointmentStatus}
                      onChange={(e) => setSelectedAppointmentStatus(e.target.value)}
                      className="status-filter"
                    >
                      <option value="">Tous les statuts</option>
                      {getAppointmentStatuses().map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button 
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setEditingAppointment(null);
                        setShowAppointmentForm(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                      </svg>
                      Nouveau rendez-vous
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        console.log('[Admin] Click Proposer un RDV');
                        setShowAppointmentProposalForm(true);
                        setTimeout(()=>console.log('[Admin] showAppointmentProposalForm=', true), 0);
                      }}
                    >
                      Proposer un RDV
                    </button>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : appointmentsView === 'calendar' ? (
                <div className="appointments-calendar" style={{ background: 'white' }}>
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    locales={[frLocale]}
                    locale="fr"
                    headerToolbar={{ start: 'prev,next today', center: 'title', end: 'dayGridMonth,timeGridWeek,timeGridDay' }}
                    height="auto"
                    eventOverlap={true}
                    slotEventOverlap={true}
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    events={appointments.map(a => {
                      const statusDef = getAppointmentStatuses().find(s => s.value === a.status);
                      const label = labels.find(l => l.id === a.label_id);
                      const color = label?.color || statusDef?.color || '#3b82f6';
                      const titleBase = a.title || (a.expand?.customer_id?.name || 'Rendez-vous');
                      const title = label?.name ? `${titleBase} · ${label.name}` : titleBase;
                      return {
                        id: a.id,
                        title,
                        start: a.start_date,
                        end: a.end_date,
                        backgroundColor: color,
                        borderColor: color,
                        display: 'block',
                      };
                    })}
                    dateClick={(info) => {
                      setEditingAppointment(null);
                      setShowAppointmentForm(true);
                    }}
                    select={async (selectionInfo) => {
                      // Préparer une création sur la plage sélectionnée
                      setEditingAppointment(null);
                      setShowAppointmentForm(true);
                    }}
                    eventClick={(info) => {
                      const appt = appointments.find(a => a.id === info.event.id);
                      if (appt) {
                        setEditingAppointment(appt);
                        setShowAppointmentForm(true);
                      }
                    }}
                    eventDrop={async (dropInfo) => {
                      try {
                        await pb.collection('appointments').update(dropInfo.event.id, {
                          start_date: dropInfo.event.start?.toISOString(),
                          end_date: dropInfo.event.end?.toISOString() || dropInfo.event.start?.toISOString(),
                        });
                        await loadAppointments();
                      } catch (e) {
                        console.error('Erreur maj RDV (drop):', e);
                        dropInfo.revert();
                      }
                    }}
                    eventResize={async (resizeInfo) => {
                      try {
                        await pb.collection('appointments').update(resizeInfo.event.id, {
                          start_date: resizeInfo.event.start?.toISOString(),
                          end_date: resizeInfo.event.end?.toISOString() || resizeInfo.event.start?.toISOString(),
                        });
                        await loadAppointments();
                      } catch (e) {
                        console.error('Erreur maj RDV (resize):', e);
                        resizeInfo.revert();
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="appointments-grid">
                  {appointments
                    .filter(appointment => {
                      const matchesSearch = appointment.title.toLowerCase().includes(appointmentSearchTerm.toLowerCase()) ||
                                          (appointment.expand?.customer_id?.name && appointment.expand.customer_id.name.toLowerCase().includes(appointmentSearchTerm.toLowerCase()));
                      const matchesStatus = !selectedAppointmentStatus || appointment.status === selectedAppointmentStatus;
                      return matchesSearch && matchesStatus;
                    })
                    .map((appointment) => {
                      const status = getAppointmentStatuses().find(s => s.value === appointment.status);
                      const startDate = new Date(appointment.start_date);
                      const endDate = new Date(appointment.end_date);
                      const isToday = startDate.toDateString() === new Date().toDateString();
                      const isPast = startDate < new Date() && appointment.status !== 'completed';
                      
                      return (
                        <div key={appointment.id} className={`appointment-card ${isPast ? 'past' : ''} ${isToday ? 'today' : ''}`}>
                          <div className="appointment-header">
                            <div className="appointment-info">
                              <h4 className="appointment-title">{appointment.title}</h4>
                              <div className="appointment-customer">
                                {appointment.expand?.customer_id?.name || 'Client supprimé'}
                                {appointment.expand?.customer_id?.company && (
                                  <span className="company"> - {appointment.expand.customer_id.company}</span>
                                )}
                              </div>
                            </div>
                            <div className="appointment-status">
                              {appointmentSlotsCount[appointment.id] > 0 && (
                                <span
                                  className="badge"
                                  title={`Créneaux proposés: ${appointmentSlotsCount[appointment.id]}`}
                                  style={{ background:'#2563eb', color:'#fff', borderRadius:9999, padding:'2px 8px', fontSize:12, marginRight:8 }}
                                >
                                  {`${appointmentSlotsCount[appointment.id]} créneau${appointmentSlotsCount[appointment.id] > 1 ? 'x' : ''} proposés`}
                                </span>
                              )}
                              <span 
                                className="status-badge"
                                style={{ backgroundColor: status?.color }}
                              >
                                {status?.label}
                              </span>
                            </div>
                          </div>
                          
                          <div className="appointment-details">
                            <div className="appointment-datetime">
                              <div className="datetime-item">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                                </svg>
                                <span>{formatDate(appointment.start_date)}</span>
                              </div>
                              <div className="datetime-item">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z"/>
                                </svg>
                                <span>
                                  {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - 
                                  {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>

                            {appointment.expand?.vehicle_id && (
                              <div className="appointment-vehicle">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z"/>
                                </svg>
                                <span>
                                  {appointment.expand.vehicle_id.brand} {appointment.expand.vehicle_id.model}
                                  {appointment.expand.vehicle_id.license_plate && ` (${appointment.expand.vehicle_id.license_plate})`}
                                </span>
                              </div>
                            )}

                            {appointment.expand?.quote_id && (
                              <div className="appointment-quote">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                                </svg>
                                <span>Devis: {appointment.expand.quote_id.quote_number}</span>
                              </div>
                            )}

                            {appointment.notes && (
                              <div className="appointment-notes">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14,10H19.5L14,4.5V10M5,3H15L21,9V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3M5,12V14H19V12H5M5,16V18H14V16H5Z"/>
                                </svg>
                                <span>{appointment.notes}</span>
                              </div>
                            )}
                          </div>

                          <div className="appointment-actions">
                            <button
                              className="action-btn"
                              title={appointment.visible_client !== false ? 'Masquer côté client' : 'Afficher côté client'}
                              onClick={async () => {
                                try {
                                  const next = !(appointment.visible_client !== false);
                                  await pb.collection('appointments').update(appointment.id, { visible_client: next });
                                  await loadAppointments();
                                } catch (err) {
                                  console.error('Erreur maj visibilité client:', err);
                                  alert("Impossible de mettre à jour la visibilité");
                                }
                              }}
                            >
                              {appointment.visible_client !== false ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 6a9.77 9.77 0 0 0-9 6 9.77 9.77 0 0 0 18 0 9.77 9.77 0 0 0-9-6Zm0 10a4 4 0 1 1 4-4 4 4 0 0 1-4 4Z"/></svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 5.27 3.28 4l16.97 16.97-1.27 1.27-2.21-2.21A9.77 9.77 0 0 1 12 20a9.77 9.77 0 0 1-9-6 10.89 10.89 0 0 1 3.47-4.36L2 5.27Zm7.11 7.11a2.89 2.89 0 0 0 3.51 3.51l-3.51-3.51ZM12 6a9.77 9.77 0 0 1 9 6 10.89 10.89 0 0 1-3.39 4.24l-1.44-1.44A8 8 0 0 0 20 12a8 8 0 0 0-8-4 8 8 0 0 0-3.8.93L6.69 7.42A10.35 10.35 0 0 1 12 6Z"/></svg>
                              )}
                            </button>
                            <button
                              className="action-btn"
                              title="Re‑proposer des créneaux"
                              onClick={() => {
                                setReproposeAppointment(appointment);
                                setShowAppointmentProposalForm(true);
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12,5V1L7,6l5,5V7c3.31,0,6,2.69,6,6c0,1.01-0.25,1.97-0.69,2.8l1.46,1.46C19.54,16.2,20,14.66,20,13C20,8.58,16.42,5,12,5z M6.69,7.2L5.23,5.74C4.46,6.97,4,8.51,4,10c0,4.42,3.58,8,8,8v4l5-5l-5-5v4c-3.31,0-6-2.69-6-6C6,9,6.25,8.03,6.69,7.2z"/></svg>
                            </button>
                            <button 
                              className="action-btn edit-btn"
                              onClick={() => {
                                setEditingAppointment(appointment);
                                setShowAppointmentForm(true);
                              }}
                              title="Modifier"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </button>
                            {appointmentSlotsCount[appointment.id] > 0 && (
                              <button
                                className="action-btn"
                                title="Choisir et confirmer un créneau"
                                onClick={async () => {
                                  try {
                                    setChooseSlotAppt(appointment);
                                    const slots = await pb.collection('appointment_slots').getFullList({
                                      filter: `appointment_id = '${appointment.id}' && status = 'proposed'`,
                                      sort: 'start',
                                    });
                                    setChooseSlotList(slots);
                                    setShowChooseSlotModal(true);
                                  } catch (e) {
                                    console.error('Erreur chargement créneaux:', e);
                                    alert('Erreur chargement des créneaux proposés');
                                  }
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 11H7v2h2v-2m4 0h-2v2h2v-2m-2-9a10 10 0 00-10 10 10 10 0 0010 10 10 10 0 0010-10A10 10 0 0011 2m0 18A8 8 0 013 12a8 8 0 018-8 8 8 0 018 8 8 8 0 01-8 8z"/></svg>
                              </button>
                            )}
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => deleteAppointment(appointment.id)}
                              title="Supprimer"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  
                  {appointments.filter(appointment => {
                    const matchesSearch = appointment.title.toLowerCase().includes(appointmentSearchTerm.toLowerCase()) ||
                                        (appointment.expand?.customer_id?.name && appointment.expand.customer_id.name.toLowerCase().includes(appointmentSearchTerm.toLowerCase()));
                    const matchesStatus = !selectedAppointmentStatus || appointment.status === selectedAppointmentStatus;
                    return matchesSearch && matchesStatus;
                  }).length === 0 && (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                      </svg>
                      <h3>Aucun rendez-vous trouvé</h3>
                      <p>
                        {appointmentSearchTerm || selectedAppointmentStatus
                          ? "Aucun rendez-vous ne correspond aux critères de recherche"
                          : "Commencez par planifier votre premier rendez-vous"
                        }
                      </p>
                      {!appointmentSearchTerm && !selectedAppointmentStatus && (
                        <button 
                          className="btn-primary"
                          onClick={() => {
                            setEditingAppointment(null);
                            setShowAppointmentForm(true);
                          }}
                        >
                          Planifier un rendez-vous
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-content">
              <div className="settings-header">
                <h3>Paramètres de l'entreprise</h3>
                <button 
                  className="btn-primary"
                  onClick={() => setShowSettingsForm(true)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  Modifier les paramètres
                </button>
              </div>

              {loading ? (
                <div className="loading-spinner">Chargement...</div>
              ) : settings ? (
                <div className="settings-overview">
                  <div className="settings-grid">
                    {/* Informations entreprise */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Informations de l'entreprise</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">Nom :</span>
                          <span className="info-value">{settings.company_name || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Email :</span>
                          <span className="info-value">{settings.company_email || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Téléphone :</span>
                          <span className="info-value">{settings.company_phone || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Adresse :</span>
                          <span className="info-value">
                            {settings.company_address ? 
                              `${settings.company_address}${settings.company_postal_code ? `, ${settings.company_postal_code}` : ''}${settings.company_city ? ` ${settings.company_city}` : ''}` 
                              : 'Non définie'
                            }
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Informations légales */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Informations légales</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">SIRET :</span>
                          <span className="info-value">{settings.siret || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">SIREN :</span>
                          <span className="info-value">{settings.siren || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">TVA :</span>
                          <span className="info-value">{settings.tva_number || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">RCS :</span>
                          <span className="info-value">{settings.rcs || 'Non défini'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Paramètres financiers */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Paramètres financiers</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7,15H9C9,16.08 10.37,17 12,17C13.63,17 15,16.08 15,15C15,13.9 13.96,13.5 11.76,12.97C9.64,12.44 7,11.78 7,9C7,7.21 8.47,5.69 10.5,5.18V3H13.5V5.18C15.53,5.69 17,7.21 17,9H15C15,7.92 13.63,7 12,7C10.37,7 9,7.92 9,9C9,10.1 10.04,10.5 12.24,11.03C14.36,11.56 17,12.22 17,15C17,16.79 15.53,18.31 13.5,18.82V21H10.5V18.82C8.47,18.31 7,16.79 7,15Z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">TVA par défaut :</span>
                          <span className="info-value">{settings.default_tax_rate}%</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Validité devis :</span>
                          <span className="info-value">{settings.quote_validity_days} jours</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Délai paiement :</span>
                          <span className="info-value">{settings.invoice_payment_terms} jours</span>
                        </div>
                      </div>
                    </div>

                    {/* Numérotation */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Numérotation</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M2,17H22V19H2V17M1.15,12.65L4,11.47L4.85,12.65L2,13.83L1.15,12.65M6.5,10L10.5,10V12L6.5,12V10M6.5,6L20.5,6V8L6.5,8V6M2,4L4,4V6L2,6V4Z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">Prochain devis :</span>
                          <span className="info-value">{settings.quote_prefix}-{String(settings.quote_counter).padStart(4, '0')}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Prochaine facture :</span>
                          <span className="info-value">{settings.invoice_prefix}-{String(settings.invoice_counter).padStart(4, '0')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Logo et signature */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Logo et signature</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19M19,19H5V5H19V19M13.96,12.29L11.21,15.83L9.25,13.47L6.5,17H17.5L13.96,12.29Z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">Logo :</span>
                          <span className="info-value">
                            {settings.logo ? (
                              <img 
                                src={`https://christophedepanne.infocolab.online/api/files/settings/${settings.id}/${settings.logo}`}
                                alt="Logo"
                                className="settings-logo-preview"
                              />
                            ) : (
                              'Non défini'
                            )}
                          </span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Signature :</span>
                          <span className="info-value">
                            {settings.signature ? (
                              <img 
                                src={`https://christophedepanne.infocolab.online/api/files/settings/${settings.id}/${settings.signature}`}
                                alt="Signature"
                                className="settings-signature-preview"
                              />
                            ) : (
                              'Non définie'
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Informations bancaires */}
                    <div className="settings-card">
                      <div className="settings-card-header">
                        <h4>Informations bancaires</h4>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11,8H13V16H11V14H9V12H11V8M9,10V12H7V10H9M15,12V14H13V12H15M15,8V10H13V8H15M17,6H7A2,2 0 0,0 5,8V16A2,2 0 0,0 7,18H17A2,2 0 0,0 19,16V8A2,2 0 0,0 17,6Z"/>
                        </svg>
                      </div>
                      <div className="settings-info">
                        <div className="info-item">
                          <span className="info-label">Banque :</span>
                          <span className="info-value">{settings.bank_name || 'Non définie'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">IBAN :</span>
                          <span className="info-value">{settings.bank_iban || 'Non défini'}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">BIC :</span>
                          <span className="info-value">{settings.bank_bic || 'Non défini'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                  </svg>
                  <h3>Paramètres non configurés</h3>
                  <p>Configurez les paramètres de votre entreprise pour générer des devis et factures.</p>
                  <button 
                    className="btn-primary"
                    onClick={() => setShowSettingsForm(true)}
                  >
                    Configurer maintenant
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Formulaire d'article */}
      {showArticleForm && (
        <ArticleForm
          article={editingArticle}
          onSubmit={async (articleData) => {
            setLoading(true);
            let result;
            if (editingArticle) {
              result = await updateArticle(editingArticle.id, articleData);
            } else {
              result = await createArticle(articleData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowArticleForm(false);
            setEditingArticle(null);
          }}
          loading={loading}
        />
      )}

      {/* Choisir et confirmer un créneau proposé */}
      {showChooseSlotModal && (
        <div className="appointment-form-overlay">
          <div className="appointment-form-container">
            <div className="appointment-form-header">
              <h2>Choisir un créneau</h2>
              <button type="button" onClick={() => setShowChooseSlotModal(false)} className="close-btn" title="Fermer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="appointment-form" style={{ paddingBottom: 0 }}>
              <div className="form-section" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                <div style={{ marginBottom: 12 }}>
                  <strong>RDV:</strong> {chooseSlotAppt?.title || 'Rendez-vous'} — {chooseSlotAppt?.expand?.customer_id?.name || ''}
                </div>
                {chooseSlotList.length === 0 ? (
                  <div style={{ color:'#6b7280' }}>Aucun créneau proposé.</div>
                ) : (
                  <div style={{ display:'grid', gap:8 }}>
                    {chooseSlotList.map((s) => (
                      <div key={s.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1px solid #e5e7eb', borderRadius:8, padding:10 }}>
                        <div>
                          <strong>{new Date(s.start).toLocaleDateString('fr-FR')}</strong>
                          <span style={{ marginLeft:8 }}>
                            {new Date(s.start).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})} → {new Date(s.end).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}
                          </span>
                          <span style={{ marginLeft:8, color:'#6b7280' }}>({s.duration_minutes} min)</span>
                        </div>
                        <button
                          className="btn-primary"
                          onClick={async () => {
                            try {
                              setLoading(true);
                              // 1) Mettre à jour le RDV avec le créneau choisi
                              await pb.collection('appointments').update(chooseSlotAppt.id, {
                                start_date: s.start,
                                end_date: s.end,
                                status: 'scheduled',
                                rdv_status: 'confirmed',
                                confirmed_at: new Date().toISOString(),
                                chosen_slot_id: s.id,
                              });
                              // 2) Marquer le créneau choisi comme 'selected'
                              await pb.collection('appointment_slots').update(s.id, { status: 'selected', is_available: false });
                              // 3) Marquer les autres créneaux de ce RDV comme 'expired'
                              const others = chooseSlotList.filter(x => x.id !== s.id);
                              for (const o of others) {
                                try { await pb.collection('appointment_slots').update(o.id, { status: 'expired', is_available: false }); } catch {}
                              }
                              // 4) Log event 'confirmed'
                              try {
                                await pb.collection('appointment_events').create({
                                  appointment_id: chooseSlotAppt.id,
                                  type: 'confirmed',
                                  payload: { slot_id: s.id },
                                });
                              } catch (e) { console.warn('Log event failed:', e); }

                              await loadAppointments();
                              setShowChooseSlotModal(false);
                              setChooseSlotAppt(null);
                              setChooseSlotList([]);
                            } catch (e) {
                              console.error('Erreur confirmation du créneau:', e);
                              alert('Erreur lors de la confirmation du créneau');
                            } finally {
                              setLoading(false);
                            }
                          }}
                        >
                          Confirmer ce créneau
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de proposition de RDV (création OU re‑proposition) */}
      {showAppointmentProposalForm && (
        <AppointmentProposalForm
          customers={customers}
          vehicles={vehicles}
          quotes={quotes}
          loading={loading}
          onSubmit={async ({ appointment, slots }) => {
            try {
              setLoading(true);
              let apptId = null;
              if (reproposeAppointment) {
                apptId = reproposeAppointment.id;
                await pb.collection('appointments').update(apptId, {
                  rdv_status: 'proposed',
                  proposed_at: new Date().toISOString(),
                });
              } else {
                // Créer un RDV "brouillon" orienté proposition (sans start/end)
                const createRes = await pb.collection('appointments').create({
                  title: appointment.title,
                  customer_id: appointment.customer_id,
                  vehicle_id: appointment.vehicle_id || null,
                  quote_id: appointment.quote_id || null,
                  notes: appointment.notes || null,
                  rdv_status: 'proposed',
                  proposed_at: new Date().toISOString(),
                  visible_client: true,
                });
                apptId = createRes?.id;
              }
              // Créer les créneaux proposés
              if (apptId && Array.isArray(slots)) {
                for (const s of slots) {
                  try {
                    await pb.collection('appointment_slots').create({
                      appointment_id: apptId,
                      start: s.start,
                      end: s.end,
                      duration_minutes: s.duration_minutes,
                      status: 'proposed',
                      is_available: true,
                    });
                  } catch (e) {
                    console.error('Erreur création créneau:', e);
                  }
                }
                // Event
                try {
                  await pb.collection('appointment_events').create({
                    appointment_id: apptId,
                    type: 'proposed',
                    payload: { slotsCount: slots.length, mode: reproposeAppointment ? 'repropose' : 'create' },
                  });
                } catch (e) {
                  console.warn('Impossible de créer l\'événement proposed:', e);
                }
              }
              await loadAppointments();
              setShowAppointmentProposalForm(false);
              setReproposeAppointment(null);
            } catch (err) {
              console.error('Erreur proposition RDV:', err);
              alert('Erreur lors de la proposition du rendez-vous');
            } finally {
              setLoading(false);
            }
          }}
          onCancel={() => { setShowAppointmentProposalForm(false); setReproposeAppointment(null); }}
        />
      )}

      {/* Formulaire client */}
      {showCustomerForm && (
        <CustomerForm
          customer={editingCustomer}
          onSubmit={async (customerData) => {
            setLoading(true);
            let result;
            if (editingCustomer) {
              result = await updateCustomer(editingCustomer.id, customerData);
            } else {
              result = await createCustomer(customerData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowCustomerForm(false);
            setEditingCustomer(null);
          }}
          loading={loading}
        />
      )}

      {/* Formulaire véhicule */}
      {showVehicleForm && (
        <VehicleForm
          vehicle={editingVehicle}
          customer={selectedCustomerForVehicle}
          onSubmit={async (vehicleData) => {
            setLoading(true);
            let result;
            if (editingVehicle) {
              result = await updateVehicle(editingVehicle.id, vehicleData);
            } else {
              result = await createVehicle(vehicleData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowVehicleForm(false);
            setEditingVehicle(null);
            setSelectedCustomerForVehicle(null);
          }}
          loading={loading}
        />
      )}

      {/* Formulaire service */}
      {showServiceForm && (
        <ServiceForm
          service={editingService}
          onSubmit={async (serviceData) => {
            setLoading(true);
            let result;
            if (editingService) {
              result = await updateService(editingService.id, serviceData);
            } else {
              result = await createService(serviceData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowServiceForm(false);
            setEditingService(null);
          }}
          loading={loading}
        />
      )}

      {/* Formulaire paramètres */}
      {showSettingsForm && (
        <SettingsForm
          settings={settings}
          onSubmit={async (settingsData, logoFile, signatureFile) => {
            setLoading(true);
            const result = await updateSettings(settingsData, logoFile, signatureFile);
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowSettingsForm(false);
          }}
          loading={loading}
        />
      )}

      {/* Formulaire devis */}
      {showQuoteForm && (
        <QuoteForm
          quote={editingQuote}
          customers={customers}
          vehicles={vehicles}
          services={services}
          parts={parts}
          settings={settings}
          defaultCustomerId={prefillQuoteCustomerId}
          onSubmit={async (quoteData) => {
            setLoading(true);
            let result;
            if (editingQuote) {
              result = await updateQuote(editingQuote.id, quoteData);
            } else {
              result = await createQuote(quoteData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowQuoteForm(false);
            setEditingQuote(null);
            setPrefillQuoteCustomerId('');
          }}
          loading={loading}
          onCreateCustomer={createCustomer}
          onCreateVehicle={createVehicle}
          onCreateService={createService}
          onCreatePart={createPart}
        />
      )}

      {/* Formulaire facture */}
      {showInvoiceForm && (
        <InvoiceForm
          invoice={editingInvoice}
          customers={customers}
          vehicles={vehicles}
          services={services}
          parts={parts}
          settings={settings}
          quotes={quotes}
          defaultCustomerId={prefillInvoiceCustomerId}
          defaultQuoteId={prefillInvoiceQuoteId}
          onSubmit={async (invoiceData) => {
            setLoading(true);
            let result;
            if (editingInvoice) {
              result = await updateInvoice(editingInvoice.id, invoiceData);
            } else {
              result = await createInvoice(invoiceData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowInvoiceForm(false);
            setEditingInvoice(null);
            setPrefillInvoiceCustomerId('');
            setPrefillInvoiceQuoteId('');
          }}
          onCreateCustomer={async (customerData) => {
            console.log('🚀 Création client rapide (facture):', customerData);
            
            const result = await createCustomer(customerData);
            if (result.success) {
              console.log('✅ Client créé avec succès');
              
              await loadCustomers();
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const updatedCustomers = await pb.collection('customers').getFullList({
                sort: '-created',
                filter: `name = "${customerData.name}" AND email = "${customerData.email}"`
              });
              
              const newCustomer = updatedCustomers.find(c => 
                c.name === customerData.name && 
                (!customerData.email || c.email === customerData.email)
              );
              
              console.log('🔍 Nouveau client trouvé:', newCustomer);
              
              if (newCustomer) {
                return newCustomer;
              } else {
                return { 
                  id: `temp_${Date.now()}`, 
                  name: customerData.name,
                  email: customerData.email,
                  phone: customerData.phone,
                  company: customerData.company
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du client');
            }
          }}
          onCreateVehicle={async (vehicleData) => {
            console.log('🚀 Création véhicule rapide (facture):', vehicleData);
            
            const result = await createVehicle(vehicleData);
            if (result.success) {
              console.log('✅ Véhicule créé avec succès');
              
              await loadVehicles();
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const updatedVehicles = await pb.collection('vehicles').getFullList({
                sort: '-created',
                filter: `customer_id = "${vehicleData.customer_id}"`
              });
              
              const newVehicle = updatedVehicles.find(v => 
                v.brand === vehicleData.brand && 
                v.model === vehicleData.model &&
                v.customer_id === vehicleData.customer_id
              );
              
              console.log('🔍 Nouveau véhicule trouvé:', newVehicle);
              
              if (newVehicle) {
                return newVehicle;
              } else {
                return { 
                  id: `temp_${Date.now()}`, 
                  brand: vehicleData.brand,
                  model: vehicleData.model,
                  year: vehicleData.year,
                  customer_id: vehicleData.customer_id
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du véhicule');
            }
          }}
          onCreateService={async (serviceData) => {
            console.log('🚀 Création service rapide (facture):', serviceData);
            
            const result = await createService(serviceData);
            if (result.success) {
              console.log('✅ Service créé avec succès');
              
              await loadServices();
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const updatedServices = await pb.collection('services_catalog').getFullList({
                sort: '-created'
              });
              
              const newService = updatedServices.find(s => 
                s.name === serviceData.name && 
                s.category === serviceData.category
              );
              
              console.log('🔍 Nouveau service trouvé:', newService);
              
              if (newService) {
                return newService;
              } else {
                return { 
                  id: `temp_${Date.now()}`, 
                  name: serviceData.name,
                  category: serviceData.category,
                  base_price: serviceData.base_price,
                  description: serviceData.description
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du service');
            }
          }}
          onCreatePart={async (partData) => {
            console.log('🚀 Création pièce rapide (facture):', partData);
            
            const result = await createPart(partData);
            if (result.success) {
              console.log('✅ Pièce créée avec succès');
              
              // Recharger la liste des pièces
              await loadParts();
              
              return result.part;
            } else {
              throw new Error(result.error || 'Erreur lors de la création de la pièce');
            }
          }}
          onCancel={() => {
            setShowInvoiceForm(false);
            setEditingInvoice(null);
            setPrefillInvoiceCustomerId('');
          }}
          loading={loading}
        />
      )}

      {/* Formulaire rendez-vous */}
      {showAppointmentForm && (
        <AppointmentForm
          appointment={editingAppointment}
          customers={customers}
          vehicles={vehicles}
          services={services}
          quotes={quotes.filter(q => q.status === 'accepted')}
          labels={labels}
          onCreateLabel={async (labelData) => {
            // Création d'un libellé depuis le formulaire RDV
            const created = await pb.collection('appointment_labels').create({
              name: labelData.name,
              color: labelData.color,
              active: labelData.active !== false
            });
            try {
              const recs = await pb.collection('appointment_labels').getFullList({ sort: 'name' });
              setLabels(recs || []);
            } catch (e) {
              console.warn('Impossible de recharger les labels après création:', e);
            }
            return created;
          }}
          onSubmit={async (appointmentData) => {
            setLoading(true);
            let result;
            if (editingAppointment) {
              result = await updateAppointment(editingAppointment.id, appointmentData);
            } else {
              result = await createAppointment(appointmentData);
            }
            setLoading(false);
            
            if (!result.success) {
              alert(result.error || 'Erreur lors de l\'enregistrement');
            }
          }}
          onCancel={() => {
            setShowAppointmentForm(false);
            setEditingAppointment(null);
          }}
          loading={loading}
          onCreateCustomer={async (customerData) => {
            console.log('🚀 Création client rapide (RDV):', customerData);
            
            const result = await createCustomer(customerData);
            if (result.success) {
              console.log('✅ Client créé avec succès');
              
              // Recharger la liste des clients
              await loadCustomers();
              
              // Attendre un peu que l'état soit mis à jour
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Récupérer la liste mise à jour depuis PocketBase
              const updatedCustomers = await pb.collection('customers').getFullList({
                sort: '-created'
              });
              
              // Trouver le nouveau client (le plus récent avec ce nom)
              const newCustomer = updatedCustomers.find(c => 
                c.name === customerData.name
              );
              
              if (newCustomer) {
                return newCustomer;
              } else {
                return { 
                  id: `temp_${Date.now()}`, 
                  name: customerData.name,
                  email: customerData.email,
                  phone: customerData.phone
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du client');
            }
          }}
          onCreateVehicle={async (vehicleData) => {
            console.log('🚀 Création véhicule rapide (RDV):', vehicleData);
            
            const result = await createVehicle(vehicleData);
            if (result.success) {
              console.log('✅ Véhicule créé avec succès');
              
              // Recharger la liste des véhicules
              await loadVehicles();
              
              // Attendre un peu que l'état soit mis à jour
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Récupérer la liste mise à jour depuis PocketBase
              const updatedVehicles = await pb.collection('vehicles').getFullList({
                sort: '-created'
              });
              
              // Trouver le nouveau véhicule
              const newVehicle = updatedVehicles.find(v => 
                v.brand === vehicleData.brand && 
                v.model === vehicleData.model &&
                v.customer_id === vehicleData.customer_id
              );
              
              if (newVehicle) {
                return newVehicle;
              } else {
                return { 
                  id: `temp_${Date.now()}`, 
                  brand: vehicleData.brand,
                  model: vehicleData.model,
                  year: vehicleData.year,
                  customer_id: vehicleData.customer_id
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du véhicule');
            }
          }}
          onCreateService={async (serviceData) => {
            console.log('🚀 Création service rapide (RDV):', serviceData);
            
            const result = await createService(serviceData);
            if (result.success) {
              console.log('✅ Service créé avec succès');
              
              // Recharger la liste des services
              await loadServices();
              
              // Attendre un peu que l'état soit mis à jour
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Récupérer la liste mise à jour depuis PocketBase
              const updatedServices = await pb.collection('services_catalog').getFullList({
                sort: '-created'
              });
              
              // Trouver le nouveau service (le plus récent avec ce nom)
              const newService = updatedServices.find(s => 
                s.name === serviceData.name && 
                s.category === serviceData.category
              );
              
              console.log('🔍 Nouveau service trouvé:', newService);
              
              if (newService) {
                return newService;
              } else {
                // Fallback - créer un objet temporaire
                return { 
                  id: `temp_${Date.now()}`, 
                  name: serviceData.name,
                  category: serviceData.category,
                  base_price: serviceData.base_price,
                  description: serviceData.description
                };
              }
            } else {
              throw new Error(result.error || 'Erreur lors de la création du service');
            }
          }}
        />
      )}

      {/* Formulaire pièces */}
      {showPartForm && (
        <PartForm
          part={editingPart}
          onSave={savePart}
          onCancel={() => {
            setShowPartForm(false);
            setEditingPart(null);
          }}
        />
      )}

      <style>{`
        .admin-page {
          min-height: 100vh;
          background: #f8fafc;
          display: flex;
          flex-direction: column;
        }

        /* Navbar Principale */}
        .admin-navbar {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          position: sticky;
          top: 0;
          z-index: 1001;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .admin-nav-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
          height: 80px; /* Hauteur fixe pour la navbar principale */}
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          position: relative;
        }

        .sub-nav-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 24px;
          height: 144px; /* Hauteur augmentée pour positionner le burger encore plus bas */}
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Sous-Navbar Fixe - Collée à la navbar principale */}
        .sub-navbar {
          background: white;
          border-bottom: 2px solid #e5e7eb;
          position: sticky;
          top: 80px; /* Exactement la hauteur de la navbar principale */}
          z-index: 1000;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 0; /* Pas d'espace entre les navbars */}
        }

        /* Désactiver le scroll horizontal globalement */
        html, body { overflow-x: hidden; width: 100%; }
        .admin-page { overflow-x: hidden; width: 100%; }
        .admin-navbar, .sub-navbar, .admin-content { max-width: 100vw; }

        /* Overflow -> burger+drawer uniquement quand nécessaire */
        .sub-nav-container {
          position: relative;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none; /* Firefox */
        }
        /* Masquer visuellement la barre de scroll horizontale des onglets */
        .sub-nav-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari, etc. */
        }
        .sub-nav-mobile-trigger {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .sub-nav-logo-btn {
          border: none;
          padding: 0;
          background: none;
          cursor: pointer;
          border-radius: 9999px;
        }

        .sub-nav-logo-img {
          width: 38px;
          height: 38px;
          border-radius: 9999px;
          object-fit: cover;
          border: 2px solid #3b82f6;
          box-shadow: 0 2px 6px rgba(59,130,246,0.4);
          background: #111827;
        }

        .sub-nav-menu-btn {
          padding: 8px 14px;
          border-radius: 9999px;
          border: none;
          background: #3b82f6;
          color: #ffffff;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(59,130,246,0.4);
          white-space: nowrap;
        }

        .sub-nav-menu-btn:hover {
          background: #2563eb;
        }

        /* On laisse les onglets visibles pour pouvoir scroller horizontalement même en mode condensed */
        .sub-navbar.condensed .admin-tabs {
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          justify-content: flex-start; /* aligner les onglets au début pour voir les premiers en premier */
          margin-left: 24px; /* coller à gauche quand condensed */
          margin-right: 0;
        }
        .sub-navbar.condensed .sub-nav-container {
          padding-left: 24px; /* même padding que le mode normal, plus de décalage qui cache les premiers onglets */
        }

        .drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index:1100; }
        .sub-mobile-drawer {
          position: fixed; top:0; right:-100%; width:80%; max-width:360px; height:100vh;
          background:#fff; color:#111827; z-index:1101; transition:right .25s ease; display:flex; flex-direction:column;
        }
        .sub-mobile-drawer.open { right: 0; }
        .drawer-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid rgba(0,0,0,0.08); }
        .drawer-title { font-weight:700; }
        .drawer-close { border:none; background:none; font-size:20px; cursor:pointer; }
        .drawer-actions { display:flex; gap:10px; padding:16px 20px; }
        .drawer-actions.vertical { flex-direction: column; }
        .drawer-btn { padding:10px 14px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; text-align:left; cursor:pointer; }
        .drawer-btn.active { border-color:#3b82f6; box-shadow:0 0 0 2px rgba(59,130,246,0.2); }

        .admin-brand {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .home-link-nav {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          background: #eff6ff;
          color: #3b82f6;
          border-radius: 10px;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .home-link-nav:hover {
          background: #3b82f6;
          color: #fff;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
        }

        .brand-link {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          color: white;
          transition: all 0.3s ease;
        }

        .brand-logo-img {
          width: 40px;
          height: 40px;
          border-radius: 9999px;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.6);
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          background: #111827;
        }

        .brand-link:hover {
          transform: translateY(-1px);
        }

        .brand-icon {
          color: #3b82f6;
        }

        .brand-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
        }

        .brand-subtitle {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .admin-nav-center {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 300px;
          max-width: 500px;
        }

        .main-nav-buttons {
          display: flex;
          background: rgba(15, 23, 42, 0.4);
          padding: 6px;
          border-radius: 9999px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          gap: 6px;
          backdrop-filter: blur(12px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .main-nav-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 24px;
          border: 1px solid transparent;
          background: transparent;
          color: rgba(248, 250, 252, 0.7);
          cursor: pointer;
          border-radius: 9999px;
          font-size: 0.95rem;
          font-weight: 700;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          position: relative;
          z-index: 1;
          overflow: hidden;
        }

        .main-nav-btn:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.05);
        }

        .main-nav-btn.active {
          color: #ffffff;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: rgba(255, 255, 255, 0.2);
          box-shadow: 0 4px 15px rgba(37, 99, 235, 0.4);
        }

        .main-nav-btn svg {
          width: 18px;
          height: 18px;
          transition: transform 0.3s ease;
        }

        .main-nav-btn:hover svg {
          transform: scale(1.1);
        }

        .main-nav-btn.active svg {
          filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
        }

        .nav-label {
          letter-spacing: 0.01em;
        }

        /* Menu utilisateur déroulant */
        .user-dropdown-container {
          position: relative;
        }

        .user-avatar-btn {
          border: none;
          background: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .user-avatar-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: scale(1.05);
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.2);
        }

        .user-dropdown-menu {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.1);
          min-width: 220px;
          z-index: 1002;
          overflow: hidden;
        }

        .user-dropdown-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .user-dropdown-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .user-dropdown-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 0.875rem;
        }

        .user-dropdown-role {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .user-dropdown-divider {
          height: 1px;
          background: #e5e7eb;
        }

        .user-dropdown-actions {
          padding: 8px 0;
        }

        .user-dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: #374151;
          text-decoration: none;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }

        .user-dropdown-item:hover {
          background: #f3f4f6;
          color: #1f2937;
        }

        .user-dropdown-item.logout-item:hover {
          background: #fef2f2;
          color: #dc2626;
        }

        .user-dropdown-item svg {
          width: 16px;
          height: 16px;
          opacity: 0.7;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.1);
          padding: 8px 16px;
          border-radius: 20px;
          backdrop-filter: blur(10px);
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #94a3b8;
          text-decoration: none;
          font-size: 0.9rem;
          transition: color 0.3s ease;
        }

        .breadcrumb-item:hover {
          color: white;
        }

        .breadcrumb-separator {
          color: #64748b;
        }

        .breadcrumb-current {
          color: white;
          font-weight: 500;
          font-size: 0.9rem;
        }


        .admin-main-content {
          flex: 1;
          padding-top: 0; /* Pas d'espace pour que tout reste collé */}
        }

        .admin-container {
          position: relative;
          z-index: 1;
        }

        .admin-tabs {
          display: inline-flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 0;
          justify-content: center;
          flex-wrap: nowrap; /* Empêche de revenir à la ligne */
          white-space: nowrap; /* Stabilise le centrage */
          width: max-content; /* largeur basée sur le contenu */
          margin-left: auto; /* centre dans le conteneur */
          margin-right: auto;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          background: none;
          color: #6b7280;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          transition: all 0.3s ease;
          white-space: nowrap; /* Empêche le texte de casser */
          flex: 0 0 auto; /* Pas de rétrécissement ni d'étirement */
        }

        .tab-btn:hover {
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .tab-btn.active {
          color: #3b82f6;
          border-bottom-color: #3b82f6;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          gap: 16px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .stat-icon.total { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); }
        .stat-icon.admin { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
        .stat-icon.users { background: linear-gradient(135deg, #059669 0%, #047857 100%); }
        .stat-icon.recent { background: linear-gradient(135deg, #d97706 0%, #b45309 100%); }
        .stat-icon.articles { background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%); }
        .stat-icon.published { background: linear-gradient(135deg, #059669 0%, #047857 100%); }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
        }

        .stat-label {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .recent-activity {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .recent-activity h3 {
          margin: 0 0 20px 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          transition: background-color 0.2s ease;
        }

        .activity-item:hover {
          background: #f9fafb;
        }

        .user-avatar-small {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.8rem;
        }

        .activity-text {
          color: #374151;
          font-size: 0.9rem;
        }

        .activity-date {
          color: #6b7280;
          font-size: 0.8rem;
        }

        .users-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .users-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .users-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .table-header {
          display: grid;
          grid-template-columns: 2fr 2fr 1fr 1.5fr 1fr;
          gap: 16px;
          padding: 16px 24px;
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .table-row {
          display: grid;
          grid-template-columns: 2fr 2fr 1fr 1.5fr 1fr;
          gap: 16px;
          padding: 16px 24px;
          border-bottom: 1px solid #f3f4f6;
          align-items: center;
          transition: background-color 0.2s ease;
        }

        .table-row:hover {
          background: #f9fafb;
        }

        .user-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-name {
          font-weight: 500;
          color: #1f2937;
        }

        .user-id {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .email-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .verified-badge {
          color: #059669;
          font-size: 0.8rem;
        }

        .role-select {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          font-size: 0.9rem;
          cursor: pointer;
        }

        .role-select:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .date-cell {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .actions-cell {
          display: flex;
          gap: 8px;
        }

        .delete-btn {
          padding: 8px;
          border: none;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .delete-btn:hover:not(:disabled) {
          background: #fecaca;
        }

        .delete-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .loading-spinner {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }

        /* Drawer Historique devis (admin) */
        .quote-history-drawer {
          position: relative;
          width: 100%;
          max-width: 640px;
          max-height: 80vh;
          background: #ffffff;
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.35);
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          z-index: 1201;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .quote-history-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .quote-history-header h4 {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 600;
          color: #111827;
        }

        .quote-history-sub {
          margin: 4px 0 0;
          font-size: 0.85rem;
          color: #6b7280;
        }

        .drawer-close-btn {
          border: none;
          background: none;
          cursor: pointer;
          font-size: 22px;
          line-height: 1;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          color: #6b7280;
          transition: background-color 0.15s ease, color 0.15s ease;
        }

        .drawer-close-btn:hover {
          background: #e5e7eb;
          color: #111827;
        }

        .quote-history-body {
          flex: 1;
          padding: 16px 20px 20px;
          overflow-y: auto;
        }

        .quote-history-section {
          background: #ffffff;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          padding: 14px 14px 10px;
          margin-bottom: 16px;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
        }

        .quote-history-section h5 {
          margin: 0 0 10px;
          font-size: 0.95rem;
          font-weight: 600;
          color: #111827;
        }

        .quote-history-section .empty {
          font-size: 0.85rem;
          color: #9ca3af;
          padding: 4px 0 6px;
        }

        .history-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .history-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 8px;
          transition: background-color 0.15s ease;
        }

        .history-row:hover {
          background: #f9fafb;
        }

        .history-main {
          flex: 1;
          min-width: 0;
        }

        .history-date {
          font-size: 0.78rem;
          color: #9ca3af;
          margin-bottom: 2px;
        }

        .history-text {
          font-size: 0.88rem;
          color: #111827;
        }

        .history-meta {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: flex-end;
        }

        .history-meta .tag {
          font-size: 0.7rem;
        }

        .tag.secondary {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #bfdbfe;
        }

        .archives-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .archive-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 8px;
          transition: background-color 0.15s ease;
        }

        .archive-row:hover {
          background: #f9fafb;
        }

        .archive-main {
          flex: 1;
          min-width: 0;
        }

        .archive-title {
          font-size: 0.88rem;
          font-weight: 500;
          color: #111827;
        }

        .archive-meta {
          font-size: 0.78rem;
          color: #9ca3af;
        }

        .archive-link {
          font-size: 0.82rem;
          font-weight: 500;
          color: #2563eb;
          text-decoration: none;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(37, 99, 235, 0.25);
          background: #eff6ff;
          white-space: nowrap;
          transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
        }

        .archive-link:hover {
          background: #dbeafe;
          color: #1d4ed8;
          box-shadow: 0 1px 4px rgba(37, 99, 235, 0.3);
        }

        @media (max-width: 768px) {
          .quote-history-drawer {
            max-width: 100%;
            max-height: 85vh;
            border-radius: 0;
          }
        }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .table-header,
          .table-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .table-header {
            display: none;
          }

          .table-row {
            display: block;
            padding: 16px;
          }

          .table-row > div {
            margin-bottom: 8px;
          }

          .user-cell {
            margin-bottom: 12px;
          }
        }

        /* Blog Premium Styles */
        .blog-premium-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .header-titles h3 {
          margin: 0 0 4px 0 !important;
          font-size: 1.8rem !important;
          font-weight: 800 !important;
          color: #1e293b !important;
          letter-spacing: -0.02em;
        }

        .header-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .header-subtitle {
          margin: 0;
          color: #64748b;
          font-size: 0.95rem;
        }

        .blog-mini-stats {
          display: flex;
          gap: 12px;
        }

        .mini-stat {
          background: #f8fafc;
          padding: 10px 16px;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 80px;
          border: 1px solid #e2e8f0;
        }

        .mini-stat .stat-num {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1e293b;
        }

        .mini-stat .stat-txt {
          font-size: 0.75rem;
          color: #64748b;
          text-transform: uppercase;
          font-weight: 600;
        }

        .mini-stat.published { background: #f0fdf4; border-color: #bbf7d0; }
        .mini-stat.published .stat-num { color: #15803d; }
        .mini-stat.published .stat-txt { color: #166534; }

        .mini-stat.draft { background: #fffbeb; border-color: #fef3c7; }
        .mini-stat.draft .stat-num { color: #b45309; }
        .mini-stat.draft .stat-txt { color: #92400e; }

        .blog-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          padding-top: 20px;
          border-top: 1px dashed #e2e8f0;
        }

        .toolbar-search {
          position: relative;
          flex: 1;
          min-width: 280px;
        }

        .toolbar-search svg {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .toolbar-search input {
          width: 100%;
          padding: 12px 14px 12px 42px !important;
          border-radius: 12px !important;
          border: 1px solid #e2e8f0 !important;
          background: #f8fafc !important;
          font-size: 0.95rem !important;
          transition: all 0.2s ease;
          margin-bottom: 0 !important;
        }

        .toolbar-search input:focus {
          outline: none;
          border-color: #3b82f6 !important;
          background: white !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .toolbar-filters {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .premium-select {
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: white;
          font-size: 0.9rem;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
        }

        .btn-icon-secondary {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon-secondary:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .btn-premium-action {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 20px;
          border-radius: 12px;
          border: none;
          background: #3b82f6;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
        }

        .btn-premium-action:hover {
          background: #2563eb;
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(59, 130, 246, 0.3);
        }

        /* Grid */
        .articles-premium-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 24px;
          animation: fadeInDashboard 0.4s ease-out;
        }

        .article-premium-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          border: 1px solid #f1f5f9;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          text-align: left;
        }

        .article-premium-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
        }

        .card-visual {
          position: relative;
          height: 200px;
          overflow: hidden;
          background: #f1f5f9;
        }

        .card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .article-premium-card:hover .card-img {
          transform: scale(1.05);
        }

        .card-img-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #cbd5e1;
        }

        .card-overlays {
          position: absolute;
          inset: 0;
          padding: 16px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          pointer-events: none;
          background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 40%, rgba(0,0,0,0.5) 100%);
        }

        .card-category {
          align-self: flex-start;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(4px);
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.7rem;
          font-weight: 800;
          color: #3b82f6;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .card-status-badge {
          align-self: flex-end;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 0.72rem;
          font-weight: 700;
          color: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .card-status-badge.published { background: #10b981; }
        .card-status-badge.draft { background: #f59e0b; }

        .card-main {
          padding: 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .card-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 14px;
          font-size: 0.8rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .card-date, .card-link {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .card-title {
          margin: 0 0 12px 0 !important;
          font-size: 1.3rem !important;
          font-weight: 700 !important;
          color: #1e293b !important;
          line-height: 1.4 !important;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-excerpt {
          margin: 0 0 24px 0 !important;
          font-size: 0.92rem !important;
          color: #64748b !important;
          line-height: 1.6 !important;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }

        @keyframes fadeInDashboard {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .card-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 18px;
          border-top: 1px solid #f1f5f9;
        }

        .action-btn.edit {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 18px;
          border-radius: 10px;
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #dbeafe;
          font-weight: 700;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.edit:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .secondary-actions {
          display: flex;
          gap: 10px;
        }

        .action-btn-circle {
          width: 38px;
          height: 38px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn-circle:hover {
          background: #f1f5f9;
          color: #1e293b;
          border-color: #cbd5e1;
          transform: translateY(-2px);
        }

        .action-btn-circle.delete:hover {
          background: #fef2f2;
          color: #dc2626;
          border-color: #fecaca;
        }

        @keyframes premiumSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Users Premium Styles */
        .users-premium-header {
          margin-bottom: 24px;
        }

        .users-mini-stats {
          display: flex;
          gap: 12px;
        }

        .mini-stat.admin { background: #eff6ff; border-color: #dbeafe; }
        .mini-stat.admin .stat-num { color: #2563eb; }
        .mini-stat.admin .stat-txt { color: #1e40af; }

        .users-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          padding-top: 20px;
          border-top: 1px dashed #e2e8f0;
          margin-top: 20px;
        }

        .premium-search-input {
          width: 100%;
          padding: 12px 14px 12px 42px !important;
          border-radius: 12px !important;
          border: 1px solid #e2e8f0 !important;
          background: #f8fafc !important;
          font-size: 0.95rem !important;
          transition: all 0.2s ease;
          margin-bottom: 0 !important;
        }

        .premium-search-input:focus {
          outline: none;
          border-color: #3b82f6 !important;
          background: white !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .users-premium-list-container {
          background: white;
          border-radius: 20px;
          border: 1px solid #f1f5f9;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          animation: fadeInDashboard 0.4s ease-out;
        }

        .users-table-header {
          display: grid;
          grid-template-columns: 2fr 2fr 1.5fr 1fr 100px;
          padding: 16px 24px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          color: #64748b;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .user-premium-row {
          display: grid;
          grid-template-columns: 2fr 2fr 1.5fr 1fr 100px;
          padding: 16px 24px;
          align-items: center;
          border-bottom: 1px solid #f8fafc;
          transition: background-color 0.2s ease;
        }

        .user-premium-row:hover {
          background: #f1f5f9;
        }

        .user-premium-row:last-child {
          border-bottom: none;
        }

        .user-profile-summary {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-premium {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.1rem;
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
        }

        .user-identity {
          display: flex;
          flex-direction: column;
          text-align: left;
        }

        .u-name {
          font-weight: 700;
          color: #1e293b;
          font-size: 0.95rem;
        }

        .u-id {
          font-size: 0.75rem;
          color: #94a3b8;
          font-family: monospace;
        }

        .user-contact-info {
          display: flex;
          flex-direction: column;
          text-align: left;
        }

        .u-email {
          color: #475569;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .verified-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.7rem;
          color: #10b981;
          font-weight: 700;
          margin-top: 2px;
        }

        .verified-status::before {
          content: '●';
        }

        .premium-role-select {
          padding: 8px 12px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: white;
          font-size: 0.85rem;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
          max-width: 180px;
        }

        .premium-role-select.admin {
          background: #eff6ff;
          color: #2563eb;
          border-color: #dbeafe;
        }

        .u-signup-date {
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 500;
        }

        .user-row-actions {
          display: flex;
          justify-content: flex-end;
        }

        @media (max-width: 1024px) {
          .users-table-header, .user-premium-row {
            grid-template-columns: 2fr 2fr 1fr 100px;
          }
          .col-date { display: none; }
        }

        @media (max-width: 768px) {
          .users-table-header { display: none; }
          .user-premium-row {
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 20px;
          }
          .user-row-actions { justify-content: flex-start; }
          .premium-role-select { max-width: none; }
        }

        /* Styles pour les clients */
        .customers-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .customers-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .customers-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .customers-actions {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          color: #6b7280;
          z-index: 1;
        }

        .search-box input {
          padding: 10px 16px 10px 40px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          width: 250px;
          transition: all 0.2s ease;
        }

        .search-box input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .customers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .customer-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          position: relative;
        }

        .customer-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .customer-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .customer-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.2rem;
        }

        .customer-info {
          flex: 1;
        }

        .customer-name {
          margin: 0 0 4px 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .customer-company {
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
        }

        .customer-details {
          margin-bottom: 16px;
        }

        .customer-detail {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #4b5563;
        }

        .customer-detail svg {
          color: #6b7280;
          flex-shrink: 0;
        }

        .customer-detail span {
          word-break: break-all;
        }

        .customer-actions {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .customer-meta {
          padding-top: 12px;
          border-top: 1px solid #f3f4f6;
        }

        .customer-date {
          font-size: 0.8rem;
          color: #9ca3af;
        }

        /* Styles pour les véhicules dans les cartes clients */
        .customer-vehicles {
          margin: 16px 0;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #f3f4f6;
        }

        .vehicles-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .vehicles-header h5 {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #374151;
        }

        .add-vehicle-btn {
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .add-vehicle-btn:hover {
          background: #2563eb;
        }

        .vehicles-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .vehicle-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          transition: all 0.2s ease;
        }

        .vehicle-item:hover {
          border-color: #d1d5db;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .vehicle-info {
          flex: 1;
        }

        .vehicle-name {
          font-size: 0.85rem;
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 2px;
        }

        .vehicle-plate {
          font-size: 0.75rem;
          color: #6b7280;
          font-family: monospace;
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          display: inline-block;
        }

        .vehicle-actions {
          display: flex;
          gap: 4px;
        }

        .vehicle-action-btn {
          background: none;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .vehicle-action-btn.edit {
          color: #0277bd;
        }

        .vehicle-action-btn.edit:hover {
          background: #e0f2fe;
        }

        .vehicle-action-btn.delete {
          color: #dc2626;
        }

        .vehicle-action-btn.delete:hover {
          background: #fee2e2;
        }

        .no-vehicles {
          text-align: center;
          padding: 20px;
          color: #6b7280;
        }

        .no-vehicles span {
          display: block;
          font-size: 0.85rem;
          margin-bottom: 8px;
        }

        .add-first-vehicle {
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 8px 16px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .add-first-vehicle:hover {
          background: #2563eb;
        }

        /* Styles pour les services */
        .services-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: hidden; /* empêcher tout scroll horizontal parasite */
        }

        .services-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .services-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .services-actions {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .services-filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .category-filter {
          padding: 10px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
          min-width: 180px;
        }

        .category-filter:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
          width: 100%;
        }

        @media (max-width: 640px) {
          .services-grid { grid-template-columns: 1fr; }
          .service-card { width: 100%; }
        }

        /* Forcer une seule colonne aussi en petits tablettes */
        @media (max-width: 768px) {
          .services-grid { grid-template-columns: 1fr; }
          .service-card { width: 100%; }
        }

        .service-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          position: relative;
          box-sizing: border-box;
          max-width: 100%;
        }

        .service-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .service-card.inactive {
          opacity: 0.6;
          background: #f9fafb;
        }

        .service-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .service-info {
          flex: 1;
          min-width: 0; /* prevent flex child overflow */
        }

        .service-name {
          margin: 0 0 4px 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .service-category {
          font-size: 0.85rem;
          color: #6b7280;
          background: #f3f4f6;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .service-status {
          margin-left: 12px;
        }

        .status-badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .status-badge.active {
          background: #dcfce7;
          color: #166534;
        }

        .status-badge.inactive {
          background: #fee2e2;
          color: #dc2626;
        }

        .service-details {
          margin-bottom: 16px;
        }

        .service-price {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .price-label {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .price-value {
          font-size: 1.2rem;
          font-weight: 600;
          color: #059669;
        }

        .service-duration {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }

        .duration-label {
          color: #6b7280;
        }

        .duration-value {
          font-weight: 500;
          color: #374151;
        }

        .service-features {
          margin-bottom: 12px;
        }

        .feature-badge {
          font-size: 0.8rem;
          background: #dbeafe;
          color: #1d4ed8;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
        }

        .service-description {
          margin-top: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          font-size: 0.9rem;
          color: #4b5563;
          line-height: 1.5;
        }

        .service-description p {
          margin: 0;
        }

        .service-actions {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .service-meta {
          padding-top: 12px;
          border-top: 1px solid #f3f4f6;
        }

        .service-date {
          font-size: 0.8rem;
          color: #9ca3af;
        }

        /* Styles pour les paramètres */
        .settings-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: hidden; /* empêcher tout scroll horizontal parasite */
        }

        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .settings-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .settings-overview {
          width: 100%;
        }

        .settings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 20px;
          width: 100%;
        }

        @media (max-width: 640px) {
          .settings-grid { grid-template-columns: 1fr; }
          .settings-card { width: 100%; }
        }

        /* Forcer une seule colonne aussi en petits tablettes */
        @media (max-width: 768px) {
          .settings-grid { grid-template-columns: 1fr; }
          .settings-card { width: 100%; }
        }

        .settings-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          box-sizing: border-box;
          max-width: 100%;
        }

        .settings-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .settings-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f3f4f6;
          flex-wrap: wrap;
          gap: 12px;
        }

        .settings-card-header h4 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .settings-card-header svg {
          color: #6b7280;
        }

        .settings-info {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          flex-wrap: wrap;
        }

        .info-label {
          font-weight: 500;
          color: #6b7280;
          min-width: 100px;
          font-size: 0.9rem;
        }

        .info-value {
          color: #1f2937;
          font-size: 0.9rem;
          text-align: right;
          flex: 1;
          word-break: break-word;
          min-width: 0; /* prevent long values from overflowing */
        }

        .settings-logo-preview,
        .settings-signature-preview {
          max-width: 100px;
          max-height: 50px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .settings-logo-preview {
          max-height: 40px;
        }

        .settings-signature-preview {
          max-height: 30px;
        }

        /* Styles pour les devis */
        .quotes-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: hidden; /* empêcher tout scroll horizontal parasite */
        }

        .quotes-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .quotes-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .quotes-actions {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .quotes-filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .status-filter {
          padding: 10px 16px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
          min-width: 150px;
        }

        .status-filter:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .quotes-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 20px;
          width: 100%;
        }

        /* Empêcher le débordement sur mobiles comme l'onglet Stock */
        @media (max-width: 640px) {
          .quotes-grid { grid-template-columns: 1fr; }
          .quote-card { width: 100%; }
        }

        /* Forcer une seule colonne aussi en petits tablettes */
        @media (max-width: 768px) {
          .quotes-grid { grid-template-columns: 1fr; }
          .quote-card { width: 100%; }
        }

        .quote-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          position: relative;
          box-sizing: border-box;
          max-width: 100%;
        }

        .quote-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .quote-card.expired {
          border-color: #f59e0b;
          background: #fffbeb;
        }

        .quote-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .quote-info {
          flex: 1;
          min-width: 0; /* évite que le contenu pousse hors de l'écran sur mobile */
        }

        .quote-number {
          margin: 0 0 4px 0;
          font-size: 1.2rem;
          font-weight: 600;
          color: #1f2937;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .quote-customer {
          font-size: 0.9rem;
          color: #6b7280;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .quote-customer .company {
          font-style: italic;
        }

        .quote-status {
          margin-left: 12px;
        }

        .status-badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: white;
        }

        .quote-details {
          margin-bottom: 16px;
        }

        /* Empêcher le débordement des lignes internes (comme Stock/Factures) */
        .quote-vehicle span { overflow-wrap: anywhere; word-break: break-word; }

        .quote-amount {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          row-gap: 6px;
        }

        .quote-dates { display: flex; flex-direction: column; gap: 4px; }
        .quote-date {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          color: #4b5563;
          flex-wrap: wrap;
          column-gap: 8px;
          row-gap: 2px;
        }
        .quote-date span { min-width: 0; overflow-wrap: anywhere; word-break: break-word; }

        .quote-actions { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
        .pdf-actions { display: flex; gap: 4px; flex-wrap: wrap; }

        .quote-vehicle {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          font-size: 0.9rem;
          color: #4b5563;
        }

        .quote-vehicle svg {
          color: #6b7280;
        }

        .quote-amount {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 12px;
          background: #f0fdf4;
          border-radius: 8px;
          border: 1px solid #bbf7d0;
        }

        .amount-label {
          font-size: 0.9rem;
          color: #166534;
          font-weight: 500;
        }

        .amount-value {
          font-size: 1.2rem;
          font-weight: 600;
          color: #166534;
        }

        .quote-dates {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .quote-date {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
        }

        .date-label {
          color: #6b7280;
        }

        .expired-date {
          color: #dc2626;
          font-weight: 500;
        }

        .quote-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .view-btn {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .view-btn:hover {
          background: #bfdbfe;
        }

        /* Styles pour les articles (alignés sur les devis) */
        .articles-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .articles-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .articles-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .articles-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 20px;
        }

        .article-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 0; /* image top, contenu séparé */
          transition: all 0.2s ease;
          overflow: hidden;
          position: relative;
        }

        .article-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .article-image { position: relative; aspect-ratio: 16 / 9; background:#f3f4f6; }
        .article-image img { width: 100%; height: 100%; object-fit: cover; display:block; }
        .placeholder-image { width:100%; height:100%; display:flex; align-items:center; justify-content:center; color:#9ca3af; }

        .image-count-badge {
          position: absolute; bottom: 8px; right: 8px;
          background: rgba(0,0,0,0.7); color: white; font-size: .8rem; padding: 2px 8px; border-radius: 9999px;
        }

        .article-status { position: absolute; top: 8px; left: 8px; }
        .article-status .status-badge { background: #6b7280; }
        .status-badge.published { background:#10b981; }
        .status-badge.draft { background:#6b7280; }

        .article-content { padding: 16px; }
        .article-meta { display:flex; gap:8px; align-items:center; color:#6b7280; font-size:.85rem; margin-bottom:6px; }
        .article-category { background:#eef2ff; color:#3730a3; padding:2px 8px; border-radius:9999px; font-weight:500; }
        .article-date { margin-left:auto; font-variant-numeric: tabular-nums; }

        .article-title { margin: 6px 0 4px 0; font-size:1.1rem; font-weight:600; color:#111827; }
        .article-slug { font-size:.85rem; color:#6b7280; margin-bottom:8px; }
        .article-excerpt { margin:0 0 12px 0; color:#4b5563; font-size:.95rem; }

        .article-actions { display:flex; gap:8px; justify-content:flex-end; }
        .action-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; cursor:pointer; transition:all .15s ease; }
        .action-btn:hover { border-color:#3b82f6; background:#eff6ff; color:#1d4ed8; }
        .edit-btn svg, .delete-btn svg { pointer-events:none; }
        .delete-btn:hover { border-color:#ef4444; background:#fef2f2; color:#b91c1c; }

        /* Styles pour les factures */
        .invoices-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: hidden; /* empêcher tout scroll horizontal parasite */
        }

        .invoices-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .invoices-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .invoices-actions {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .invoices-filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .invoices-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
          width: 100%;
        }

        /* Empêcher le débordement sur mobiles comme les autres onglets */
        @media (max-width: 640px) {
          .invoices-grid { grid-template-columns: 1fr; }
          .invoice-card { width: 100%; }
        }

        /* Forcer une seule colonne aussi en petits tablettes */
        @media (max-width: 768px) {
          .invoices-grid { grid-template-columns: 1fr; }
          .invoice-card { width: 100%; }
        }

        .invoice-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          position: relative;
          box-sizing: border-box;
          max-width: 100%;
        }

        .invoice-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .invoice-card.overdue {
          border-color: #ef4444;
          background: #fef2f2;
        }

        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .invoice-info {
          flex: 1;
          min-width: 0; /* éviter que le contenu pousse la carte */
        }

.invoice-customer {
  font-size: 0.9rem;
  color: #6b7280;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.invoice-customer .company {
  font-style: italic;
}
        }

        .invoice-customer .company {
          font-style: italic;
        }

        .invoice-status {
          margin-left: 12px;
        }

        .invoice-details {
          margin-bottom: 16px;
        }

        .invoice-vehicle,
        .invoice-quote {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #4b5563;
        }

        .invoice-vehicle svg,
        .invoice-quote svg {
          color: #6b7280;
        }

        .invoice-amount {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 12px;
          background: #f0fdf4;
          border-radius: 8px;
          border: 1px solid #bbf7d0;
          flex-wrap: wrap; /* autoriser le retour à la ligne */
          gap: 8px;
        }

        .invoice-payment {
          background: #f8fafc;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .payment-status {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .payment-label {
          font-size: 0.9rem;
          color: #6b7280;
          font-weight: 500;
        }

        .payment-badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: white;
        }

        .payment-badge.pending {
          background-color: #f59e0b;
        }

        .payment-badge.partial {
          background-color: #3b82f6;
        }

        .payment-badge.paid {
          background-color: #10b981;
        }

        .payment-badge.overdue {
          background-color: #ef4444;
        }

        .payment-method {
          font-size: 0.85rem;
          color: #6b7280;
        }

        .invoice-dates {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
        }

        .invoice-date {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
        }

        .overdue-date {
          color: #dc2626;
          font-weight: 500;
        }

        .paid-date {
          color: #059669;
          font-weight: 500;
        }

        .invoice-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }

        .pdf-actions {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .download-btn {
          background: #10b981;
          color: white;
        }

        .download-btn:hover {
          background: #059669;
        }

        .download-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .share-btn {
          background: #f59e0b;
          color: white;
        }

        .share-btn:hover {
          background: #d97706;
        }

        .share-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Styles pour le nouveau tableau de bord */
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .kpi-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-left: 4px solid #3b82f6;
          transition: all 0.2s ease;
        }

        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .kpi-card.revenue {
          border-left-color: #10b981;
        }

        .kpi-card.quotes {
          border-left-color: #3b82f6;
        }

        .kpi-card.invoices {
          border-left-color: #f59e0b;
        }

        .kpi-card.customers {
          border-left-color: #8b5cf6;
        }

        .kpi-card .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: white;
        }

        .kpi-card.revenue .kpi-icon {
          background: #10b981;
        }

        .kpi-card.quotes .kpi-icon {
          background: #3b82f6;
        }

        .kpi-card.invoices .kpi-icon {
          background: #f59e0b;
        }

        .kpi-card.customers .kpi-icon {
          background: #8b5cf6;
        }

        .kpi-info h3 {
          margin: 0 0 8px 0;
          font-size: 2rem;
          font-weight: 700;
          color: #1f2937;
        }

        .kpi-info p {
          margin: 0 0 4px 0;
          font-size: 1rem;
          font-weight: 500;
          color: #6b7280;
        }

        .kpi-subtitle {
          font-size: 0.85rem;
          color: #9ca3af;
        }

        .alert-section {
          margin-bottom: 30px;
        }

        .alert-card {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .alert-card.overdue {
          background: #fef2f2;
          border-color: #fecaca;
        }

        .alert-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #dc2626;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .alert-content {
          flex: 1;
        }

        .alert-content h4 {
          margin: 0 0 4px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #dc2626;
        }

        .alert-content p {
          margin: 0;
          font-size: 0.9rem;
          color: #7f1d1d;
        }

        .alert-action {
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .alert-action:hover {
          background: #b91c1c;
        }

        /* Bouton violet pour partager le lien espace client (devis/facture) */
        .action-btn.link-btn {
          background: #8b5cf6;
          border-color: #8b5cf6;
          color: #fff;
        }
        .action-btn.link-btn:hover {
          background: #7c3aed;
          border-color: #7c3aed;
          color: #fff;
        }

        /* Compacter Gestion/Contenu sur petits écrans: masquer les labels pour garder les icônes */
        @media (max-width: 640px) {
          .main-nav-buttons .nav-label { display: none; }
          .main-nav-buttons .main-nav-btn { padding: 8px; }
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 30px;
        }

        @media (max-width: 1024px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }

        .dashboard-section {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* Empêcher le débordement - Activité récente */
        .activity-list { overflow-x: hidden; }
        .activity-item { flex-wrap: wrap; }
        .activity-content { min-width: 0; }
        .activity-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .activity-meta { flex-wrap: wrap; gap: 8px; }
        .activity-amount, .activity-status { flex-shrink: 0; white-space: nowrap; }

        @media (max-width: 640px) {
          .activity-item { flex-direction: column; align-items: flex-start; gap: 8px; }
          .activity-amount, .activity-status { align-self: flex-end; }
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }

        /* Empêcher le débordement - Actions rapides */
        .quick-actions { display: flex; flex-wrap: wrap; gap: 12px; }
        .quick-action-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; border: 1px solid #e5e7eb; background: #fff; cursor: pointer; transition: all .15s ease; flex: 1 1 220px; min-width: 180px; }
        .quick-action-btn svg { flex-shrink: 0; }
        .quick-action-btn.primary { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; }
        .quick-action-btn.secondary { border-color: #f59e0b; background: #fffbeb; color: #b45309; }
        .quick-action-btn.tertiary { border-color: #10b981; background: #ecfdf5; color: #047857; }
        .quick-action-btn:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.08); }

        .section-header h3 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
          color: #1f2937;
        }

        .section-count {
          font-size: 0.85rem;
          color: #6b7280;
          background: #f3f4f6;
          padding: 4px 8px;
          border-radius: 12px;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #f3f4f6;
          transition: all 0.2s ease;
        }

        .activity-item:hover {
          border-color: #d1d5db;
          background: #f9fafb;
        }

        .activity-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .activity-item.quote .activity-icon {
          background: #3b82f6;
        }

        .activity-item.invoice .activity-icon {
          background: #10b981;
        }

        .activity-content {
          flex: 1;
        }

        .activity-title {
          font-weight: 500;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .activity-meta {
          display: flex;
          gap: 12px;
          font-size: 0.85rem;
          color: #6b7280;
        }

        .activity-amount {
          font-weight: 600;
          color: #059669;
        }

        .activity-status {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: white;
        }

        .activity-status.sent {
          background: #3b82f6;
        }

        .activity-status.accepted {
          background: #10b981;
        }

        .activity-status.draft {
          background: #6b7280;
        }

        .activity-status.paid {
          background: #10b981;
        }

        .activity-status.pending {
          background: #f59e0b;
        }

        .empty-activity {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
        }

        .empty-activity svg {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-activity p {
          margin: 0 0 8px 0;
          font-weight: 500;
        }

        .empty-activity span {
          font-size: 0.9rem;
          color: #9ca3af;
        }

        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .quick-action-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border-radius: 8px;
          border: none;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .quick-action-btn.primary {
          background: #3b82f6;
          color: white;
        }

        .quick-action-btn.primary:hover {
          background: #2563eb;
        }

        .quick-action-btn.secondary {
          background: #10b981;
          color: white;
        }

        .quick-action-btn.secondary:hover {
          background: #059669;
        }

        .quick-action-btn.tertiary {
          background: #8b5cf6;
          color: white;
        }

        .quick-action-btn.tertiary:hover {
          background: #7c3aed;
        }

        /* Styles pour les rendez-vous */
        .appointments-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow-x: hidden; /* empêcher tout scroll horizontal parasite */
        }

        .appointments-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .appointments-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .appointments-actions {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
        }

        .appointments-filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .appointments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
          width: 100%;
        }

        @media (max-width: 640px) {
          .appointments-grid { grid-template-columns: 1fr; }
          .appointment-card { width: 100%; }
        }

        /* Forcer une seule colonne aussi en petits tablettes */
        @media (max-width: 768px) {
          .appointments-grid { grid-template-columns: 1fr; }
          .appointment-card { width: 100%; }
        }

        .appointment-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
          position: relative;
          box-sizing: border-box;
          max-width: 100%;
        }

        .appointment-card:hover {
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .appointment-card.today {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .appointment-card.past {
          border-color: #f59e0b;
          background: #fffbeb;
        }

        .appointment-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .appointment-info {
          flex: 1;
          min-width: 0; /* prevent flex child overflow */
        }

        .appointment-title {
          margin: 0 0 4px 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .appointment-customer {
          font-size: 0.9rem;
          color: #6b7280;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .appointment-customer .company {
          font-style: italic;
        }

        .appointment-status {
          margin-left: 12px;
        }

        .appointment-details {
          margin-bottom: 16px;
        }

        .appointment-datetime {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .datetime-item {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
        }

        .datetime-item svg {
          color: #6b7280;
        }

        .appointment-vehicle,
        .appointment-quote,
        .appointment-notes {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #4b5563;
        }

        .appointment-vehicle svg,
        .appointment-quote svg,
        .appointment-notes svg {
          color: #6b7280;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .appointment-notes span {
          line-height: 1.4;
        }

        .appointment-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        /* Styles pour le stock */
        .stock-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stock-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
          gap: 16px;
        }

        .stock-actions {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: border-color 0.2s ease;
          min-width: 160px;
        }

        /* Responsive layout to keep actions within viewport */
        @media (max-width: 768px) {
          .stock-actions { width: 100%; }
          .stock-actions .search-box { flex: 1 1 220px; }
          .stock-actions .filter-select { flex: 1 1 160px; }
          .stock-actions > button { flex: 1 1 calc(50% - 10px); white-space: nowrap; }
        }

        @media (max-width: 480px) {
          .stock-actions > button { flex: 1 1 100%; }
        }

        .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .filter-select:hover {
          border-color: #9ca3af;
        }

        .parts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }

        .part-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .part-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .part-card.low-stock {
          border-color: #f59e0b;
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
        }

        .part-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 16px;
        }

        .part-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .part-info {
          flex: 1;
          min-width: 0;
        }

        .part-name {
          margin: 0 0 4px 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.3;
        }

        .part-reference {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .part-actions {
          display: flex;
          gap: 8px;
          margin-left: auto;
        }

        .part-details {
          margin-bottom: 16px;
        }

        .part-meta {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .part-category {
          background: #dbeafe;
          color: #1e40af;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .part-supplier {
          background: #f3f4f6;
          color: #6b7280;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .part-stats {
          display: flex;
          gap: 16px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          display: block;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          line-height: 1;
        }

        .stat-label {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .part-status {
          margin-top: 12px;
        }

        .low-stock-warning {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 6px;
          color: #92400e;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .low-stock-warning svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .part-form-modal {
          max-width: 800px;
          width: 90vw;
        }

        .part-form {
          padding: 0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        /* Styles pour le formulaire de pièces */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 24px;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
        }

        .close-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .part-form {
          padding: 0 24px 24px 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }

        .form-group input,
        .form-group select {
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: white;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .form-group input::placeholder {
          color: #9ca3af;
        }

        .error-message {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 4px;
          font-weight: 500;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 32px;
          padding-top: 20px;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .btn-primary {
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        /* Styles pour l'icône œil dans la carte client */
        .customer-name {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .view-detail-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        
        .view-detail-btn:hover {
          color: #3b82f6;
          background-color: #f3f4f6;
        }
        
        .view-detail-btn svg {
          display: block;
        }

        /* Styles pour la popup de détail client */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .customer-detail-modal {
          background: white;
          border-radius: 8px;
          width: 100%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          padding: 16px 24px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
        }

        .close-modal {
          background: none;
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-modal:hover {
          color: #1f2937;
          background-color: #f3f4f6;
        }

        .modal-body {
          padding: 20px 24px;
          flex: 1;
          overflow-y: auto;
        }

        .customer-detail-section,
        .customer-vehicles-section {
          margin-bottom: 24px;
        }

        .customer-detail-section h4,
        .customer-vehicles-section h4 {
          margin: 0 0 16px 0;
          font-size: 1.1rem;
          color: #111827;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .detail-row {
          display: flex;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .detail-row.full-width {
          flex-direction: column;
        }

        .detail-label {
          font-weight: 500;
          color: #4b5563;
          width: 120px;
          flex-shrink: 0;
        }

        .detail-value {
          color: #1f2937;
          flex: 1;
        }

        .detail-row.full-width .detail-value {
          margin-top: 4px;
        }

        .notes {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px;
          white-space: pre-wrap;
          line-height: 1.5;
        }

        .vehicles-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-top: 12px;
        }

        .vehicle-item {
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 12px 16px;
        }

        .vehicle-name {
          font-weight: 500;
          color: #111827;
          margin-bottom: 4px;
        }

        .vehicle-plate,
        .vehicle-vin {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .vehicle-vin {
          font-family: monospace;
          margin-top: 2px;
        }

        .no-vehicles {
          color: #6b7280;
          font-style: italic;
          margin: 12px 0 0 0;
        }

        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        /* Bouton + (vert) et menu d'ajout */
        .relative-inline { position: relative; display: inline-block; }

        .action-btn.add-btn {
          border: 1px solid #10b981;
          color: #10b981;
          background: #ecfdf5;
          border-radius: 8px;
        }
        .action-btn.add-btn:hover {
          border-color: #059669;
          color: #059669;
          background: #d1fae5;
        }

        .add-menu {
          position: absolute;
          top: 40px;
          left: 0;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          box-shadow: 0 10px 20px rgba(0,0,0,0.12);
          padding: 8px;
          z-index: 20;
          min-width: 180px;
        }

        .add-menu .add-menu-item {
          width: 100%;
          background: transparent;
          border: none;
          text-align: left;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 0.95rem;
          color: #065f46;
          cursor: pointer;
        }
        .add-menu .add-menu-item:hover {
          background: #ecfdf5;
          color: #047857;
        }

        /* Styles pop-up QR */
        .qr-modal {
          background: #ffffff;
          border-radius: 14px;
          width: 100%;
          max-width: 520px;
          box-shadow: 0 20px 45px rgba(0,0,0,0.18);
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }

        .qr-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          background: #f8fafc;
          border-bottom: 1px solid #e5e7eb;
        }

        .qr-title { display:flex; align-items:center; gap:8px; font-weight:600; color:#111827; }
        .qr-title svg { color:#2563eb; }

        .qr-close {
          background: none;
          border: none;
          font-size: 22px;
          line-height: 1;
          color: #6b7280;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
        }
        .qr-close:hover { background:#f3f4f6; color:#111827; }

        .qr-body { padding: 18px; text-align:center; }
        .qr-text { margin: 0 0 10px 0; color:#374151; font-weight:500; }

        .qr-image-wrap { display:flex; align-items:center; justify-content:center; padding:12px; background:#ffffff; border:1px solid #e5e7eb; border-radius:12px; width: fit-content; margin: 0 auto; }
        .qr-image { width: 240px; height: 240px; display:block; }

        .qr-link { margin-top: 12px; color:#2563eb; font-weight:600; word-break: break-all; font-size: 0.95rem; }

        .qr-actions { display:flex; gap:10px; flex-wrap:wrap; justify-content:center; padding: 14px 18px 20px 18px; border-top: 1px solid #f3f4f6; }

        /* Responsive pour le formulaire */
        @media (max-width: 768px) {
          .part-form-modal {
            width: 95vw;
            max-width: none;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .modal-header {
            padding: 16px 16px 0 16px;
          }

          .part-form {
            padding: 0 16px 16px 16px;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-secondary,
          .btn-primary {
            width: 100%;
            justify-content: center;
          }
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
          grid-column: 1 / -1;
        }

        .empty-state svg {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          font-size: 1.25rem;
          color: #374151;
        }

        .empty-state p {
          margin: 0 0 24px 0;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .articles-grid {
            grid-template-columns: 1fr;
          }

          .customers-grid {
            grid-template-columns: 1fr;
          }

          .customers-header {
            flex-direction: column;
            align-items: stretch;
          }

          .customers-actions {
            flex-direction: column;
          }

          .search-box input {
            width: 100%;
          }

          .blog-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .blog-actions {
            justify-content: center;
          }
        }
      `}</style>
      {/* Popup de détail client */}
      {showCustomerDetail && selectedCustomer && (
        <CustomerDetailPopup 
          customer={selectedCustomer} 
          onClose={() => setShowCustomerDetail(false)} 
        />
      )}
      
      {/* Navigation Mobile Admin */}
      <AdminBottomNavbar 
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveMainTab(tab === 'blog' || tab === 'users' ? 'contenu' : 'gestion');
          setActiveTab(tab);
        }}
        onNewClient={() => {
          setActiveTab('customers');
          setEditingCustomer(null);
          setShowCustomerForm(true);
        }}
        onNewQuote={() => {
          setActiveTab('quotes');
          setEditingQuote(null);
          setShowQuoteForm(true);
        }}
        onNewInvoice={() => {
          setActiveTab('invoices');
          setEditingInvoice(null);
          setShowInvoiceForm(true);
        }}
      />
    </div>
  );
};

export default Admin;
